"use client";

import {
  type CSSProperties,
  useEffect,
  useRef,
  useState,
} from "react";
import Link from "next/link";
import DecisionPointForm from "@/components/DecisionPointForm";
import OfferModal from "@/components/OfferModal";
import { strategicOffers, type OfferId } from "@/components/offerData";
import PrototypePlanet, {
  type AICapabilitySignal,
  type BrandSignal,
  type IndustrySignal,
  type JudgmentForce,
  type OutcomeSignal,
  type PlanetMotionPreference,
  type PlanetMotionState,
} from "@/components/PrototypePlanet";
import GlobalDeliveryNetwork from "@/components/GlobalDeliveryNetwork";

const clamp = (value: number, min = 0, max = 1) => Math.min(max, Math.max(min, value));
const getAssetUrl = (path: string): string => {
  if (!path) return path;
  if (path.startsWith("http://") || path.startsWith("https://") || path.startsWith("data:")) return path;
  const base = import.meta.env.BASE_URL || "/";
  const prefix = base.endsWith("/") ? base : `${base}/`;
  return `${prefix}${path.startsWith("/") ? path.slice(1) : path}`;
};
const smoothstep = (value: number) => {
  const x = clamp(value);
  return x * x * (3 - 2 * x);
};
const phase = (progress: number, start: number, end: number) =>
  clamp((progress - start) / Math.max(0.001, end - start));
const mix = (from: number, to: number, amount: number) => from + (to - from) * amount;

const HERO_PULLBACK_START = 0.002;
const HERO_PULLBACK_END = 0.020;
const HERO_COPY_EXIT_START = 0.015;
const HERO_COPY_EXIT_END = 0.035;
const HERO_MANIFESTO_START = 0.035;
const HERO_MANIFESTO_END = 0.035;
const HERO_SIGNAL_START = 0.035;
const HERO_SIGNAL_END = 0.035;
const HERO_DOCK_START = 0.022;
const TIMELINE_START = 0.035;
const APPROVED_DESKTOP_HERO_TIMING = {
  pullbackStart: 0.004,
  pullbackEnd: 0.046,
  copyExitStart: 0.018,
  copyExitEnd: 0.052,
  manifestoStart: 0.049,
  manifestoEnd: 0.068,
  signalStart: 0.057,
  signalEnd: 0.091,
  dockStart: 0.068,
  firstChapterStart: 0.09,
  firstChapterMiniatureEnd: 0.102,
  firstChapterTravelStart: 0.109,
  firstChapterEnd: 0.13,
} as const;
const FIRST_PAIR_SEGMENT = 0.076;
const COMPACT_SEGMENT = 0.0685;
const WHY_FTO_CLOSE_GAP = 0.012;
const CHAPTER_READING_HOLD = 0.014;
const CHAPTER_EXIT_DURATION = 0.012;
const AI_SPEED_DESKTOP = 2.85;
const AI_SPEED_MOBILE = 2.15;
const AI_SPEED_REDUCED = 1.08;
const AI_DIRECT_MOTION_DURATION_MS = 900;
const AI_DIRECT_MOTION_PENDING = -1;

type WhyFtoState = "standard" | "ai-human" | "outcomes";
type WhyFtoPerspective = "neutral" | "ai" | "human";
type FtoAiWorkflowKey = "triage" | "agent-assist" | "qa-monitoring" | "data-validation";
type FtoAiPanelView = "overview" | FtoAiWorkflowKey;
type FtoAiTransitionPhase = "idle" | "preparing" | "animating";
type FtoPeopleWorkflowKey = "complex-escalations" | "retention-recovery" | "claims-exceptions" | "high-value-care";
type FtoPeoplePanelView = "overview" | FtoPeopleWorkflowKey;
type FtoPeopleTransitionPhase = "idle" | "preparing" | "animating";
type SolutionDetailPhase = "idle" | "exit" | "enter";
type IndustryDetailPhase = "idle" | "exit" | "enter";

const ftoAiWorkflows: Array<{
  key: FtoAiWorkflowKey;
  label: string;
  description: string;
}> = [
  {
    key: "triage",
    label: "Triage & Routing",
    description: "FTO AI helps prioritize and route incoming work using intent, urgency, and defined workflow rules — so the right work reaches the right next step faster.",
  },
  {
    key: "agent-assist",
    label: "Agent Assist",
    description: "FTO AI surfaces relevant context and recommended next steps in the flow of work, helping agents respond faster while keeping human ownership visible.",
  },
  {
    key: "qa-monitoring",
    label: "QA & Monitoring",
    description: "FTO AI reviews interaction patterns at scale to surface quality signals, exceptions, and potential compliance risks earlier.",
  },
  {
    key: "data-validation",
    label: "Data Processing & Validation",
    description: "FTO AI helps structure, compare, and validate repetitive data so teams can focus on exceptions, decisions, and higher-value work.",
  },
];

const ftoPeopleWorkflows: Array<{
  key: FtoPeopleWorkflowKey;
  label: string;
  description: string;
}> = [
  {
    key: "complex-escalations",
    label: "Complex Escalations",
    description: "FTO people assess context, resolve ambiguity, and take ownership when issues fall outside standard paths or require judgment.",
  },
  {
    key: "retention-recovery",
    label: "Retention & Recovery",
    description: "FTO people recognize customer sentiment, adapt the conversation, and work toward the right resolution when trust or loyalty is at risk.",
  },
  {
    key: "claims-exceptions",
    label: "Claims & Exceptions",
    description: "FTO people evaluate exceptions, competing priorities, and supporting context so complex cases receive accountable, case-specific decisions.",
  },
  {
    key: "high-value-care",
    label: "High-Value Customer Care",
    description: "FTO people deliver attentive, context-aware support for interactions where personalization, empathy, and brand judgment matter most.",
  },
];

const humanQualities: Array<{
  force: JudgmentForce;
  number: string;
  title: string;
  detail: string;
  accessibleLabel: string;
}> = [
  {
    force: "empathy",
    number: "01",
    title: "Empathy",
    detail: "Recognizes the human stakes.",
    accessibleLabel: "Explore Empathy: recognizes the human stakes.",
  },
  {
    force: "nuance",
    number: "02",
    title: "Nuance",
    detail: "Interprets what rules cannot.",
    accessibleLabel: "Explore Nuance: interprets what rules cannot.",
  },
  {
    force: "accountability",
    number: "03",
    title: "Accountability",
    detail: "Takes ownership of the outcome.",
    accessibleLabel: "Explore Accountability: takes ownership of the outcome.",
  },
];

const brandSignals: Array<{
  signal: BrandSignal;
  title: string;
  detail: string;
  detailLines: string[];
  accessibleLabel: string;
}> = [
  {
    signal: "precision",
    title: "Precision",
    detail: "Protects quality in every detail.",
    detailLines: ["Protects quality", "in every detail."],
    accessibleLabel: "Explore Precision: protects quality in every detail.",
  },
  {
    signal: "judgment",
    title: "Judgment",
    detail: "Adapts the response when context changes.",
    detailLines: ["Adapts the response", "when context", "changes."],
    accessibleLabel: "Explore Judgment: adapts the response when context changes.",
  },
  {
    signal: "trust",
    title: "Trust",
    detail: "Builds confidence through consistent delivery.",
    detailLines: ["Builds confidence", "through consistent", "delivery."],
    accessibleLabel: "Explore Trust: builds confidence through consistent delivery.",
  },
];

const brandOutcomes: Array<{
  key: OutcomeSignal;
  number: string;
  title: string;
  color: string;
  rgb: string;
  desktopColor: string;
  desktopRgb: string;
  microcopy: string;
  caseStudy1Text?: string;
  quote1Role: string;
  quote1Text: string;
  desktopQuote1Text?: string;
  caseStudy2Text?: string;
  quote2Role: string;
  quote2Text: string;
  caseStudy3Text?: string;
  quote3Role: string;
  quote3Text: string;
  accessibleLabel: string;
}> = [
  {
    key: "clarity",
    number: "01",
    title: "Fintech Dispute Resolution",
    color: "#6fcfff",
    rgb: "111,207,255",
    desktopColor: "#f6921e",
    desktopRgb: "246,146,30",
    microcopy: "Reduced dispute resolution cycle from 4 days to 4 hours.",
    quote1Role: "VP of Payment Operations",
    quote1Text: "“FTO transformed our dispute operations. Their blend of AI triage and expert human investigators resolved backlogs in weeks and cut customer wait times by over 80%.”",
    desktopQuote1Text: "“FTO transformed our dispute operations. Their blend of AI triage and expert human investigators resolved backlog in weeks and cut customer wait times by over 80%.”",
    quote2Role: "Head of Customer Experience",
    quote2Text: "“Our cardholders get real-time updates and empathetic support during stressful fraud moments. Escalations dropped dramatically from day one.”",
    quote3Role: "Chief Compliance Officer",
    quote3Text: "“Audit-ready precision on every case. FTO’s workflow discipline gave our leadership complete confidence across regulated transaction volumes.”",
    accessibleLabel: "Explore Fintech Dispute Resolution Case Study",
  },
  {
    key: "consistency",
    number: "02",
    title: "Healthcare Intake & Triage",
    color: "#00b879",
    rgb: "0,184,121",
    desktopColor: "#009f66",
    desktopRgb: "0,159,102",
    microcopy: "99.8% compliance adherence across 24/7 patient coordination.",
    quote1Role: "Chief Operating Officer",
    quote1Text: "“Patient care requires zero margin for error. FTO built a delivery model that handles surge intake with unmatched consistency and compassionate care.”",
    quote2Role: "Clinical Operations Director",
    quote2Text: "“Our physicians receive thoroughly validated intake data before every consultation, saving over 15 minutes of administrative time per patient visit.”",
    quote3Role: "Patient Experience Lead",
    quote3Text: "“Patients consistently mention how seamless and reassuring the intake process was, even during emergency after-hours coordination.”",
    accessibleLabel: "Explore Healthcare Intake & Triage Case Study",
  },
  {
    key: "confidence",
    number: "03",
    title: "B2B SaaS Growth Operations",
    color: "#f6921e",
    rgb: "246,146,30",
    desktopColor: "#2278ac",
    desktopRgb: "34,120,172",
    microcopy: "4.9/5 CSAT sustained through 300% volume expansion.",
    quote1Role: "VP of Global Customer Support",
    quote1Text: "“When our enterprise user base tripled in six months, FTO scaled technical tier-1 and tier-2 workflows seamlessly without a single SLA breach.”",
    quote2Role: "Head of Product Support",
    quote2Text: "“The FTO team operates like true in-house product specialists. They don’t just close tickets; they feed critical UX insights directly back to our engineering sprints.”",
    quote3Role: "VP of Customer Success",
    quote3Text: "“Our Net Revenue Retention hit record highs. Having FTO back our delivery gave our enterprise clients total peace of mind.”",
    accessibleLabel: "Explore B2B SaaS Growth Operations Case Study",
  },
  {
    key: "trust",
    number: "04",
    title: "Global E-Commerce & Retail",
    color: "#f3dbc0",
    rgb: "243,219,192",
    desktopColor: "#6fcfff",
    desktopRgb: "111,207,255",
    microcopy: "Omnichannel peak-season delivery across 5 global timezones.",
    quote1Role: "Global Head of Customer Service",
    quote1Text: "“During our biggest holiday peak in company history, FTO absorbed 5× volume spikes with zero latency and our highest holiday satisfaction ratings to date.”",
    quote2Role: "Director of Logistics & Fulfillment",
    quote2Text: "“Exception handling and order routing were flawless. Customers received proactive resolution before delivery hiccups could become complaints.”",
    quote3Role: "Chief Brand Officer",
    quote3Text: "“Every interaction was delivered with our exact brand voice and hospitality standards. FTO made a global operation feel intimately personal.”",
    accessibleLabel: "Explore Global E-Commerce & Retail Case Study",
  },
];

function OutcomeProofCopy({ copy }: { copy: string }) {
  return (
    <>
      {copy
        .split(/(\d+(?:\.\d+)?(?:\/\d+(?:\.\d+)?)?%?)/g)
        .filter(Boolean)
        .map((part, index) => /^\d/.test(part) ? (
          <strong className="outcome-proof-value" key={`${part}-${index}`}>{part}</strong>
        ) : (
          <span key={`${part}-${index}`}>{part}</span>
        ))}
    </>
  );
}

const whyFtoBusinessOutcomes: Array<{
  key: OutcomeSignal;
  number: string;
  title: string;
  color: string;
  rgb: string;
  microcopy: string;
  accessibleLabel: string;
}> = [
  {
    key: "clarity",
    number: "01",
    title: "Faster Execution",
    color: "#6fcfff",
    rgb: "111,207,255",
    microcopy: "Keep work moving and reduce friction from first touch to resolution.",
    accessibleLabel: "Explore Faster Execution: keep work moving and reduce friction from first touch to resolution.",
  },
  {
    key: "consistency",
    number: "02",
    title: "Consistent Quality",
    color: "#00b879",
    rgb: "0,184,121",
    microcopy: "Apply the same standard across teams, channels, and volume.",
    accessibleLabel: "Explore Consistent Quality: apply the same standard across teams, channels, and volume.",
  },
  {
    key: "confidence",
    number: "03",
    title: "Scalable Capacity",
    color: "#f6921e",
    rgb: "246,146,30",
    microcopy: "Add coverage and capability without adding operational complexity.",
    accessibleLabel: "Explore Scalable Capacity: add coverage and capability without adding operational complexity.",
  },
  {
    key: "trust",
    number: "04",
    title: "Customer Trust",
    color: "#f3dbc0",
    rgb: "243,219,192",
    microcopy: "Keep judgment, accountability, and care where they matter most.",
    accessibleLabel: "Explore Customer Trust: keep judgment, accountability, and care where they matter most.",
  },
];

type PerspectiveKey = "ai" | "human";
type AICapabilityKey = AICapabilitySignal;

const aiCapabilities: Array<{
  key: AICapabilityKey;
  number: string;
  title: string;
  detail: string;
  accessibleLabel: string;
}> = [
  {
    key: "speed",
    number: "01",
    title: "Speed",
    detail: "Accelerates response and execution.",
    accessibleLabel: "Explore Speed: AI accelerates response and execution.",
  },
  {
    key: "pattern",
    number: "02",
    title: "Pattern Recognition",
    detail: "Identifies signals across complex volumes of work.",
    accessibleLabel: "Explore Pattern Recognition: AI identifies signals across complex volumes of work.",
  },
  {
    key: "repeatability",
    number: "03",
    title: "Repeatability",
    detail: "Applies consistent logic at scale.",
    accessibleLabel: "Explore Repeatability: AI applies consistent logic at scale.",
  },
];

const perspectiveArticles: Record<PerspectiveKey, {
  number: string;
  element: "AI" | "Hu";
  label: string;
  headline: string;
  intro: string;
  leadTitle: string;
  leadItems: string[];
  supportTitle: string;
  supportItems: string[];
  principle: string[];
  capabilities: string[];
  primaryCta: string;
  secondaryCta: string;
}> = {
  ai: {
    number: "01",
    element: "AI",
    label: "ARTIFICIAL INTELLIGENCE",
    headline: "Precision at scale.",
    intro: "AI creates value when the signal is clear, the pattern repeats and speed matters. It can recognize patterns, prioritize work, support routine decisions and keep high-volume operations moving with consistency.",
    leadTitle: "WHERE AI SHOULD LEAD",
    leadItems: [
      "Repeatable work",
      "Pattern recognition",
      "Routing and prioritization",
      "Frontline assistance",
    ],
    supportTitle: "WHERE AI SHOULD NOT OPERATE ALONE",
    supportItems: [
      "Ambiguous moments",
      "Emotionally sensitive interactions",
      "Material exceptions",
      "Accountable decisions",
    ],
    principle: [
      "Automate the pattern.",
      "Escalate the exception.",
      "Keep ownership visible.",
    ],
    capabilities: ["Speed", "Pattern recognition", "Repeatability", "Visible ownership"],
    primaryCta: "DESIGN THE RIGHT DELIVERY MODEL",
    secondaryCta: "CONTINUE TO HUMAN JUDGMENT →",
  },
  human: {
    number: "02",
    element: "Hu",
    label: "HUMAN JUDGMENT",
    headline: "Context when the moment changes.",
    intro: "The moments that shape trust rarely arrive under perfect conditions. A customer is frustrated. An exception falls outside the standard process. The facts are incomplete. The next response carries more weight than the workflow behind it.",
    leadTitle: "WHERE PEOPLE SHOULD LEAD",
    leadItems: [
      "Nuanced interactions",
      "Exceptions with competing priorities",
      "Trust recovery",
      "Accountable ownership",
    ],
    supportTitle: "HOW AI SHOULD SUPPORT THE MOMENT",
    supportItems: [
      "Surface context",
      "Identify risk and urgency",
      "Recommend—not conceal",
      "Capture the learning",
    ],
    principle: [
      "Use technology to inform the decision—",
      "not to distance the person making it.",
    ],
    capabilities: ["Empathy", "Context", "Accountability", "Trust"],
    primaryCta: "DESIGN THE RIGHT BALANCE",
    secondaryCta: "← VIEW AI PERSPECTIVE",
  },
};

const solutions: Array<{
  eyebrow: string;
  title: string;
  description: string;
  workflows: [string, string, string];
}> = [
  {
    eyebrow: "Clarity + care",
    title: "Customer Experience & Support",
    description: "Extend customer support with trained teams built around your channels, workflows, and service standards.",
    workflows: ["Customer Care", "Technical Support", "Escalations & Retention"],
  },
  {
    eyebrow: "Growth + follow-through",
    title: "Sales & Revenue Support",
    description: "Add capacity across prospecting, qualification, pipeline support, and customer growth.",
    workflows: ["Lead Generation", "Qualification & Pipeline Support", "Retention & Growth"],
  },
  {
    eyebrow: "Control + continuity",
    title: "Back-Office Operations",
    description: "Run critical administrative and operational workflows with greater consistency, accuracy, and ownership.",
    workflows: ["Administrative Processing", "QA & Compliance", "Finance & Operations"],
  },
  {
    eyebrow: "Accuracy + scale",
    title: "Data & Transaction Processing",
    description: "Process, validate, and manage high-volume data and transactions with disciplined operational control.",
    workflows: ["Data Processing", "Validation & Documentation", "Transaction Support"],
  },
  {
    eyebrow: "Precision + trust",
    title: "Insurance Operations",
    description: "Support policyholders and internal teams across claims, underwriting, renewals, and policy servicing.",
    workflows: ["Claims & FNOL", "Underwriting Support", "Renewals & Policy Servicing"],
  },
  {
    eyebrow: "Care + accuracy",
    title: "Healthcare Administration",
    description: "Extend healthcare administration with trained teams focused on accuracy, responsiveness, and process discipline.",
    workflows: ["RCM Support", "Billing & Coding", "Member & Administrative Support"],
  },
];

const industries: Array<{
  key: IndustrySignal;
  number: string;
  title: string;
  trust: string;
  customer: string;
  proof: [string, string, string];
  metrics: [
    { value: string; label: string },
    { value: string; label: string },
    { value: string; label: string },
  ];
  workflows: [string, string, string, string];
  help: string;
  cta: string;
  accent: string;
  rgb: string;
  secondary: string;
}> = [
  {
    key: "insurance",
    number: "01",
    title: "Insurance",
    trust: "Every detail handled correctly.",
    customer: "Protected, informed and confident that every detail is being handled correctly.",
    proof: ["Accuracy", "Clarity", "Accountability"],
    metrics: [
      { value: "95%+", label: "QA SCORE" },
      { value: "98%+", label: "COMPLIANCE" },
      { value: "95%+", label: "CSAT" },
    ],
    workflows: ["Claims Intake", "Policy Servicing", "Underwriting Support", "Renewals"],
    help: "FTO combines licensed talent, disciplined workflows, and quality controls to deliver accurate, consistent, accountable insurance operations.",
    cta: "DISCUSS YOUR INSURANCE OPERATIONS",
    accent: "#00b879",
    rgb: "0,184,121",
    secondary: "#2278ac",
  },
  {
    key: "healthcare",
    number: "02",
    title: "Healthcare",
    trust: "Care that stays coordinated.",
    customer: "Supported, understood and confident that care coordination will not break down.",
    proof: ["Accuracy", "Responsiveness", "Care"],
    metrics: [
      { value: "98%+", label: "DATA ACCURACY" },
      { value: "90%+", label: "SLA" },
      { value: "95%+", label: "CSAT" },
    ],
    workflows: ["Patient / Member Support", "RCM Support", "Billing & Coding", "Scheduling"],
    help: "FTO connects patient support, verification, scheduling, billing, and back-office workflows into a coordinated operating model—designed to keep information accurate, queues moving, and every next step clear.",
    cta: "DISCUSS YOUR HEALTHCARE OPERATIONS",
    accent: "#00b879",
    rgb: "0,184,121",
    secondary: "#d2e3f3",
  },
  {
    key: "financial",
    number: "03",
    title: "Financial Services",
    trust: "Every interaction controlled and accurate.",
    customer: "Secure, informed and confident that their information and requests are handled correctly.",
    proof: ["Accuracy", "Security", "Responsiveness"],
    metrics: [
      { value: "98%+", label: "SLA COMPLIANCE" },
      { value: "95%+", label: "QA SCORE" },
      { value: "98%+", label: "COMPLIANCE" },
    ],
    workflows: ["Customer Support", "Onboarding Support", "Transaction Support", "Account Servicing"],
    help: "FTO combines controlled workflows, consistent QA, secure handling, and clear exception management across customer, transaction, onboarding, and account-servicing operations—so precision holds even as volume scales.",
    cta: "DISCUSS YOUR FINANCIAL SERVICES OPERATIONS",
    accent: "#00b879",
    rgb: "0,184,121",
    secondary: "#6fcfff",
  },
  {
    key: "technology",
    number: "04",
    title: "Technology",
    trust: "Progress without unnecessary friction.",
    customer: "Helped quickly, guided clearly and confident the issue is moving toward resolution.",
    proof: ["Clarity", "Speed", "Continuity"],
    metrics: [
      { value: "90%+", label: "CSAT" },
      { value: "85%+", label: "FCR" },
      { value: "92%+", label: "QA SCORE" },
    ],
    workflows: ["Technical Support", "Customer Onboarding", "Back-Office Support", "Retention Support"],
    help: "FTO extends technical support with structured troubleshooting, knowledge workflows, agent assist, and fast human escalation—helping teams resolve issues faster while keeping ownership clear.",
    cta: "DISCUSS YOUR TECHNOLOGY OPERATIONS",
    accent: "#00b879",
    rgb: "0,184,121",
    secondary: "#2278ac",
  },
  {
    key: "consumer",
    number: "05",
    title: "Consumer Goods",
    trust: "A consistent brand experience after every purchase.",
    customer: "Supported consistently, updated clearly and confident that the brand will follow through.",
    proof: ["Responsiveness", "Consistency", "Care"],
    metrics: [
      { value: "90%+", label: "CSAT" },
      { value: "95%+", label: "SLA ADHERENCE" },
      { value: "15%+", label: "AHT REDUCTION" },
    ],
    workflows: ["Customer Care", "Order Support", "Returns & Resolution", "Retention Outreach"],
    help: "FTO connects customer care, order support, returns, and retention across channels—helping brands stay responsive, consistent, and in control through everyday demand and peak volume.",
    cta: "DISCUSS YOUR CONSUMER GOODS OPERATIONS",
    accent: "#00b879",
    rgb: "0,184,121",
    secondary: "#f6921e",
  },
  {
    key: "education",
    number: "06",
    title: "Education",
    trust: "Guidance throughout the learner journey.",
    customer: "Guided, reassured and confident that they understand the next step.",
    proof: ["Clarity", "Continuity", "Empathy"],
    metrics: [
      { value: "+15%", label: "ENROLLMENT CONVERSION" },
      { value: "−20%", label: "COST PER ENROLLMENT" },
      { value: "90%+", label: "QA SCORE" },
    ],
    workflows: ["Enrollment Support", "Student Services", "Scheduling", "Retention Outreach"],
    help: "FTO supports the learner journey with coordinated enrollment, student services, scheduling, and retention workflows—so every inquiry gets a clear answer and every next step keeps moving.",
    cta: "DISCUSS YOUR EDUCATION OPERATIONS",
    accent: "#00b879",
    rgb: "0,184,121",
    secondary: "#6fcfff",
  },
];

function IndustryIcon({ industry }: { industry: IndustrySignal }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" data-industry-icon={industry}>
      {industry === "insurance" && (
        <>
          <path d="M12 2.8 19 6.5v5.4c0 4.2-2.7 7.4-7 9.3-4.3-1.9-7-5.1-7-9.3V6.5l7-3.7Z" />
          <path d="M8.7 8.7h6.6v7H8.7zM10.4 10.6h3.2M10.4 12.5h3.2M10.4 14.4h2.2" />
        </>
      )}
      {industry === "healthcare" && (
        <path d="M9.1 3.7h5.8v5.4h5.4v5.8h-5.4v5.4H9.1v-5.4H3.7V9.1h5.4V3.7Z" />
      )}
      {industry === "financial" && (
        <>
          <path d="m3.5 8.8 8.5-5 8.5 5H3.5ZM5 9.1h14M4.3 18.8h15.4M3.4 21h17.2" />
          <path d="M6.4 9.2v9.4M10.1 9.2v9.4M13.9 9.2v9.4M17.6 9.2v9.4" />
        </>
      )}
      {industry === "technology" && (
        <>
          <path d="M7.2 7.2 10.3 10.3M16.8 7.2l-3.1 3.1M7.2 16.8l3.1-3.1M16.8 16.8l-3.1-3.1" />
          <circle cx="5.5" cy="5.5" r="2.1" />
          <circle cx="18.5" cy="5.5" r="2.1" />
          <circle cx="5.5" cy="18.5" r="2.1" />
          <circle cx="18.5" cy="18.5" r="2.1" />
          <circle cx="12" cy="12" r="2.3" />
        </>
      )}
      {industry === "consumer" && (
        <>
          <path d="M6.2 8.2h11.6l1 12H5.2l1-12Z" />
          <path d="M9 8.1V6.4a3 3 0 0 1 6 0v1.7" />
        </>
      )}
      {industry === "education" && (
        <>
          <path d="m3 9 9-5 9 5-9 5-9-5Z" />
          <path d="M7 12v4.1c2.8 2.2 7.2 2.2 10 0V12M21 9v6" />
          <circle cx="21" cy="16.5" r=".8" />
        </>
      )}
    </svg>
  );
}

function OutcomeCaseIcon({ outcome }: { outcome: OutcomeSignal }) {
  return (
    <svg className="outcome-case-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true" data-outcome-icon={outcome}>
      {outcome === "clarity" && (
        <>
          <circle cx="12" cy="12" r="8.4" />
          <path d="M14.7 8.8c-.7-.6-1.7-.9-2.7-.9-1.7 0-2.8.8-2.8 2s1 1.7 2.8 2.1c1.8.4 2.8 1 2.8 2.1s-1.1 2-2.8 2c-1.2 0-2.3-.4-3.1-1.1M12 6.2v11.6" />
        </>
      )}
      {outcome === "consistency" && (
        <>
          <path d="M12 20.3S4.2 16 4.2 9.8A4.1 4.1 0 0 1 12 7.9a4.1 4.1 0 0 1 7.8 1.9C19.8 16 12 20.3 12 20.3Z" />
          <path d="M12 9.1v5.6M9.2 11.9h5.6" />
        </>
      )}
      {outcome === "confidence" && (
        <>
          <circle cx="12" cy="12" r="3.2" />
          <path d="M12 3.1v2.2M12 18.7v2.2M3.1 12h2.2M18.7 12h2.2M5.7 5.7l1.6 1.6M16.7 16.7l1.6 1.6M18.3 5.7l-1.6 1.6M7.3 16.7l-1.6 1.6" />
          <circle cx="12" cy="12" r="7.1" strokeDasharray="2.1 2.1" />
        </>
      )}
      {outcome === "trust" && (
        <>
          <path d="M3.4 5.3h2.1l1.8 9.1h9.5l2-6.5H6.2M8.8 18.3a1.3 1.3 0 1 1 0 2.6 1.3 1.3 0 0 1 0-2.6ZM16.3 18.3a1.3 1.3 0 1 1 0 2.6 1.3 1.3 0 0 1 0-2.6Z" />
          <path d="M8.2 11.3h8.8" />
        </>
      )}
    </svg>
  );
}

const workflowNodes = [
  { id: "01", action: "Define", result: "Outcome", x: 8, y: 47, kind: "entry" },
  { id: "02", action: "Map", result: "Reality", x: 28, y: 18, kind: "system" },
  { id: "03", action: "Orchestrate", result: "AI", x: 51, y: 47, kind: "ai" },
  { id: "04", action: "Place", result: "Judgment", x: 37, y: 81, kind: "human" },
  { id: "05", action: "Connect", result: "Actions", x: 76, y: 21, kind: "system" },
  { id: "06", action: "Measure", result: "Improve", x: 88, y: 73, kind: "measure" },
];

const workflowLinks = [
  { id: "define-map", d: "M 58 132 C 94 96, 134 63, 190 54", activeAt: 1, kind: "system" },
  { id: "map-orchestrate", d: "M 202 55 C 260 58, 310 88, 354 128", activeAt: 2, kind: "ai" },
  { id: "orchestrate-judgment", d: "M 350 141 C 324 170, 292 204, 262 230", activeAt: 3, kind: "human" },
  { id: "orchestrate-actions", d: "M 366 127 C 412 84, 466 64, 529 61", activeAt: 4, kind: "ai" },
  { id: "judgment-actions", d: "M 270 231 C 350 211, 423 125, 531 67", activeAt: 4, kind: "human" },
  { id: "actions-measure", d: "M 541 70 C 584 104, 614 148, 618 200", activeAt: 5, kind: "system" },
  { id: "feedback", d: "M 613 213 C 576 272, 420 285, 246 270 C 112 259, 45 220, 57 143", activeAt: 5, kind: "feedback" },
];

const workflowNodeLinks = [
  ["define-map"],
  ["define-map", "map-orchestrate"],
  ["map-orchestrate", "orchestrate-judgment", "orchestrate-actions"],
  ["orchestrate-judgment", "judgment-actions"],
  ["orchestrate-actions", "judgment-actions", "actions-measure"],
  ["actions-measure", "feedback"],
];

type Chapter = {
  key: string;
  number: string;
  label: string;
  kicker: string;
  title: string;
  body: string;
  color: string;
  rgb: string;
  side: "left" | "right";
  point: [number, number];
  density?: "dense";
};

type NextChapterNavigationProps = {
  destinationLabel: string;
  destinationChapter: string;
  destinationIndex: number;
  placement: "hero" | "ai-human" | "chapter-footer" | "mobile";
  onNavigate: (chapter: string, index: number) => void;
};

function NextChapterNavigation({
  destinationLabel,
  destinationChapter,
  destinationIndex,
  placement,
  onNavigate,
}: NextChapterNavigationProps) {
  return (
    <button
      className="chapter-next-navigation"
      type="button"
      data-placement={placement}
      data-destination={destinationChapter}
      aria-label={`Next chapter: ${destinationLabel}.`}
      onClick={() => onNavigate(destinationChapter, destinationIndex)}
    >
      <span className="chapter-next-label">NEXT</span>
      <span className="chapter-next-destination">
        <span>{destinationLabel}</span>
        <i aria-hidden="true">↓</i>
      </span>
    </button>
  );
}

const chapters: Chapter[] = [
  /*
  // Commented out per user request:
  {
    key: "signal",
    number: "02",
    label: "The Delivery Standard",
    kicker: "THE DELIVERY STANDARD",
    title: "Consistency is built into every interaction.",
    body: "FTO designs customer operations around precision, judgment, and trust—so teams can deliver the right standard, even when the work gets complex.",
    color: "#009f66",
    rgb: "0,159,102",
    side: "left",
    point: [66, 34],
  },
  */
  {
    key: "outcomes",
    number: "02",
    label: "Business Outcomes",
    kicker: "BUSINESS OUTCOMES",
    title: "Better operations. Stronger outcomes.",
    body: "From resolution speed and compliance to customer satisfaction and global scale, FTO turns operational performance into measurable business results.",
    color: "#f6921e",
    rgb: "246,146,30",
    side: "left",
    point: [22, 46],
    density: "dense",
  },
  {
    key: "perspectives",
    number: "03",
    label: "AI + Human",
    kicker: "FRAME THE CHOICE",
    title: "AI provides precision. People provide judgment.",
    body: "FTO designs where each belongs.",
    color: "#f6921e",
    rgb: "246,146,30",
    side: "left",
    point: [61, 58],
    density: "dense",
  },
  /*
  // Commented out per user request:
  {
    key: "ai",
    number: "04",
    label: "AI Precision",
    kicker: "AI PRECISION",
    title: "Precision when every second and standard matter.",
    body: "AI recognizes patterns, accelerates repeatable decisions and applies consistent logic across high-volume work.",
    color: "#2278ac",
    rgb: "34,120,172",
    side: "left",
    point: [42, 25],
  },
  {
    key: "human",
    number: "05",
    label: "Human Judgment",
    kicker: "HUMAN JUDGMENT",
    title: "Judgment when the moment carries weight.",
    body: "Empathy recognizes the human stakes. Nuance interprets what rules cannot. Accountability takes ownership of the outcome.",
    color: "#f6921e",
    rgb: "246,146,30",
    side: "right",
    point: [31, 62],
  },
  {
    key: "outcomes",
    number: "08",
    label: "Brand Outcomes",
    kicker: "BRAND OUTCOMES",
    title: "Built to deliver the experience your brand promises.",
    body: "Start with the customer expectation. Then design the workflows, ownership and standards required to deliver it consistently.",
    color: "#f6921e",
    rgb: "246,146,30",
    side: "left",
    point: [22, 46],
    density: "dense",
  },
  {
    key: "workflow",
    number: "07",
    label: "Live Workflow",
    kicker: "LIVE CLIENT WORKFLOW",
    title: "Every business runs differently. So should every workflow.",
    body: "Customization of your workflow to exactly fit your business rules, technology, brand standards, and human decision points. It's all possible in the Agentic era with the right partner.",
    color: "#009f66",
    rgb: "0,159,102",
    side: "left",
    point: [52, 20],
    density: "dense",
  },
  */
  {
    key: "solutions",
    number: "04",
    label: "Services & Workflows",
    kicker: "SERVICES & WORKFLOWS",
    title: "Operations built to do the work that creates value.",
    body: "FTO builds and runs outsourced customer-facing and back-office operations around your goals and service standards.",
    color: "#2278ac",
    rgb: "34,120,172",
    side: "right",
    point: [78, 45],
    density: "dense",
  },
  {
    key: "industries",
    number: "05",
    label: "Industries",
    kicker: "INDUSTRIES",
    title: "Every industry sets a different standard for trust.",
    body: "FTO designs delivery around each industry’s customer expectations, moments of trust and operating realities.",
    color: "#009f66",
    rgb: "0,159,102",
    side: "right",
    point: [48, 75],
    density: "dense",
  },
  {
    key: "about",
    number: "06",
    label: "About FTO",
    kicker: "ABOUT FTO",
    title: "About FTO.",
    body: "Local market context. Global operating discipline. Every FTO location is designed to deliver with the same intent.",
    color: "#f3dbc0",
    rgb: "243,219,192",
    side: "left",
    point: [36, 39],
    density: "dense",
  },
  {
    key: "standard",
    number: "07",
    label: "Global Delivery",
    kicker: "THE FTO STANDARD",
    title: "Global delivery. One brand standard.",
    body: "Local market context. Global operating discipline. Every FTO location is designed to deliver with the same intent.",
    color: "#f3dbc0",
    rgb: "243,219,192",
    side: "left",
    point: [36, 39],
    density: "dense",
  },
  {
    key: "contact",
    number: "08",
    label: "Start the Conversation",
    kicker: "DESIGN YOUR DELIVERY SIGNATURE",
    title: "Decide what your customers should feel first.",
    body: "Your brand already has a standard. Define the experience you want to improve, and we’ll help design the right delivery model around it.",
    color: "#009f66",
    rgb: "0,159,102",
    side: "right",
    point: [73, 29],
    density: "dense",
  },
];

const getChapterIndex = (key: string) => {
  const index = chapters.findIndex((chapter) => chapter.key === key);
  return index >= 0 ? index : 0;
};

const PERSPECTIVES_CHAPTER_INDEX = getChapterIndex("perspectives");
const OUTCOMES_CHAPTER_INDEX = getChapterIndex("outcomes");
const SOLUTIONS_CHAPTER_INDEX = getChapterIndex("solutions");
const INDUSTRIES_CHAPTER_INDEX = getChapterIndex("industries");
const STANDARD_CHAPTER_INDEX = getChapterIndex("standard");
const ABOUT_CHAPTER_INDEX = getChapterIndex("about");
const CONTACT_CHAPTER_INDEX = getChapterIndex("contact");

const NEXT_CHAPTER_DESTINATIONS: Record<string, {
  label: string;
  chapter: string;
  index: number;
}> = {
  hero: { label: "BUSINESS OUTCOMES", chapter: "outcomes", index: OUTCOMES_CHAPTER_INDEX },
  outcomes: { label: "AI + HUMAN", chapter: "perspectives", index: PERSPECTIVES_CHAPTER_INDEX },
  perspectives: { label: "SERVICES & WORKFLOWS", chapter: "solutions", index: SOLUTIONS_CHAPTER_INDEX },
  solutions: { label: "INDUSTRIES", chapter: "industries", index: INDUSTRIES_CHAPTER_INDEX },
  industries: { label: "ABOUT FTO", chapter: "about", index: ABOUT_CHAPTER_INDEX },
  about: { label: "GLOBAL DELIVERY", chapter: "standard", index: STANDARD_CHAPTER_INDEX },
  standard: { label: "START THE CONVERSATION", chapter: "contact", index: CONTACT_CHAPTER_INDEX },
};

const AI_CHAPTER_INDEX = -1;
const HUMAN_CHAPTER_INDEX = -1;
const WORKFLOW_CHAPTER_INDEX = -1;
// Re-enable only when the corresponding visible convergence treatment returns to the active story.
const HAS_VISIBLE_CONVERGENCE_TREATMENT = false;

const WHY_FTO_FIRST_INDEX = PERSPECTIVES_CHAPTER_INDEX;
const WHY_FTO_LAST_INDEX = PERSPECTIVES_CHAPTER_INDEX;

const PUBLIC_SECTION_PRESENTATION: Record<string, { number: string; label: string }> = {
  outcomes: { number: "02", label: "Business Outcomes" },
  perspectives: { number: "03", label: "AI + Human" },
  solutions: { number: "04", label: "Services & Workflows" },
  industries: { number: "05", label: "Industries" },
  about: { number: "06", label: "About FTO" },
  standard: { number: "07", label: "Global Delivery" },
  contact: { number: "08", label: "Start the Conversation" },
};

const PUBLIC_SECTION_TOTAL = "08";

const getPublicSectionPresentation = (chapter: Chapter) =>
  PUBLIC_SECTION_PRESENTATION[chapter.key] ?? {
    number: chapter.number,
    label: chapter.label,
  };

const whyFtoTabs: Array<{
  state: WhyFtoState;
  number: string;
  label: string;
  chapterIndex: number;
}> = [
  { state: "ai-human", number: "03", label: "AI + Human", chapterIndex: PERSPECTIVES_CHAPTER_INDEX },
];

const navigationItems = [
  { key: "hero", number: "01", label: "Hero", chapterIndex: -1 },
  ...chapters.map((chapter, chapterIndex) => ({
    key: chapter.key,
    ...getPublicSectionPresentation(chapter),
    chapterIndex,
  })),
];

const menuNavigationItems: Array<{
  key: string;
  number: string;
  label: string;
  chapterIndex: number | null;
}> = navigationItems.flatMap((item) => item.key === "contact"
  ? [
      { key: "faq", number: "—", label: "FAQ", chapterIndex: null },
      item,
    ]
  : [item]);

const desktopMenuLabels: Record<string, string> = {
  hero: "Home",
  outcomes: "Outcomes",
  perspectives: "AI + Human",
  solutions: "Services",
  industries: "Industries",
  about: "About FTO",
  standard: "Global Delivery",
  faq: "FAQ",
};

const chapterNavigationItems = navigationItems.filter(({ chapterIndex }) => chapterIndex >= 0);

const timings = chapters.map((_, index) => {
  const isFinalChapter = index === chapters.length - 1;
  const count = chapters.length;
  const availableSpan = 0.88;
  const segment = count > 1 ? availableSpan / (count - 1) : availableSpan;
  const start = TIMELINE_START + index * segment;
  const travelDuration = Math.min(0.022, segment * 0.18);
  const travelStart = start;
  const travelEnd = start + travelDuration;
  const readingHold = segment * 0.70;
  const exitStart = isFinalChapter ? 1.06 : travelEnd + readingHold;
  const exitDuration = Math.min(0.025, segment * 0.18);
  const exitEnd = isFinalChapter ? 1.1 : exitStart + exitDuration;

  return {
    start,
    miniatureEnd: start + travelDuration * 0.4,
    travelStart,
    travelEnd,
    end: travelEnd,
    exitStart,
    exitEnd,
  };
});

const APPROVED_DESKTOP_FIRST_CHAPTER_TIMING = {
  ...timings[0],
  start: APPROVED_DESKTOP_HERO_TIMING.firstChapterStart,
  miniatureEnd: APPROVED_DESKTOP_HERO_TIMING.firstChapterMiniatureEnd,
  travelStart: APPROVED_DESKTOP_HERO_TIMING.firstChapterTravelStart,
  travelEnd: APPROVED_DESKTOP_HERO_TIMING.firstChapterEnd,
  end: APPROVED_DESKTOP_HERO_TIMING.firstChapterEnd,
  exitStart: 0.205,
  exitEnd: 0.225,
};

const DESKTOP_CHAPTER_TIMING_BY_KEY: Record<string, (typeof timings)[number]> = {
  outcomes: APPROVED_DESKTOP_FIRST_CHAPTER_TIMING,
  perspectives: {
    start: 0.215,
    miniatureEnd: 0.223,
    travelStart: 0.215,
    travelEnd: 0.237,
    end: 0.237,
    exitStart: 0.335,
    exitEnd: 0.355,
  },
  solutions: {
    start: 0.345,
    miniatureEnd: 0.353,
    travelStart: 0.345,
    travelEnd: 0.367,
    end: 0.367,
    exitStart: 0.46,
    exitEnd: 0.48,
  },
  industries: {
    start: 0.47,
    miniatureEnd: 0.478,
    travelStart: 0.47,
    travelEnd: 0.492,
    end: 0.492,
    exitStart: 0.585,
    exitEnd: 0.605,
  },
  about: {
    start: 0.595,
    miniatureEnd: 0.603,
    travelStart: 0.595,
    travelEnd: 0.617,
    end: 0.617,
    exitStart: 0.685,
    exitEnd: 0.705,
  },
  standard: {
    start: 0.695,
    miniatureEnd: 0.703,
    travelStart: 0.695,
    travelEnd: 0.717,
    end: 0.717,
    exitStart: 0.81,
    exitEnd: 0.83,
  },
  contact: {
    start: 0.82,
    miniatureEnd: 0.83,
    travelStart: 0.82,
    travelEnd: 0.846,
    end: 0.846,
    exitStart: 1.06,
    exitEnd: 1.1,
  },
};

const desktopTimings = chapters.map((chapter, index) =>
  DESKTOP_CHAPTER_TIMING_BY_KEY[chapter.key] ?? timings[index]);

const getAiMotionProfile = (
  timelineProgress: number,
  aiTiming = timings[PERSPECTIVES_CHAPTER_INDEX],
) => {
  const presenceIn = smoothstep(phase(
    timelineProgress,
    aiTiming.travelStart - 0.004,
    aiTiming.end - 0.004,
  ));
  const presenceOut = smoothstep(phase(
    timelineProgress,
    aiTiming.exitStart,
    aiTiming.exitEnd - 0.002,
  ));

  return {
    presence: presenceIn * (1 - presenceOut),
    pulseProgress: phase(
      timelineProgress,
      aiTiming.end - 0.004,
      aiTiming.end + 0.008,
    ),
  };
};

const createSignalSystemTransition = (
  heroSignalEnd: number,
  firstChapterTiming: (typeof timings)[number],
) => ({
  statementExitStart: heroSignalEnd - 0.004,
  statementExitEnd: firstChapterTiming.travelStart + 0.001,
  signalStart: heroSignalEnd - 0.002,
  signalEnd: firstChapterTiming.travelStart + 0.008,
  railStart: firstChapterTiming.travelStart + 0.002,
  railEnd: firstChapterTiming.end - 0.004,
  kickerStart: firstChapterTiming.travelStart + 0.003,
  kickerEnd: firstChapterTiming.travelStart + 0.009,
  headlineStart: firstChapterTiming.travelStart + 0.006,
  headlineEnd: firstChapterTiming.travelStart + 0.012,
  bodyStart: firstChapterTiming.travelStart + 0.008,
  bodyEnd: firstChapterTiming.travelStart + 0.014,
  principlesStart: firstChapterTiming.travelStart + 0.01,
  principlesEnd: firstChapterTiming.travelStart + 0.017,
  precisionStart: firstChapterTiming.travelStart + 0.012,
  precisionEnd: firstChapterTiming.travelStart + 0.018,
  judgmentStart: firstChapterTiming.travelStart + 0.0135,
  judgmentEnd: firstChapterTiming.travelStart + 0.0195,
  trustStart: firstChapterTiming.travelStart + 0.015,
  trustEnd: firstChapterTiming.end,
  complete: firstChapterTiming.end,
});

const signalSystemTransition = createSignalSystemTransition(HERO_SIGNAL_END, timings[0]);
const approvedDesktopSignalSystemTransition = createSignalSystemTransition(
  APPROVED_DESKTOP_HERO_TIMING.signalEnd,
  APPROVED_DESKTOP_FIRST_CHAPTER_TIMING,
);

const intelligenceBridge = (() => {
  const start = timings[PERSPECTIVES_CHAPTER_INDEX].exitStart;
  const exitEnd = timings[SOLUTIONS_CHAPTER_INDEX]?.start ?? (start + 0.05);
  const duration = exitEnd - start;

  return {
    start,
    emergeEnd: start + duration * 0.22,
    stageEnd: start + duration * 0.39,
    mergeStart: start + duration * 0.33,
    mergeEnd: start + duration * 0.79,
    holdEnd: start + duration * 0.88,
    exitEnd,
  };
})();

type CompositionOptions = {
  progress: number;
  timing: (typeof timings)[number];
  source: HTMLDivElement;
  target: HTMLElement;
  mobile: boolean;
};

function renderComposition({ progress, timing, source, target, mobile }: CompositionOptions) {
  const { start, miniatureEnd, travelStart, travelEnd, end, exitStart, exitEnd } = timing;
  const arrivalEnd = travelEnd ?? end;
  const birth = phase(progress, start, exitEnd);
  const miniature = smoothstep(phase(progress, start, miniatureEnd));
  const travel = smoothstep(phase(progress, travelStart, arrivalEnd));
  const exiting = smoothstep(phase(progress, exitStart, exitEnd));
  const point = source.getBoundingClientRect();
  const originX = point.left + point.width * 0.5;
  const originY = point.top + point.height * 0.5;
  const finalX = target.offsetLeft + target.offsetWidth * 0.5;
  const finalY = target.offsetTop + target.offsetHeight * 0.5;
  const minimumScale = mobile ? 0.014 : 0.006;
  const heldMiniatureScale = mobile ? 0.102 : 0.08;
  const miniatureScale = mix(minimumScale, heldMiniatureScale, miniature);
  const growth = mix(miniatureScale, 1, travel);
  const exitDirection = target.dataset.side === "left" ? -1 : 1;
  const x = (originX - finalX) * (1 - travel) + exiting * exitDirection * (mobile ? 28 : 76);
  const y = (originY - finalY) * (1 - travel) - exiting * (mobile ? 18 : 34);
  const opacityIn = smoothstep(phase(progress, start - 0.003, miniatureEnd));
  const opacity = opacityIn * (1 - exiting);
  const clarity = smoothstep(phase(progress, travelStart + (arrivalEnd - travelStart) * 0.35, arrivalEnd));
  const seed = (1 - travel) * opacityIn;

  if (mobile && target.id === "workflow") {
    const settle = smoothstep(phase(progress, start, start + (end - start) * 0.38));
    const workflowOpacity = settle * (1 - exiting);
    const workflowY = (1 - settle) * 14 - exiting * 18;

    target.style.transform = `translate3d(0, ${workflowY}px, 0) scale(1)`;
    target.style.opacity = `${workflowOpacity}`;
    target.style.visibility = workflowOpacity <= 0.002 ? "hidden" : "visible";
    target.style.pointerEvents = settle > 0.88 && exiting < 0.08 ? "auto" : "none";
    target.style.setProperty("--clarity", `${settle}`);
    target.style.setProperty("--chapter-progress", `${birth}`);
    target.style.setProperty("--seed", "0");
    target.style.setProperty("--travel", "1");
    target.dataset.active = settle > 0.88 && exiting < 0.08 ? "true" : "false";
    return;
  }

  target.style.transform = `translate3d(${x}px, ${y}px, 0) scale(${growth * (1 - exiting * 0.06)})`;
  target.style.opacity = `${opacity}`;
  target.style.visibility = opacity <= 0.002 ? "hidden" : "visible";
  target.style.pointerEvents = clarity > 0.88 && exiting < 0.08 ? "auto" : "none";
  target.style.setProperty("--clarity", `${clarity}`);
  target.style.setProperty("--chapter-progress", `${birth}`);
  target.style.setProperty("--seed", `${seed}`);
  target.style.setProperty("--travel", `${travel}`);
  target.dataset.active = clarity > 0.88 && exiting < 0.08 ? "true" : "false";
}

function renderDesktopFirstChapterHandoff(
  progress: number,
  timing: (typeof timings)[number],
  target: HTMLElement,
) {
  const arrivalEnd = timing.travelEnd ?? timing.end;
  const entry = smoothstep(phase(progress, timing.miniatureEnd - 0.002, arrivalEnd));
  const exiting = smoothstep(phase(
    progress,
    timing.exitStart - 0.009,
    timing.exitStart + 0.007,
  ));
  const birth = phase(progress, timing.start, timing.exitEnd);
  const entryDirection = target.dataset.side === "left" ? -1 : 1;
  const x = (1 - entry) * entryDirection * 32 + exiting * entryDirection * 76;
  const y = (1 - entry) * 14 - exiting * 34;
  const opacity = entry * (1 - exiting);

  target.style.transform = `translate3d(${x}px, ${y}px, 0) scale(${mix(0.94, 1, entry) * (1 - exiting * 0.06)})`;
  target.style.opacity = `${opacity}`;
  target.style.visibility = opacity <= 0.002 ? "hidden" : "visible";
  target.style.pointerEvents = entry > 0.88 && exiting < 0.08 ? "auto" : "none";
  target.style.setProperty("--clarity", `${entry}`);
  target.style.setProperty("--chapter-progress", `${birth}`);
  target.style.setProperty("--seed", "0");
  target.style.setProperty("--travel", `${entry}`);
  target.dataset.active = entry > 0.88 && exiting < 0.08 ? "true" : "false";
}

type ConvergenceOptions = {
  progress: number;
  stage: HTMLElement;
  planet: HTMLDivElement;
  aiSource: HTMLDivElement;
  humanSource: HTMLDivElement;
  aiPanel: HTMLDivElement;
  humanPanel: HTMLDivElement;
  unifiedPanel: HTMLDivElement;
  mobile: boolean;
  reducedMotion: boolean;
};

function renderIntelligenceConvergence({
  progress,
  stage,
  planet,
  aiSource,
  humanSource,
  aiPanel,
  humanPanel,
  unifiedPanel,
  mobile,
  reducedMotion,
}: ConvergenceOptions) {
  const awaken = smoothstep(phase(progress, intelligenceBridge.start, intelligenceBridge.emergeEnd));
  const travel = smoothstep(phase(progress, intelligenceBridge.start + 0.006, intelligenceBridge.stageEnd));
  const spiral = smoothstep(phase(
    progress,
    intelligenceBridge.emergeEnd - 0.003,
    intelligenceBridge.mergeEnd - 0.001,
  ));
  const attraction = reducedMotion
    ? smoothstep(phase(spiral, 0.28, 1))
    : Math.pow(spiral, 1.22);
  const assimilation = smoothstep(phase(spiral, reducedMotion ? 0.56 : 0.76, 0.99));
  const exiting = smoothstep(phase(progress, intelligenceBridge.holdEnd, intelligenceBridge.exitEnd));
  const stageBounds = stage.getBoundingClientRect();
  const planetBounds = planet.getBoundingClientRect();
  const planetX = planetBounds.left - stageBounds.left + planetBounds.width * 0.5;
  const planetY = planetBounds.top - stageBounds.top + planetBounds.height * 0.5;
  const radius = planetBounds.width * (mobile ? 0.34 : 0.43);
  const sourceCenter = (source: HTMLDivElement) => {
    const bounds = source.getBoundingClientRect();
    return {
      x: bounds.left - stageBounds.left + bounds.width * 0.5,
      y: bounds.top - stageBounds.top + bounds.height * 0.5,
    };
  };
  const placePanel = (
    panel: HTMLDivElement,
    source: { x: number; y: number },
    destination: { x: number; y: number },
    depth: number,
  ) => {
    const halfWidth = panel.offsetWidth * 0.5;
    const halfHeight = panel.offsetHeight * 0.5;
    const safeDestination = {
      x: clamp(destination.x, halfWidth + 18, stageBounds.width - halfWidth - 18),
      y: clamp(destination.y, halfHeight + 94, stageBounds.height - halfHeight - 52),
    };
    const centerX = mix(source.x, safeDestination.x, travel);
    const centerY = mix(source.y, safeDestination.y, travel);
    const depthScale = reducedMotion ? 1 : mix(0.96, 1.035, depth);
    const coreScale = mix(1, mobile ? 0.46 : 0.52, assimilation);
    const scale = mix(0.06, 1, awaken) * depthScale * coreScale;
    const depthOpacity = reducedMotion ? 1 : mix(0.84, 1, depth);
    const opacity = awaken * depthOpacity * (1 - assimilation) * (1 - exiting);
    panel.style.transform = `translate3d(${centerX - halfWidth}px, ${centerY - halfHeight}px, 0) scale(${scale})`;
    panel.style.opacity = `${opacity}`;
    panel.style.visibility = opacity <= 0.002 ? "hidden" : "visible";
    panel.style.zIndex = "14";
    panel.style.setProperty("--orbit-depth", `${depth}`);
    panel.style.setProperty("--assimilation", `${assimilation}`);
  };

  const aiOrigin = sourceCenter(aiSource);
  const humanOrigin = sourceCenter(humanSource);
  const radiusScale = mix(1, reducedMotion ? 0.1 : 0.028, attraction);
  const horizontalSpread = radius * radiusScale;
  const verticalSpread = radius * (mobile ? 0.56 : 0.61) * radiusScale;
  const angularProgress = reducedMotion
    ? spiral
    : spiral * 0.9 + spiral * spiral * 0.1;
  const turns = reducedMotion ? 0.35 : 2.55;
  const orbitAngle = -Math.PI * 0.78 + angularProgress * Math.PI * 2 * turns;
  const aiDepth = (Math.sin(orbitAngle) + 1) * 0.5;
  const humanDepth = (Math.sin(orbitAngle + Math.PI) + 1) * 0.5;
  placePanel(
    aiPanel,
    aiOrigin,
    {
      x: planetX + Math.cos(orbitAngle) * horizontalSpread,
      y: planetY + Math.sin(orbitAngle) * verticalSpread,
    },
    aiDepth,
  );
  placePanel(
    humanPanel,
    humanOrigin,
    {
      x: planetX + Math.cos(orbitAngle + Math.PI) * horizontalSpread,
      y: planetY + Math.sin(orbitAngle + Math.PI) * verticalSpread,
    },
    humanDepth,
  );
  aiPanel.style.setProperty("--orbit-progress", `${spiral}`);
  humanPanel.style.setProperty("--orbit-progress", `${spiral}`);

  const halfWidth = unifiedPanel.offsetWidth * 0.5;
  const halfHeight = unifiedPanel.offsetHeight * 0.5;
  const unifiedIn = smoothstep(phase(spiral, reducedMotion ? 0.68 : 0.84, 1));
  const unifiedOpacity = unifiedIn * (1 - exiting);
  const unifiedScale = mix(0.62, 1, unifiedIn) * (1 - exiting * 0.035);
  const unifiedX = clamp(
    planetX,
    halfWidth + 18,
    stageBounds.width - halfWidth - 18,
  );
  const unifiedY = clamp(
    planetY,
    halfHeight + 92,
    stageBounds.height - halfHeight - 52,
  );
  unifiedPanel.style.transform = `translate3d(${unifiedX - halfWidth}px, ${unifiedY - halfHeight - exiting * 18}px, 0) scale(${unifiedScale})`;
  unifiedPanel.style.opacity = `${unifiedOpacity}`;
  unifiedPanel.style.visibility = unifiedOpacity <= 0.002 ? "hidden" : "visible";
  unifiedPanel.style.setProperty("--convergence", `${unifiedIn}`);
}

function getPlanetSide(
  progress: number,
  chapterTimings = timings,
  firstDockStart = HERO_DOCK_START,
  firstMiniatureEnd = chapterTimings[0].miniatureEnd,
) {
  const firstDock = smoothstep(phase(progress, firstDockStart, firstMiniatureEnd));
  const sideForChapter = (index: number) => chapters[index].side === "left" ? 1 : -1;
  let position = mix(0, sideForChapter(0), firstDock);

  for (let chapterIndex = 1; chapterIndex < chapters.length; chapterIndex += 1) {
    const previousSide = sideForChapter(chapterIndex - 1);
    const nextSide = sideForChapter(chapterIndex);
    if (previousSide === nextSide) continue;
    const transitionStart = chapterTimings[chapterIndex - 1].exitStart;
    const transitionEnd = chapterTimings[chapterIndex].miniatureEnd;
    if (progress >= transitionStart) {
      position = mix(previousSide, nextSide, smoothstep(phase(progress, transitionStart, transitionEnd)));
    }
  }

  return position;
}

type SignalSystemBridgeOptions = {
  bridge: SVGSVGElement;
  stage: HTMLElement;
  source: HTMLDivElement;
  target: HTMLElement;
  mobile: boolean;
  travel: number;
  rail: number;
};

function renderSignalSystemBridge({
  bridge,
  stage,
  source,
  target,
  mobile,
  travel,
  rail,
}: SignalSystemBridgeOptions) {
  const stageBounds = stage.getBoundingClientRect();
  const sourceBounds = source.getBoundingClientRect();
  if (stageBounds.width <= 0 || stageBounds.height <= 0) return;

  bridge.setAttribute("viewBox", `0 0 ${stageBounds.width} ${stageBounds.height}`);

  const sourceX = sourceBounds.left - stageBounds.left + sourceBounds.width * 0.5;
  const sourceY = sourceBounds.top - stageBounds.top + sourceBounds.height * 0.5;
  const railX = target.offsetLeft + (
    target.dataset.side === "left"
      ? mobile ? 4 : 1.5
      : target.offsetWidth - (mobile ? 4 : 1.5)
  );
  const railStartY = target.offsetTop + target.offsetHeight * (mobile ? 0.16 : 0.24);
  const railEndY = target.offsetTop + target.offsetHeight * (mobile ? 0.84 : 0.76);
  const controlA = mobile
    ? { x: sourceX - 22, y: sourceY - 30 }
    : { x: sourceX - Math.min(140, stageBounds.width * 0.08), y: sourceY - 74 };
  const controlB = mobile
    ? { x: railX + 72, y: railStartY + 36 }
    : { x: railX + Math.min(230, stageBounds.width * 0.14), y: railStartY - 56 };
  const travelPath = bridge.querySelector<SVGPathElement>(".signal-system-bridge-travel");
  const railPath = bridge.querySelector<SVGPathElement>(".signal-system-bridge-rail");
  const node = bridge.querySelector<SVGCircleElement>(".signal-system-bridge-node");
  if (!travelPath || !railPath || !node) return;

  travelPath.setAttribute(
    "d",
    `M ${sourceX} ${sourceY} C ${controlA.x} ${controlA.y}, ${controlB.x} ${controlB.y}, ${railX} ${railStartY}`,
  );
  railPath.setAttribute("d", `M ${railX} ${railStartY} L ${railX} ${railEndY}`);

  if (rail > 0.001) {
    node.setAttribute("cx", `${railX}`);
    node.setAttribute("cy", `${mix(railStartY, railEndY, rail)}`);
    return;
  }

  const pathLength = travelPath.getTotalLength();
  const point = travelPath.getPointAtLength(pathLength * travel);
  node.setAttribute("cx", `${point.x}`);
  node.setAttribute("cy", `${point.y}`);
}

type BrandSignalFieldOptions = {
  field: SVGSVGElement;
  stage: HTMLElement;
  planet: HTMLDivElement;
  mobile: boolean;
};

function renderBrandSignalField({
  field,
  stage,
  planet,
  mobile,
}: BrandSignalFieldOptions) {
  const stageBounds = stage.getBoundingClientRect();
  const planetBounds = planet.getBoundingClientRect();
  if (stageBounds.width <= 0 || stageBounds.height <= 0) return;

  field.setAttribute("viewBox", `0 0 ${stageBounds.width} ${stageBounds.height}`);

  const centerX = planetBounds.left - stageBounds.left + planetBounds.width * 0.5;
  const centerY = planetBounds.top - stageBounds.top + planetBounds.height * 0.5;
  const tablet = !mobile && stageBounds.width <= 1180;
  const orbitProfiles: Record<BrandSignal, {
    centerOffsetX: number;
    frontHalf: "upper" | "lower";
    nodeAngle: number;
    rotation: number;
    rx: number;
    ry: number;
    labelOffset: [number, number];
  }> = {
    precision: {
      centerOffsetX: 0,
      frontHalf: "lower",
      nodeAngle: 250,
      rotation: -12,
      rx: planetBounds.width * (mobile ? 0.44 : 0.46),
      ry: planetBounds.height * (mobile ? 0.13 : 0.14),
      labelOffset: mobile
        ? [-planetBounds.width * 0.56, planetBounds.height * 0.51]
        : tablet
          ? [planetBounds.width * 0.16, -planetBounds.height * 0.19]
          : [-planetBounds.width * 0.43, -planetBounds.height * 0.19],
    },
    judgment: {
      centerOffsetX: 0,
      frontHalf: "lower",
      nodeAngle: 290,
      rotation: 32,
      rx: planetBounds.width * (mobile ? 0.4 : 0.43),
      ry: planetBounds.height * (mobile ? 0.2 : 0.21),
      labelOffset: mobile
        ? [planetBounds.width * 0.06, planetBounds.height * 0.505]
        : [planetBounds.width * 0.16, tablet ? planetBounds.height * 0.01 : -planetBounds.height * 0.2],
    },
    trust: {
      centerOffsetX: 0,
      frontHalf: "upper",
      nodeAngle: 110,
      rotation: -36,
      rx: planetBounds.width * (mobile ? 0.46 : 0.5),
      ry: planetBounds.height * (mobile ? 0.25 : 0.27),
      labelOffset: mobile
        ? [planetBounds.width * 0.08, planetBounds.height * 0.62]
        : [planetBounds.width * 0.16, planetBounds.height * 0.14],
    },
  };
  Object.values(orbitProfiles).forEach((profile) => {
    const rotation = profile.rotation * (Math.PI / 180);
    const horizontalExtent = Math.hypot(
      profile.rx * Math.cos(rotation),
      profile.ry * Math.sin(rotation),
    );
    const safeRight = Math.max(1, stageBounds.width - centerX - 18);
    if (horizontalExtent <= safeRight) return;
    const overflow = horizontalExtent - safeRight;
    profile.centerOffsetX = -overflow * 0.56;
    const fittedScale = Math.min(1, (safeRight - profile.centerOffsetX) / horizontalExtent);
    profile.rx *= fittedScale;
    profile.ry *= fittedScale;
  });
  const pointOnOrbit = (
    profile: (typeof orbitProfiles)[BrandSignal],
    angle: number,
  ) => {
    const rotation = profile.rotation * (Math.PI / 180);
    const x = Math.cos(angle) * profile.rx;
    const y = Math.sin(angle) * profile.ry;
    return {
      x: centerX + profile.centerOffsetX + x * Math.cos(rotation) - y * Math.sin(rotation),
      y: centerY + x * Math.sin(rotation) + y * Math.cos(rotation),
    };
  };
  const orbitPath = (
    profile: (typeof orbitProfiles)[BrandSignal],
    start: number,
    end: number,
    steps = 72,
  ) => Array.from({ length: steps + 1 }, (_, index) => {
    const angle = start + (end - start) * (index / steps);
    const point = pointOnOrbit(profile, angle);
    return `${index === 0 ? "M" : "L"} ${point.x.toFixed(2)} ${point.y.toFixed(2)}`;
  }).join(" ");
  const occlusionField = field.querySelector<SVGRectElement>(".brand-signal-occlusion-field");
  const occlusionCore = field.querySelector<SVGCircleElement>(".brand-signal-occlusion-core");
  occlusionField?.setAttribute("x", "0");
  occlusionField?.setAttribute("y", "0");
  occlusionField?.setAttribute("width", `${stageBounds.width}`);
  occlusionField?.setAttribute("height", `${stageBounds.height}`);
  occlusionCore?.setAttribute("cx", `${centerX}`);
  occlusionCore?.setAttribute("cy", `${centerY}`);
  occlusionCore?.setAttribute("r", `${planetBounds.width * (mobile ? 0.325 : 0.335)}`);

  const labelWidth = mobile ? 156 : 220;
  const labelHeight = mobile ? 86 : 90;

  brandSignals.forEach(({ signal }) => {
    const profile = orbitProfiles[signal];
    const node = pointOnOrbit(profile, profile.nodeAngle * (Math.PI / 180));
    const frontStart = profile.frontHalf === "upper" ? 0 : Math.PI;
    const frontEnd = frontStart + Math.PI;
    const backStart = frontEnd % (Math.PI * 2);
    const backEnd = backStart + Math.PI;
    const fullPath = orbitPath(profile, 0, Math.PI * 2, 120);
    const frontPath = orbitPath(profile, frontStart, frontEnd);
    const backPath = orbitPath(profile, backStart, backEnd);
    const rawLabelX = node.x + profile.labelOffset[0];
    const rawLabelY = node.y + profile.labelOffset[1];
    const labelX = clamp(rawLabelX, mobile ? 18 : 24, stageBounds.width - labelWidth - 12);
    const labelY = clamp(rawLabelY, mobile ? 88 : 104, stageBounds.height - labelHeight - 18);
    const connectorEndX = signal === "precision" ? labelX + labelWidth - 12 : labelX - 8;
    const connectorEndY = labelY + (mobile ? 7 : 12);
    const d = `M ${node.x} ${node.y} L ${connectorEndX} ${connectorEndY}`;
    const group = field.querySelector<SVGGElement>(`[data-brand-signal="${signal}"]`);
    if (!group) return;

    group.querySelectorAll<SVGPathElement>('[data-orbit-segment="full"]').forEach((orbit) => {
      orbit.setAttribute("d", fullPath);
    });
    group.querySelectorAll<SVGPathElement>('[data-orbit-segment="front"]').forEach((orbit) => {
      orbit.setAttribute("d", frontPath);
    });
    group.querySelectorAll<SVGPathElement>('[data-orbit-segment="back"]').forEach((orbit) => {
      orbit.setAttribute("d", backPath);
    });
    group.querySelectorAll<SVGPathElement>(".brand-signal-connector").forEach((connector) => {
      connector.setAttribute("d", d);
    });
    group.querySelector<SVGGElement>(".brand-signal-beacon")
      ?.setAttribute("transform", `translate(${node.x} ${node.y})`);
    const labelBackdrop = group.querySelector<SVGRectElement>(".brand-signal-label-backdrop");
    labelBackdrop?.setAttribute("x", `${labelX - 10}`);
    labelBackdrop?.setAttribute("y", `${labelY - (mobile ? 17 : 19)}`);
    labelBackdrop?.setAttribute("width", `${labelWidth}`);
    labelBackdrop?.setAttribute("height", `${labelHeight}`);
    const labelHit = group.querySelector<SVGRectElement>(".brand-signal-label-hit");
    labelHit?.setAttribute("x", `${labelX - 12}`);
    labelHit?.setAttribute("y", `${labelY - (mobile ? 22 : 25)}`);
    labelHit?.setAttribute("width", `${labelWidth + 12}`);
    labelHit?.setAttribute("height", `${mobile ? 98 : 102}`);
    group.querySelectorAll<SVGTextElement>(".brand-signal-target-label").forEach((label) => {
      label.setAttribute("x", `${labelX}`);
      label.setAttribute("y", `${labelY}`);
    });
  });
}

export default function Home() {
  const trackRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLElement>(null);
  const planetRef = useRef<HTMLDivElement>(null);
  const heroRef = useRef<HTMLDivElement>(null);
  const whyFtoShellRef = useRef<HTMLElement>(null);
  const beaconRefs = useRef<Array<HTMLDivElement | null>>([]);
  const panelRefs = useRef<Array<HTMLElement | null>>([]);
  const convergenceAiRef = useRef<HTMLDivElement>(null);
  const convergenceHumanRef = useRef<HTMLDivElement>(null);
  const convergenceUnifiedRef = useRef<HTMLDivElement>(null);
  const motionDebugRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const offerTriggerRef = useRef<HTMLElement | null>(null);
  const offerModalOpenRef = useRef(false);
  const signalSystemBridgeRef = useRef<SVGSVGElement>(null);
  const brandSignalFieldRef = useRef<SVGSVGElement>(null);
  const brandSignalCardRefs = useRef<Record<BrandSignal, HTMLButtonElement | null>>({
    precision: null,
    judgment: null,
    trust: null,
  });
  const brandSignalFocusRef = useRef<BrandSignal | null>(null);
  const brandSignalInteractionRef = useRef(false);
  const brandSignalTouchTimerRef = useRef<number | null>(null);
  const judgmentFocusRef = useRef<JudgmentForce | null>(null);
  const outcomeFocusRef = useRef<OutcomeSignal>("clarity");
  const outcomeSequenceRef = useRef(0);
  const outcomeTabRefs = useRef<Record<OutcomeSignal, HTMLButtonElement | null>>({
    clarity: null,
    consistency: null,
    confidence: null,
    trust: null,
  });
  const industryTabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const industryNodeRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const industryFocusRef = useRef<IndustrySignal>("insurance");
  const industryPreviewRef = useRef(false);
  const industrySequenceRef = useRef(0);
  const perspectiveFocusRef = useRef<PerspectiveKey | null>(null);
  const aiCapabilityFocusRef = useRef<AICapabilityKey | null>(null);
  const perspectiveOverlayOpenRef = useRef(false);
  const perspectiveOverlayRef = useRef<HTMLDivElement>(null);
  const perspectiveCloseButtonRef = useRef<HTMLButtonElement>(null);
  const perspectiveTriggerRef = useRef<HTMLButtonElement | null>(null);
  const perspectiveRestoreFocusRef = useRef(true);
  const perspectiveCloseTimerRef = useRef<number | null>(null);
  const whyFtoStateRef = useRef<WhyFtoState>("standard");
  const whyFtoPerspectiveRef = useRef<WhyFtoPerspective>("neutral");
  const ftoAiDisplayedViewRef = useRef<FtoAiPanelView>("overview");
  const ftoAiTransitionFrameRef = useRef<number | null>(null);
  const ftoAiTransitionTimerRef = useRef<number | null>(null);
  const ftoPeopleDisplayedViewRef = useRef<FtoPeoplePanelView>("overview");
  const ftoPeopleTransitionFrameRef = useRef<number | null>(null);
  const ftoPeopleTransitionTimerRef = useRef<number | null>(null);
  const solutionTransitionFrameRef = useRef<number | null>(null);
  const solutionTransitionTimerRef = useRef<number | null>(null);
  const industryTransitionFrameRef = useRef<number | null>(null);
  const industryTransitionTimerRef = useRef<number | null>(null);
  const aiDirectMotionStartRef = useRef<number | null>(null);
  const aiDirectRenderRequestRef = useRef<(() => void) | null>(null);
  const planetMotionRef = useRef<PlanetMotionState>({
    preference: "auto",
    mode: "full",
    speed: 1,
    precision: 0,
    aiCapabilityFocus: null,
    brandSignal: 0,
    brandSignalFocus: null,
    judgment: 0,
    judgmentFocus: null,
    outcomes: 0,
    outcomeFocus: null,
    outcomeSequence: 0,
    industries: 0,
    industryFocus: null,
    industryPreview: false,
    industrySequence: 0,
    questions: 0,
    questionCategory: "about",
    questionPreview: false,
    questionSequence: 0,
    perspectives: 0,
    perspectiveFocus: null,
    aiWorld: 0,
    humanWorld: 0,
    contact: 0,
  });
  const [progress, setProgress] = useState(0);
  const [activeChapter, setActiveChapter] = useState(-1);
  const [convergenceActive, setConvergenceActive] = useState(false);
  const [whyFtoActive, setWhyFtoActive] = useState(false);
  const [whyFtoState, setWhyFtoState] = useState<WhyFtoState>("standard");
  const [whyFtoPerspective, setWhyFtoPerspective] = useState<WhyFtoPerspective>("neutral");
  const [selectedFtoAiWorkflow, setSelectedFtoAiWorkflow] = useState<FtoAiWorkflowKey | null>(null);
  const [displayedFtoAiView, setDisplayedFtoAiView] = useState<FtoAiPanelView>("overview");
  const [previousFtoAiView, setPreviousFtoAiView] = useState<FtoAiPanelView | null>(null);
  const [ftoAiTransitionPhase, setFtoAiTransitionPhase] = useState<FtoAiTransitionPhase>("idle");
  const [selectedFtoPeopleWorkflow, setSelectedFtoPeopleWorkflow] = useState<FtoPeopleWorkflowKey | null>(null);
  const [displayedFtoPeopleView, setDisplayedFtoPeopleView] = useState<FtoPeoplePanelView>("overview");
  const [previousFtoPeopleView, setPreviousFtoPeopleView] = useState<FtoPeoplePanelView | null>(null);
  const [ftoPeopleTransitionPhase, setFtoPeopleTransitionPhase] = useState<FtoPeopleTransitionPhase>("idle");
  const [workflowStage, setWorkflowStage] = useState(0);
  const [workflowFocus, setWorkflowFocus] = useState<number | null>(null);
  const [deliveryMode, setDeliveryMode] = useState<"ai" | "unified" | "human">("unified");
  const [activeSolution, setActiveSolution] = useState(0);
  const [displayedSolution, setDisplayedSolution] = useState(0);
  const [solutionDetailPhase, setSolutionDetailPhase] = useState<SolutionDetailPhase>("idle");
  const [selectedIndustry, setSelectedIndustry] = useState(0);
  const [displayedIndustry, setDisplayedIndustry] = useState(0);
  const [industryDetailPhase, setIndustryDetailPhase] = useState<IndustryDetailPhase>("idle");
  const [previewIndustry, setPreviewIndustry] = useState<number | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [offerModalState, setOfferModalState] = useState<{ activeOfferId: OfferId | null } | null>(null);
  const [brandSignalReady, setBrandSignalReady] = useState(false);
  const [brandSignalFocus, setBrandSignalFocus] = useState<BrandSignal | null>(null);
  const [judgmentFocus, setJudgmentFocus] = useState<JudgmentForce | null>(null);
  const [outcomeReady, setOutcomeReady] = useState(false);
  const [selectedOutcome, setSelectedOutcome] = useState<OutcomeSignal>("clarity");
  const [previewOutcome, setPreviewOutcome] = useState<OutcomeSignal | null>(null);
  const [, setOutcomeSequence] = useState(0);
  const [perspectiveFocus, setPerspectiveFocus] = useState<PerspectiveKey | null>(null);
  const [aiCapabilityFocus, setAiCapabilityFocus] = useState<AICapabilityKey | null>(null);
  const [perspectiveOverlay, setPerspectiveOverlay] = useState<PerspectiveKey | null>(null);
  const [perspectiveOverlayClosing, setPerspectiveOverlayClosing] = useState(false);
  const activeOutcomeKey = previewOutcome ?? selectedOutcome;
  const activeOutcome = brandOutcomes.find(({ key }) => key === activeOutcomeKey) ?? brandOutcomes[0];
  const activeIndustryIndex = previewIndustry ?? selectedIndustry;
  const activeIndustry = industries[activeIndustryIndex];
  const renderedIndustry = previewIndustry !== null
    ? activeIndustry
    : industries[displayedIndustry];
  const activePerspective = perspectiveOverlay
    ? perspectiveArticles[perspectiveOverlay]
    : null;
  const perspectiveOverlayOpen = perspectiveOverlay !== null;
  const offerModalOpen = offerModalState !== null;

  const selectSolution = (index: number, immediate = false) => {
    if (solutionTransitionTimerRef.current !== null) {
      window.clearTimeout(solutionTransitionTimerRef.current);
      solutionTransitionTimerRef.current = null;
    }
    if (solutionTransitionFrameRef.current !== null) {
      window.cancelAnimationFrame(solutionTransitionFrameRef.current);
      solutionTransitionFrameRef.current = null;
    }

    setActiveSolution(index);
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
      || planetMotionRef.current.mode === "reduced";
    if (immediate || window.innerWidth <= 1180 || reducedMotion || index === displayedSolution) {
      setDisplayedSolution(index);
      setSolutionDetailPhase("idle");
      return;
    }

    setSolutionDetailPhase("exit");
    solutionTransitionTimerRef.current = window.setTimeout(() => {
      setDisplayedSolution(index);
      setSolutionDetailPhase("enter");
      solutionTransitionTimerRef.current = null;
      solutionTransitionFrameRef.current = window.requestAnimationFrame(() => {
        solutionTransitionFrameRef.current = window.requestAnimationFrame(() => {
          setSolutionDetailPhase("idle");
          solutionTransitionFrameRef.current = null;
        });
      });
    }, 95);
  };

  const selectBrandSignal = (signal: BrandSignal | null, interactive = false) => {
    if (brandSignalTouchTimerRef.current !== null) {
      window.clearTimeout(brandSignalTouchTimerRef.current);
      brandSignalTouchTimerRef.current = null;
    }
    brandSignalInteractionRef.current = interactive;
    brandSignalFocusRef.current = signal;
    planetMotionRef.current = {
      ...planetMotionRef.current,
      brandSignalFocus: signal,
    };
    setBrandSignalFocus(signal);
  };

  const releaseTouchBrandSignal = () => {
    if (brandSignalTouchTimerRef.current !== null) {
      window.clearTimeout(brandSignalTouchTimerRef.current);
    }
    brandSignalTouchTimerRef.current = window.setTimeout(() => {
      selectBrandSignal(null);
    }, 1700);
  };

  const selectJudgmentForce = (force: JudgmentForce | null) => {
    judgmentFocusRef.current = force;
    planetMotionRef.current = {
      ...planetMotionRef.current,
      judgmentFocus: force,
    };
    setJudgmentFocus(force);
  };

  const selectAiCapability = (capability: AICapabilityKey | null) => {
    aiCapabilityFocusRef.current = capability;
    planetMotionRef.current = {
      ...planetMotionRef.current,
      aiCapabilityFocus: capability,
    };
    if (stageRef.current?.dataset.aiSpeed === "active") {
      stageRef.current.dataset.aiCapability = capability ?? "none";
    }
    setAiCapabilityFocus(capability);
  };

  const applyOutcomeFocus = (outcome: OutcomeSignal, restart = false) => {
    const changed = outcomeFocusRef.current !== outcome;
    if (changed || restart) {
      outcomeSequenceRef.current += 1;
      setOutcomeSequence(outcomeSequenceRef.current);
    }
    outcomeFocusRef.current = outcome;
    planetMotionRef.current = {
      ...planetMotionRef.current,
      outcomeFocus: outcome,
      outcomeSequence: outcomeSequenceRef.current,
    };
    if (stageRef.current?.dataset.outcomeReady === "true") {
      stageRef.current.dataset.outcomeFocus = outcome;
    }
  };

  const selectOutcome = (outcome: OutcomeSignal) => {
    setSelectedOutcome(outcome);
    setPreviewOutcome(null);
    applyOutcomeFocus(outcome, true);
  };

  const previewOutcomeLens = (outcome: OutcomeSignal | null) => {
    setPreviewOutcome(outcome);
    applyOutcomeFocus(outcome ?? selectedOutcome);
  };

  const applyIndustryFocus = (index: number, preview: boolean) => {
    const industry = industries[index];
    industryFocusRef.current = industry.key;
    industryPreviewRef.current = preview;
    if (stageRef.current?.dataset.industryReady === "true") {
      stageRef.current.dataset.industryFocus = industry.key;
      stageRef.current.dataset.industryPreview = preview ? "true" : "false";
    }
    planetMotionRef.current = {
      ...planetMotionRef.current,
      industryFocus: industry.key,
      industryPreview: preview,
    };
  };

  const previewIndustryStandard = (index: number | null) => {
    setPreviewIndustry(index);
    applyIndustryFocus(index ?? selectedIndustry, index !== null);
  };

  const selectIndustryStandard = (index: number, immediate = false) => {
    if (industryTransitionTimerRef.current !== null) {
      window.clearTimeout(industryTransitionTimerRef.current);
      industryTransitionTimerRef.current = null;
    }
    if (industryTransitionFrameRef.current !== null) {
      window.cancelAnimationFrame(industryTransitionFrameRef.current);
      industryTransitionFrameRef.current = null;
    }

    setSelectedIndustry(index);
    setPreviewIndustry(null);
    applyIndustryFocus(index, false);
    industrySequenceRef.current += 1;
    planetMotionRef.current = {
      ...planetMotionRef.current,
      industrySequence: industrySequenceRef.current,
    };

    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
      || planetMotionRef.current.mode === "reduced";
    if (immediate || window.innerWidth <= 1180 || reducedMotion || index === displayedIndustry) {
      setDisplayedIndustry(index);
      setIndustryDetailPhase("idle");
      return;
    }

    setIndustryDetailPhase("exit");
    industryTransitionTimerRef.current = window.setTimeout(() => {
      setDisplayedIndustry(index);
      setIndustryDetailPhase("enter");
      industryTransitionTimerRef.current = null;
      industryTransitionFrameRef.current = window.requestAnimationFrame(() => {
        industryTransitionFrameRef.current = window.requestAnimationFrame(() => {
          setIndustryDetailPhase("idle");
          industryTransitionFrameRef.current = null;
        });
      });
    }, 95);
  };

  const selectPerspectiveElement = (perspective: PerspectiveKey | null) => {
    perspectiveFocusRef.current = perspective;
    planetMotionRef.current = {
      ...planetMotionRef.current,
      perspectiveFocus: perspective,
    };
    setPerspectiveFocus(perspective);
  };

  const selectFtoAiWorkflow = (workflow: FtoAiWorkflowKey | null) => {
    const nextView: FtoAiPanelView = workflow ?? "overview";
    setSelectedFtoAiWorkflow(workflow);
    if (nextView === ftoAiDisplayedViewRef.current) return;

    if (ftoAiTransitionFrameRef.current !== null) {
      window.cancelAnimationFrame(ftoAiTransitionFrameRef.current);
    }
    if (ftoAiTransitionTimerRef.current !== null) {
      window.clearTimeout(ftoAiTransitionTimerRef.current);
    }

    setPreviousFtoAiView(ftoAiDisplayedViewRef.current);
    ftoAiDisplayedViewRef.current = nextView;
    setDisplayedFtoAiView(nextView);
    setFtoAiTransitionPhase("preparing");

    ftoAiTransitionFrameRef.current = window.requestAnimationFrame(() => {
      setFtoAiTransitionPhase("animating");
      ftoAiTransitionFrameRef.current = null;
    });
    ftoAiTransitionTimerRef.current = window.setTimeout(() => {
      setPreviousFtoAiView(null);
      setFtoAiTransitionPhase("idle");
      ftoAiTransitionTimerRef.current = null;
    }, 220);
  };

  const selectFtoPeopleWorkflow = (workflow: FtoPeopleWorkflowKey | null) => {
    const nextView: FtoPeoplePanelView = workflow ?? "overview";
    setSelectedFtoPeopleWorkflow(workflow);
    if (nextView === ftoPeopleDisplayedViewRef.current) return;

    if (ftoPeopleTransitionFrameRef.current !== null) {
      window.cancelAnimationFrame(ftoPeopleTransitionFrameRef.current);
    }
    if (ftoPeopleTransitionTimerRef.current !== null) {
      window.clearTimeout(ftoPeopleTransitionTimerRef.current);
    }

    setPreviousFtoPeopleView(ftoPeopleDisplayedViewRef.current);
    ftoPeopleDisplayedViewRef.current = nextView;
    setDisplayedFtoPeopleView(nextView);
    setFtoPeopleTransitionPhase("preparing");

    ftoPeopleTransitionFrameRef.current = window.requestAnimationFrame(() => {
      setFtoPeopleTransitionPhase("animating");
      ftoPeopleTransitionFrameRef.current = null;
    });
    ftoPeopleTransitionTimerRef.current = window.setTimeout(() => {
      setPreviousFtoPeopleView(null);
      setFtoPeopleTransitionPhase("idle");
      ftoPeopleTransitionTimerRef.current = null;
    }, 220);
  };

  const selectWhyFtoPerspective = (perspective: WhyFtoPerspective) => {
    const enteringAi = perspective === "ai" && whyFtoPerspectiveRef.current !== "ai";
    const enteringHuman = perspective === "human" && whyFtoPerspectiveRef.current !== "human";
    const stableDesktopPlanet = window.innerWidth > 1180;
    whyFtoPerspectiveRef.current = perspective;
    aiDirectMotionStartRef.current = !stableDesktopPlanet && perspective === "ai"
      ? AI_DIRECT_MOTION_PENDING
      : null;
    planetMotionRef.current = {
      ...planetMotionRef.current,
      perspectiveFocus: stableDesktopPlanet
        ? perspective === "neutral" ? null : perspective
        : planetMotionRef.current.perspectiveFocus,
      aiWorld: !stableDesktopPlanet && perspective === "ai" ? 1 : 0,
      humanWorld: !stableDesktopPlanet && perspective === "human" ? 1 : 0,
    };
    setWhyFtoPerspective(perspective);
    if (enteringAi) selectFtoAiWorkflow(null);
    if (enteringHuman) selectFtoPeopleWorkflow(null);
    if (perspective === "ai") aiDirectRenderRequestRef.current?.();
    window.requestAnimationFrame(() => {
      if (window.innerWidth <= 760) {
        document.querySelector<HTMLElement>(".why-fto-ai-human")?.scrollTo({
          top: 0,
          behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
        });
      }
      window.dispatchEvent(new Event("resize"));
    });
  };

  const openPerspective = (
    perspective: PerspectiveKey,
    trigger: HTMLButtonElement,
  ) => {
    if (perspectiveCloseTimerRef.current) {
      window.clearTimeout(perspectiveCloseTimerRef.current);
      perspectiveCloseTimerRef.current = null;
    }
    perspectiveTriggerRef.current = trigger;
    perspectiveRestoreFocusRef.current = true;
    perspectiveOverlayOpenRef.current = true;
    setMenuOpen(false);
    setPerspectiveOverlayClosing(false);
    setPerspectiveOverlay(perspective);
    selectPerspectiveElement(perspective);
  };

  const switchPerspective = (perspective: PerspectiveKey) => {
    setPerspectiveOverlay(perspective);
    selectPerspectiveElement(perspective);
    window.requestAnimationFrame(() => {
      perspectiveOverlayRef.current?.querySelector<HTMLElement>(".perspective-overlay-editorial")?.focus();
    });
  };

  const closePerspective = (navigateToContact = false) => {
    if (!perspectiveOverlay || perspectiveOverlayClosing) return;
    perspectiveRestoreFocusRef.current = !navigateToContact;
    setPerspectiveOverlayClosing(true);
    perspectiveCloseTimerRef.current = window.setTimeout(() => {
      perspectiveOverlayOpenRef.current = false;
      setPerspectiveOverlay(null);
      setPerspectiveOverlayClosing(false);
      selectPerspectiveElement(null);
      perspectiveCloseTimerRef.current = null;
      if (navigateToContact) {
        navigateTo("contact", chapters.length - 1);
      }
    }, 260);
  };
  useEffect(() => {
    let frame = 0;
    const searchParams = new URLSearchParams(window.location.search);
    const requestedMotion = searchParams.get("motion");
    const motionPreference: PlanetMotionPreference = requestedMotion === "full"
      || requestedMotion === "reduced"
      ? requestedMotion
      : "auto";
    const motionDebugEnabled = searchParams.get("motionDebug") === "1";
    const systemMotionPreference = window.matchMedia("(prefers-reduced-motion: reduce)");

    const render = () => {
      frame = 0;
      const track = trackRef.current;
      const stage = stageRef.current;
      const planet = planetRef.current;
      const hero = heroRef.current;
      if (!track || !stage || !planet || !hero) return;

      const mobile = window.matchMedia("(max-width: 760px)").matches;
      const reducedMotion = motionPreference === "reduced"
        || (motionPreference === "auto" && systemMotionPreference.matches);
      const effectiveMotionMode = reducedMotion ? "reduced" : "full";

      if (mobile) {
        const pageDistance = Math.max(
          1,
          document.documentElement.scrollHeight - window.innerHeight,
        );
        const mobileProgress = clamp(window.scrollY / pageDistance);
        const sectionMarker = 70 + window.innerHeight * 0.3;
        const mobileSections = chapters.map((chapter, index) => ({
          index,
          node: index === PERSPECTIVES_CHAPTER_INDEX
            ? whyFtoShellRef.current
            : panelRefs.current[index],
        }));
        let currentChapter = -1;

        for (const section of mobileSections) {
          if (section.node && section.node.getBoundingClientRect().top <= sectionMarker) {
            currentChapter = section.index;
          }
        }

        const currentPresentation = currentChapter >= 0
          ? chapters[currentChapter]
          : { color: "#009f66", rgb: "0,159,102" };
        const currentWhyFtoVisible = currentChapter === PERSPECTIVES_CHAPTER_INDEX;

        stage.dataset.mobileFlow = "true";
        stage.dataset.motionPreference = motionPreference;
        stage.dataset.motionMode = effectiveMotionMode;
        stage.dataset.heroPhase = currentChapter < 0 ? "horizon" : "chapters";
        stage.dataset.whyFtoActive = currentWhyFtoVisible ? "true" : "false";
        stage.dataset.whyFtoState = "ai-human";
        stage.dataset.outcomeReady = currentChapter === OUTCOMES_CHAPTER_INDEX ? "true" : "false";
        stage.dataset.industryReady = currentChapter === INDUSTRIES_CHAPTER_INDEX ? "true" : "false";
        stage.dataset.aboutReady = currentChapter === ABOUT_CHAPTER_INDEX ? "true" : "false";
        stage.dataset.standardReady = currentChapter === STANDARD_CHAPTER_INDEX ? "true" : "false";
        stage.style.setProperty("--hero-opacity", "1");
        stage.style.setProperty("--hero-pullback", "0");
        stage.style.setProperty("--hero-manifesto", "0");
        stage.style.setProperty("--hero-manifesto-exit", "0");
        stage.style.setProperty("--hero-signal", "0");
        stage.style.setProperty("--dock", "0");
        stage.style.setProperty("--planet-side", "0");
        stage.style.setProperty("--planet-speed", "1");
        stage.style.setProperty("--speed-effect", "0");
        stage.style.setProperty("--speed-pulse", "0");
        stage.style.setProperty("--active-accent", currentPresentation.color);
        stage.style.setProperty("--active-rgb", currentPresentation.rgb);

        planetMotionRef.current = {
          ...planetMotionRef.current,
          preference: motionPreference,
          mode: effectiveMotionMode,
          speed: 1,
          precision: 0,
          aiCapabilityFocus: null,
          brandSignal: 0,
          brandSignalFocus: null,
          judgment: 0,
          judgmentFocus: null,
          outcomes: 0,
          outcomeFocus: null,
          industries: 0,
          industryFocus: null,
          industryPreview: false,
          questions: 0,
          questionCategory: null,
          questionPreview: false,
          perspectives: 0,
          perspectiveFocus: null,
          aiWorld: 0,
          humanWorld: 0,
          contact: 0,
        };

        if (whyFtoStateRef.current !== "ai-human") {
          whyFtoStateRef.current = "ai-human";
          setWhyFtoState("ai-human");
        }
        setActiveChapter((current) => current === currentChapter ? current : currentChapter);
        setConvergenceActive((current) => current ? false : current);
        setWhyFtoActive((current) => current === currentWhyFtoVisible ? current : currentWhyFtoVisible);
        setOutcomeReady((current) => current === (currentChapter === OUTCOMES_CHAPTER_INDEX)
          ? current
          : currentChapter === OUTCOMES_CHAPTER_INDEX);
        setProgress((current) => Math.abs(current - mobileProgress) > 0.002
          || mobileProgress === 0
          || mobileProgress === 1
          ? mobileProgress
          : current);
        return;
      }

      stage.dataset.mobileFlow = "false";
      const bounds = track.getBoundingClientRect();
      const distance = Math.max(1, track.offsetHeight - window.innerHeight);
      const p = clamp(-bounds.top / distance);
      const desktop = window.innerWidth > 1180;
      const chapterTimings = desktop ? desktopTimings : timings;
      const firstChapterTiming = chapterTimings[0];
      const perspectiveTiming = chapterTimings[PERSPECTIVES_CHAPTER_INDEX];
      const heroTiming = desktop
        ? APPROVED_DESKTOP_HERO_TIMING
        : {
            pullbackStart: HERO_PULLBACK_START,
            pullbackEnd: HERO_PULLBACK_END,
            copyExitStart: HERO_COPY_EXIT_START,
            copyExitEnd: HERO_COPY_EXIT_END,
            manifestoStart: HERO_MANIFESTO_START,
            manifestoEnd: HERO_MANIFESTO_END,
            signalStart: HERO_SIGNAL_START,
            signalEnd: HERO_SIGNAL_END,
            dockStart: HERO_DOCK_START,
          };
      const activeSignalSystemTransition = desktop
        ? approvedDesktopSignalSystemTransition
        : signalSystemTransition;
      const legacySignalSystemEnabled = !desktop;
      const whyFtoEnter = smoothstep(phase(
        p,
        perspectiveTiming.start - 0.01,
        perspectiveTiming.travelStart,
      ));
      const whyFtoExit = smoothstep(phase(
        p,
        perspectiveTiming.exitStart,
        perspectiveTiming.exitEnd,
      ));
      const whyFtoPresence = whyFtoEnter * (1 - whyFtoExit);
      const whyFtoEditorialEnter = desktop
        ? smoothstep(phase(
            p,
            perspectiveTiming.start + 0.002,
            perspectiveTiming.end - 0.002,
          ))
        : whyFtoEnter;
      const whyFtoEditorialExit = desktop
        ? smoothstep(phase(
            p,
            perspectiveTiming.exitStart - 0.01,
            perspectiveTiming.exitStart + 0.008,
          ))
        : whyFtoExit;
      const whyFtoEditorialPresence = whyFtoEditorialEnter * (1 - whyFtoEditorialExit);
      const currentWhyFtoState: WhyFtoState = "ai-human";
      const currentWhyFtoActive = whyFtoPresence > 0.02;
      const currentWhyFtoVisible = whyFtoEditorialPresence > 0.02;
      const heroPullback = smoothstep(phase(p, heroTiming.pullbackStart, heroTiming.pullbackEnd));
      const heroCopyExit = smoothstep(phase(p, heroTiming.copyExitStart, heroTiming.copyExitEnd));
      const heroManifestoIn = smoothstep(phase(p, heroTiming.manifestoStart, heroTiming.manifestoEnd));
      const heroManifestoOut = smoothstep(phase(
        p,
        heroTiming.signalEnd - 0.002,
        firstChapterTiming.travelStart,
      ));
      const heroManifestoPresence = heroManifestoIn * (1 - heroManifestoOut);
      const heroSignalIn = smoothstep(phase(p, heroTiming.signalStart, heroTiming.signalEnd));
      const heroSignalOut = smoothstep(phase(
        p,
        activeSignalSystemTransition.signalStart,
        activeSignalSystemTransition.signalEnd,
      ));
      const heroSignal = heroSignalIn * (1 - heroSignalOut);
      const dock = smoothstep(phase(p, heroTiming.dockStart, firstChapterTiming.miniatureEnd));
      const aiTiming = perspectiveTiming;
      const scrollAiMotion = getAiMotionProfile(p, aiTiming);
      let directAiMotion = { presence: 0, pulseProgress: 0 };
      if (
        aiDirectMotionStartRef.current !== null
        && whyFtoPerspectiveRef.current === "ai"
      ) {
        if (aiDirectMotionStartRef.current === AI_DIRECT_MOTION_PENDING) {
          aiDirectMotionStartRef.current = window.performance.now();
        }
        const directProgress = clamp(
          (window.performance.now() - aiDirectMotionStartRef.current)
          / AI_DIRECT_MOTION_DURATION_MS,
        );
        directAiMotion = getAiMotionProfile(
          mix(
            aiTiming.travelStart - 0.004,
            aiTiming.exitEnd - 0.002,
            directProgress,
          ),
          aiTiming,
        );
        if (directProgress >= 1) aiDirectMotionStartRef.current = null;
      } else if (aiDirectMotionStartRef.current !== null) {
        aiDirectMotionStartRef.current = null;
      }
      const aiSpeedPresence = Math.max(
        scrollAiMotion.presence,
        directAiMotion.presence,
      );
      if (aiSpeedPresence <= 0.02 && aiCapabilityFocusRef.current !== null) {
        aiCapabilityFocusRef.current = null;
        setAiCapabilityFocus(null);
      }
      const humanTiming = perspectiveTiming;
      const humanJudgmentIn = smoothstep(phase(
        p,
        humanTiming.start - 0.004,
        humanTiming.end - 0.002,
      ));
      const humanJudgmentOut = smoothstep(phase(
        p,
        humanTiming.exitStart,
        humanTiming.exitEnd,
      ));
      const humanJudgmentPresence = humanJudgmentIn * (1 - humanJudgmentOut);
      if (humanJudgmentPresence <= 0.02 && judgmentFocusRef.current !== null) {
        judgmentFocusRef.current = null;
        setJudgmentFocus(null);
      }
      const stableWhyFtoPlanet = desktop
        && currentWhyFtoActive
        && currentWhyFtoState === "ai-human";
      const visibleAiSpeedPresence = stableWhyFtoPlanet ? 0 : aiSpeedPresence;
      const visibleHumanJudgmentPresence = stableWhyFtoPlanet ? 0 : humanJudgmentPresence;
      const aiSpeedTarget = reducedMotion
        ? AI_SPEED_REDUCED
        : mobile
          ? AI_SPEED_MOBILE
          : AI_SPEED_DESKTOP;
      const planetSpeed = mix(1, aiSpeedTarget, visibleAiSpeedPresence);
      const fallbackOrbitSpeed = mix(1, reducedMotion ? 1.08 : 5, visibleAiSpeedPresence);
      const signalTiming = firstChapterTiming;
      const brandSignalIn = smoothstep(phase(
        p,
        signalTiming.travelStart - 0.004,
        signalTiming.end,
      ));
      const brandSignalOut = smoothstep(phase(
        p,
        signalTiming.exitStart,
        signalTiming.exitEnd,
      ));
      const brandSignalPresence = brandSignalIn * (1 - brandSignalOut);
      const brandSignalSystemPresence = currentWhyFtoState === "standard"
        ? Math.max(heroSignal, brandSignalPresence)
        : 0;
      if (brandSignalSystemPresence <= 0.02 && brandSignalFocusRef.current !== null) {
        brandSignalFocusRef.current = null;
        setBrandSignalFocus(null);
      }
      const heroManifestoExit = reducedMotion
        ? 0
        : smoothstep(phase(
            p,
            activeSignalSystemTransition.statementExitStart,
            activeSignalSystemTransition.statementExitEnd,
          ));
      const signalSystemTravel = !legacySignalSystemEnabled
        ? 0
        : reducedMotion
        ? 0
        : smoothstep(phase(
            p,
            activeSignalSystemTransition.signalStart,
            activeSignalSystemTransition.signalEnd,
          ));
      const signalSystemRail = !legacySignalSystemEnabled
        ? 0
        : reducedMotion
        ? brandSignalIn
        : smoothstep(phase(
            p,
            activeSignalSystemTransition.railStart,
            activeSignalSystemTransition.railEnd,
          ));
      const signalSystemBridgeIn = reducedMotion
        ? 0
        : smoothstep(phase(
            p,
            activeSignalSystemTransition.signalStart,
            activeSignalSystemTransition.signalStart + 0.004,
          ));
      const signalSystemBridgeOut = reducedMotion
        ? 1
        : smoothstep(phase(
            p,
            activeSignalSystemTransition.railEnd - 0.003,
            activeSignalSystemTransition.complete + 0.002,
          ));
      const signalSystemBridgePresence = !legacySignalSystemEnabled
        || p >= activeSignalSystemTransition.complete
        ? 0
        : signalSystemBridgeIn * (1 - signalSystemBridgeOut);
      const signalSystemColor = smoothstep(phase(
        p,
        activeSignalSystemTransition.signalEnd - 0.005,
        activeSignalSystemTransition.railEnd,
      ));
      const revealSignalSystemElement = (start: number, end: number) => reducedMotion
        ? brandSignalIn
        : smoothstep(phase(p, start, end));
      const signalSystemKicker = revealSignalSystemElement(
        activeSignalSystemTransition.kickerStart,
        activeSignalSystemTransition.kickerEnd,
      );
      const signalSystemHeadline = revealSignalSystemElement(
        activeSignalSystemTransition.headlineStart,
        activeSignalSystemTransition.headlineEnd,
      );
      const signalSystemBody = revealSignalSystemElement(
        activeSignalSystemTransition.bodyStart,
        activeSignalSystemTransition.bodyEnd,
      );
      const signalSystemPrinciples = revealSignalSystemElement(
        activeSignalSystemTransition.principlesStart,
        activeSignalSystemTransition.principlesEnd,
      );
      const signalSystemPrecision = revealSignalSystemElement(
        activeSignalSystemTransition.precisionStart,
        activeSignalSystemTransition.precisionEnd,
      );
      const signalSystemJudgment = revealSignalSystemElement(
        activeSignalSystemTransition.judgmentStart,
        activeSignalSystemTransition.judgmentEnd,
      );
      const signalSystemTrust = revealSignalSystemElement(
        activeSignalSystemTransition.trustStart,
        activeSignalSystemTransition.trustEnd,
      );
      const outcomeTiming = chapterTimings[OUTCOMES_CHAPTER_INDEX];
      const outcomeIn = smoothstep(phase(
        p,
        outcomeTiming.travelStart - 0.004,
        outcomeTiming.end,
      ));
      const outcomeOut = smoothstep(phase(
        p,
        outcomeTiming.exitStart,
        outcomeTiming.exitEnd,
      ));
      const outcomePresence = outcomeIn * (1 - outcomeOut);
      const industryTiming = chapterTimings[INDUSTRIES_CHAPTER_INDEX];
      const industryIn = smoothstep(phase(
        p,
        industryTiming.travelStart + 0.002,
        industryTiming.end,
      ));
      const industryOut = smoothstep(phase(
        p,
        industryTiming.exitStart,
        industryTiming.exitEnd,
      ));
      const industryPresence = industryIn * (1 - industryOut);
      const standardTiming = chapterTimings[STANDARD_CHAPTER_INDEX];
      const standardIn = smoothstep(phase(
        p,
        standardTiming.travelStart - 0.004,
        standardTiming.end,
      ));
      const standardOut = smoothstep(phase(
        p,
        standardTiming.exitStart,
        standardTiming.exitEnd,
      ));
      const standardPresence = standardIn * (1 - standardOut);
      const aboutTiming = chapterTimings[ABOUT_CHAPTER_INDEX];
      const aboutIn = smoothstep(phase(
        p,
        aboutTiming.travelStart - 0.004,
        aboutTiming.end,
      ));
      const aboutOut = smoothstep(phase(
        p,
        aboutTiming.exitStart,
        aboutTiming.exitEnd,
      ));
      const aboutPresence = aboutIn * (1 - aboutOut);
      const perspectiveArrival = smoothstep(phase(
        p,
        perspectiveTiming.start - 0.004,
        perspectiveTiming.travelStart + 0.004,
      ));
      const perspectiveElements = smoothstep(phase(
        p,
        perspectiveTiming.travelStart + 0.002,
        perspectiveTiming.end,
      ));
      const perspectiveOut = smoothstep(phase(
        p,
        perspectiveTiming.exitStart,
        perspectiveTiming.exitEnd,
      ));
      const perspectivePresence = perspectiveArrival * (1 - perspectiveOut);
      const contactTiming = chapterTimings[CONTACT_CHAPTER_INDEX];
      const contactArrival = smoothstep(phase(
        p,
        contactTiming.start - 0.006,
        contactTiming.travelStart + 0.004,
      ));
      const contactSettle = smoothstep(phase(
        p,
        contactTiming.travelStart,
        contactTiming.end,
      ));
      const contactPresence = contactArrival;
      if (
        perspectivePresence <= 0.02
        && perspectiveFocusRef.current !== null
        && !perspectiveOverlayOpenRef.current
      ) {
        perspectiveFocusRef.current = null;
        setPerspectiveFocus(null);
      }
      planetMotionRef.current = {
        preference: motionPreference,
        mode: effectiveMotionMode,
        speed: planetSpeed,
        precision: visibleAiSpeedPresence,
        aiCapabilityFocus: visibleAiSpeedPresence > 0.02 ? aiCapabilityFocusRef.current : null,
        brandSignal: brandSignalSystemPresence,
        brandSignalFocus: currentWhyFtoState === "standard" ? brandSignalFocusRef.current : null,
        judgment: visibleHumanJudgmentPresence,
        judgmentFocus: visibleHumanJudgmentPresence > 0.02 ? judgmentFocusRef.current : null,
        outcomes: outcomePresence,
        outcomeFocus: outcomePresence > 0.02 ? outcomeFocusRef.current : null,
        outcomeSequence: outcomeSequenceRef.current,
        industries: industryPresence,
        industryFocus: industryPresence > 0.02 ? industryFocusRef.current : null,
        industryPreview: industryPresence > 0.02 && industryPreviewRef.current,
        industrySequence: industrySequenceRef.current,
        questions: 0,
        questionCategory: null,
        questionPreview: false,
        questionSequence: 0,
        perspectives: perspectivePresence,
        perspectiveFocus: stableWhyFtoPlanet
          ? whyFtoPerspectiveRef.current === "neutral" ? null : whyFtoPerspectiveRef.current
          : perspectivePresence > 0.02 || perspectiveOverlayOpenRef.current
            ? perspectiveFocusRef.current
            : null,
        aiWorld: !stableWhyFtoPlanet
          && currentWhyFtoActive && currentWhyFtoState === "ai-human"
          && whyFtoPerspectiveRef.current === "ai" ? 1 : 0,
        humanWorld: !stableWhyFtoPlanet
          && currentWhyFtoActive && currentWhyFtoState === "ai-human"
          && whyFtoPerspectiveRef.current === "human" ? 1 : 0,
        contact: contactPresence,
      };
      const speedPulseProgress = directAiMotion.presence > scrollAiMotion.presence
        ? directAiMotion.pulseProgress
        : scrollAiMotion.pulseProgress;
      const speedPulseOpacity = reducedMotion
        ? 0
        : Math.sin(speedPulseProgress * Math.PI) * visibleAiSpeedPresence;
      const workflowTiming = firstChapterTiming;
      const mobileWorkflowPresence = 0;
      const planetSide = getPlanetSide(
        p,
        chapterTimings,
        heroTiming.dockStart,
        firstChapterTiming.miniatureEnd,
      );
      const planetDistance = mobile ? 0 : Math.min(window.innerWidth * 0.285, 380);
      const convergenceEnter = smoothstep(phase(
        p,
        intelligenceBridge.start - 0.006,
        intelligenceBridge.emergeEnd,
      ));
      const convergenceExit = smoothstep(phase(
        p,
        intelligenceBridge.holdEnd,
        intelligenceBridge.exitEnd,
      ));
      const convergencePresence = HAS_VISIBLE_CONVERGENCE_TREATMENT
        ? convergenceEnter * (1 - convergenceExit)
        : 0;
      const restingPlanetX = planetDistance * planetSide * dock;
      const centeredPlanetX = mobile ? 0 : -window.innerWidth * 0.02;
      const chapterPlanetX = mix(restingPlanetX, centeredPlanetX, convergencePresence);
      const heroInitialY = reducedMotion
        ? window.innerHeight * (mobile ? 0.16 : desktop ? 0.2 : 0.18)
        : window.innerHeight * (mobile ? 0.22 : desktop ? 0.46 : 0.24);
      const heroRevealedY = window.innerHeight * (mobile ? 0.06 : 0.025);
      const heroPlanetY = mix(heroInitialY, heroRevealedY, heroPullback);
      const dockedPlanetY = mobile
        ? -window.innerHeight * 0.235 * dock
        : window.innerHeight * 0.025 * dock;
      const restingPlanetY = mix(heroPlanetY, dockedPlanetY, dock);
      const centeredPlanetY = mobile ? restingPlanetY : -window.innerHeight * 0.01;
      const chapterPlanetYBase = mix(restingPlanetY, centeredPlanetY, convergencePresence);
      const chapterPlanetY = mobile
        ? mix(chapterPlanetYBase, window.innerHeight * 0.015, aiSpeedPresence)
        : chapterPlanetYBase;
      const heroInitialScale = reducedMotion
        ? mobile ? 0.95 : desktop ? 1.34 : 1.05
        : mobile ? 1.0 : desktop ? 2.25 : 1.12;
      const heroRevealedScale = mobile ? 0.95 : desktop ? 1.02 : 1.0;
      const heroPlanetScale = mix(heroInitialScale, heroRevealedScale, heroPullback);
      const dockedPlanetScale = mobile ? 0.62 : 0.77;
      const restingPlanetScale = mix(heroPlanetScale, dockedPlanetScale, dock);
      const convergencePlanetScale = mobile ? 0.8 : 1;
      const chapterPlanetScale = mix(restingPlanetScale, convergencePlanetScale, convergencePresence);
      const perspectivePlanetScale = mix(
        mobile ? 0.7 : 0.86,
        mobile ? 0.47 : 0.59,
        perspectiveElements,
      );
      const perspectivePlanetY = mobile
        ? window.innerHeight * (window.innerHeight < 730 ? 0.52 : 0.55)
        : window.innerHeight * 0.075;
      const perspectivePlanetX = mix(
        chapterPlanetX,
        mobile ? 0 : -window.innerWidth * 0.02,
        perspectivePresence,
      );
      const perspectiveAdjustedPlanetY = mix(chapterPlanetY, perspectivePlanetY, perspectivePresence);
      const perspectiveAdjustedPlanetScale = mix(
        chapterPlanetScale,
        perspectivePlanetScale,
        perspectivePresence,
      );
      const planetX = perspectivePlanetX;
      const planetY = perspectiveAdjustedPlanetY;
      const planetScale = perspectiveAdjustedPlanetScale;
      const contactPlanetX = mobile ? 0 : -window.innerWidth * 0.36;
      const contactPlanetY = mobile
        ? -window.innerHeight * 0.21
        : window.innerHeight * 0.31;
      const contactPlanetScale = mobile ? 0.88 : 1.34;
      const decisionPlanetX = mix(planetX, contactPlanetX, contactPresence);
      const decisionPlanetY = mix(
        planetY,
        contactPlanetY,
        contactPresence * mix(0.72, 1, contactSettle),
      );
      const decisionPlanetScale = mix(
        planetScale,
        contactPlanetScale,
        contactPresence,
      );
      const contactPlanetOpacity = mobile ? 0.28 : mix(0.66, 0.48, contactSettle);
      let finalPlanetX = decisionPlanetX;
      let finalPlanetY = decisionPlanetY;
      let finalPlanetScale = decisionPlanetScale;
      if (currentWhyFtoActive && currentWhyFtoState === "standard" && mobile) {
        finalPlanetX = 0;
        finalPlanetY = 0;
        finalPlanetScale = 0.62;
      } else if (currentWhyFtoActive && currentWhyFtoState === "ai-human") {
        const perspective = whyFtoPerspectiveRef.current;
        const humanPlanetOffset = Math.max(0, Math.min(
          window.innerWidth * 0.235,
          window.innerWidth * 0.48 - planet.offsetWidth * 0.39 - 24,
        ));
        finalPlanetX = mobile
          ? 0
          : desktop && perspective === "human"
            ? -humanPlanetOffset
            : -window.innerWidth * 0.01;
        finalPlanetY = mobile
          ? window.innerHeight * (perspective === "neutral" ? 0.2 : 0.135)
          : window.innerHeight * 0.055;
        finalPlanetScale = mobile
          ? perspective === "neutral" ? 0.52 : 0.58
          : desktop && perspective === "human" ? 0.78 : 0.7;
      } else if (currentWhyFtoActive && currentWhyFtoState === "outcomes") {
        const outcomesPlanetOffset = Math.max(0, Math.min(
          window.innerWidth * 0.24,
          window.innerWidth * 0.48 - planet.offsetWidth * 0.37 - 24,
        ));
        finalPlanetX = mobile ? 0 : outcomesPlanetOffset;
        finalPlanetY = mobile ? window.innerHeight * 0.18 : window.innerHeight * 0.045;
        finalPlanetScale = mobile ? 0.56 : 0.74;
      }
      if (desktop && industryPresence > 0) {
        const industryPlanetX = Math.min(window.innerWidth * 0.245, 410);
        const industryPlanetY = -window.innerHeight * 0.205;
        finalPlanetX = mix(finalPlanetX, industryPlanetX, industryPresence);
        finalPlanetY = mix(finalPlanetY, industryPlanetY, industryPresence);
      }
      if (desktop && aboutPresence > 0) {
        const aboutPlanetX = -window.innerWidth * 0.28;
        const aboutPlanetY = window.innerHeight * 0.6;
        const aboutPlanetScale = 1.18;
        finalPlanetX = mix(finalPlanetX, aboutPlanetX, aboutPresence);
        finalPlanetY = mix(finalPlanetY, aboutPlanetY, aboutPresence);
        finalPlanetScale = mix(finalPlanetScale, aboutPlanetScale, aboutPresence);
      }
      planet.style.transform = `translate3d(${finalPlanetX}px, ${finalPlanetY}px, 0) scale(${finalPlanetScale})`;
      const aboutPlanetOpacity = desktop ? mix(1, 0.24, aboutPresence) : 1;
      const persistentPlanetOpacity = mix(1, contactPlanetOpacity, contactPresence)
        * (1 - standardPresence)
        * aboutPlanetOpacity
        * mix(1, 0.24, mobileWorkflowPresence);
      planet.style.opacity = `${persistentPlanetOpacity}`;
      hero.style.pointerEvents = p < heroTiming.copyExitStart ? "auto" : "none";
      hero.toggleAttribute("inert", p > heroTiming.copyExitEnd);

      let currentChapter = -1;
      chapters.forEach((chapter, index) => {
        const beacon = beaconRefs.current[index];
        const panel = panelRefs.current[index];
        if (!beacon || !panel) return;
        const timing = chapterTimings[index];
        if (desktop && index === OUTCOMES_CHAPTER_INDEX) {
          renderDesktopFirstChapterHandoff(p, timing, panel);
        } else {
          renderComposition({ progress: p, timing, source: beacon, target: panel, mobile });
        }
        const activationIn = smoothstep(phase(p, timing.start - 0.011, timing.start + 0.005));
        const activationOut = smoothstep(phase(p, timing.exitStart, timing.exitEnd));
        const convergenceActivation = HAS_VISIBLE_CONVERGENCE_TREATMENT && (
          index === AI_CHAPTER_INDEX
          || index === HUMAN_CHAPTER_INDEX
        )
          ? smoothstep(phase(p, intelligenceBridge.start - 0.006, intelligenceBridge.emergeEnd))
            * (1 - smoothstep(phase(p, intelligenceBridge.holdEnd, intelligenceBridge.exitEnd)))
          : 0;
        const activation = Math.max(activationIn * (1 - activationOut), convergenceActivation);
        beacon.style.setProperty("--activation", `${activation}`);
        beacon.dataset.active = activation > 0.08 ? "true" : "false";
        if (index === PERSPECTIVES_CHAPTER_INDEX) {
          const signalPanelReady = panel.dataset.active === "true";
          setBrandSignalReady((current) => current === signalPanelReady ? current : signalPanelReady);
        }
        if (index === OUTCOMES_CHAPTER_INDEX) {
          const outcomePanelReady = panel.dataset.active === "true";
          setOutcomeReady((current) => current === outcomePanelReady ? current : outcomePanelReady);
        }
        if (p >= timing.start && p < timing.exitEnd) currentChapter = index;
      });

      const signalSystemBridge = signalSystemBridgeRef.current;
      const signalSystemSource = beaconRefs.current[0];
      const signalSystemTarget = panelRefs.current[0];
      if (
        legacySignalSystemEnabled
        && signalSystemBridge
        && signalSystemSource
        && signalSystemTarget
      ) {
        renderSignalSystemBridge({
          bridge: signalSystemBridge,
          stage,
          source: signalSystemSource,
          target: signalSystemTarget,
          mobile,
          travel: signalSystemTravel,
          rail: signalSystemRail,
        });
      }

      const brandSignalField = brandSignalFieldRef.current;
      if (brandSignalField) {
        renderBrandSignalField({
          field: brandSignalField,
          stage,
          planet,
          mobile,
        });
      }

      const convergenceVisible = HAS_VISIBLE_CONVERGENCE_TREATMENT
        && p >= intelligenceBridge.start - 0.004
        && p < intelligenceBridge.exitEnd;
      const aiBeacon = beaconRefs.current[AI_CHAPTER_INDEX];
      const humanBeacon = beaconRefs.current[HUMAN_CHAPTER_INDEX];
      const convergenceAi = convergenceAiRef.current;
      const convergenceHuman = convergenceHumanRef.current;
      const convergenceUnified = convergenceUnifiedRef.current;
      if (
        HAS_VISIBLE_CONVERGENCE_TREATMENT
        && aiBeacon
        && humanBeacon
        && convergenceAi
        && convergenceHuman
        && convergenceUnified
      ) {
        renderIntelligenceConvergence({
          progress: p,
          stage,
          planet,
          aiSource: aiBeacon,
          humanSource: humanBeacon,
          aiPanel: convergenceAi,
          humanPanel: convergenceHuman,
          unifiedPanel: convergenceUnified,
          mobile,
          reducedMotion,
        });
      }
      if (convergenceVisible) currentChapter = -1;

      const workflowProgress = phase(p, workflowTiming.end - 0.003, workflowTiming.exitStart);
      const nextWorkflowStage = Math.min(5, Math.floor(workflowProgress * 6));
      setWorkflowStage((current) => current === nextWorkflowStage ? current : nextWorkflowStage);

      stage.style.setProperty("--hero-opacity", `${1 - heroCopyExit}`);
      stage.style.setProperty("--hero-pullback", `${heroPullback}`);
      stage.style.setProperty("--hero-manifesto", `${heroManifestoPresence}`);
      stage.style.setProperty("--hero-manifesto-exit", `${heroManifestoExit}`);
      stage.style.setProperty("--hero-signal", `${heroSignal}`);
      stage.style.setProperty("--why-fto-editorial-presence", `${whyFtoEditorialPresence}`);
      const whyFtoShell = whyFtoShellRef.current;
      if (whyFtoShell) {
        if (desktop) {
          whyFtoShell.style.opacity = `${whyFtoEditorialPresence}`;
          whyFtoShell.style.transition = "none";
        } else {
          whyFtoShell.style.removeProperty("opacity");
          whyFtoShell.style.removeProperty("transition");
        }
      }
      stage.style.setProperty("--dock", `${dock}`);
      stage.style.setProperty("--planet-side", `${planetSide}`);
      stage.style.setProperty("--planet-speed", `${planetSpeed}`);
      stage.style.setProperty("--fallback-globe-duration", `${54 / planetSpeed}s`);
      stage.style.setProperty("--fallback-orbit-a-duration", `${10.8 / fallbackOrbitSpeed}s`);
      stage.style.setProperty("--fallback-orbit-b-duration", `${12 / fallbackOrbitSpeed}s`);
      stage.style.setProperty("--fallback-orbit-c-duration", `${9.8 / fallbackOrbitSpeed}s`);
      stage.style.setProperty("--fallback-orbit-d-duration", `${11.4 / fallbackOrbitSpeed}s`);
      stage.style.setProperty("--speed-field-duration", `${mix(5.8, 0.92, visibleAiSpeedPresence)}s`);
      stage.style.setProperty("--speed-effect", `${visibleAiSpeedPresence}`);
      stage.style.setProperty("--speed-pulse", `${Math.max(0, speedPulseOpacity)}`);
      stage.style.setProperty("--speed-pulse-left", `${mix(12, 88, speedPulseProgress)}%`);
      stage.style.setProperty("--signal-system-bridge-presence", `${signalSystemBridgePresence}`);
      stage.style.setProperty("--signal-system-travel", `${signalSystemTravel}`);
      stage.style.setProperty("--signal-system-rail", `${signalSystemRail}`);
      stage.style.setProperty("--signal-system-kicker", `${signalSystemKicker}`);
      stage.style.setProperty("--signal-system-headline", `${signalSystemHeadline}`);
      stage.style.setProperty("--signal-system-body", `${signalSystemBody}`);
      stage.style.setProperty("--signal-system-principles", `${signalSystemPrinciples}`);
      stage.style.setProperty("--signal-system-precision", `${signalSystemPrecision}`);
      stage.style.setProperty("--signal-system-judgment", `${signalSystemJudgment}`);
      stage.style.setProperty("--signal-system-trust", `${signalSystemTrust}`);
      stage.style.setProperty(
        "--signal-system-node-rgb",
        `${Math.round(mix(111, 0, signalSystemColor))},${Math.round(mix(207, 159, signalSystemColor))},${Math.round(mix(255, 102, signalSystemColor))}`,
      );
      stage.style.setProperty("--brand-signal-presence", `${brandSignalSystemPresence}`);
      stage.style.setProperty("--judgment-presence", `${visibleHumanJudgmentPresence}`);
      stage.style.setProperty("--outcome-presence", `${outcomePresence}`);
      stage.style.setProperty("--industry-presence", `${industryPresence}`);
      stage.style.setProperty("--standard-presence", `${standardPresence}`);
      stage.style.setProperty("--about-presence", `${aboutPresence}`);
      stage.style.setProperty("--perspective-presence", `${perspectivePresence}`);
      stage.style.setProperty("--perspective-arrival", `${perspectiveArrival}`);
      stage.style.setProperty("--perspective-elements", `${perspectiveElements}`);
      const chapterAccent = currentChapter >= 0 ? chapters[currentChapter] : chapters[0];
      const accent = convergenceVisible
        ? { color: "#d2e3f3", rgb: "210,227,243" }
        : desktop && currentChapter === PERSPECTIVES_CHAPTER_INDEX
          ? { color: "#2278ac", rgb: "34,120,172" }
          : chapterAccent;
      stage.style.setProperty("--active-accent", accent.color);
      stage.style.setProperty("--active-rgb", accent.rgb);
      stage.dataset.planetSide = planetSide < -0.15 ? "left" : planetSide > 0.15 ? "right" : "center";
      stage.dataset.convergence = convergenceVisible ? "active" : "idle";
      stage.dataset.aiSpeed = visibleAiSpeedPresence > 0.02 ? "active" : "idle";
      stage.dataset.aiCapability = visibleAiSpeedPresence > 0.02
        ? aiCapabilityFocusRef.current ?? "none"
        : "none";
      stage.dataset.signalSystem = !legacySignalSystemEnabled
        ? "idle"
        : p < activeSignalSystemTransition.signalStart
        || p >= signalTiming.exitEnd
        ? "idle"
        : reducedMotion
          ? brandSignalIn > 0.02 ? "complete" : "idle"
          : p < activeSignalSystemTransition.railStart
            ? "travel"
            : p < activeSignalSystemTransition.principlesStart
              ? "rail"
              : p < activeSignalSystemTransition.complete
                ? "reveal"
                : "complete";
      stage.dataset.brandSignal = brandSignalSystemPresence > 0.08 ? "active" : "idle";
      stage.dataset.humanJudgment = visibleHumanJudgmentPresence > 0.02 ? "active" : "idle";
      stage.dataset.outcomeReady = outcomePresence > 0.08 ? "true" : "false";
      stage.dataset.outcomeFocus = outcomePresence > 0.02 ? outcomeFocusRef.current : "none";
      stage.dataset.industryReady = industryPresence > 0.08 ? "true" : "false";
      stage.dataset.industryFocus = industryPresence > 0.02 ? industryFocusRef.current : "none";
      stage.dataset.industryPreview = industryPresence > 0.02 && industryPreviewRef.current ? "true" : "false";
      stage.dataset.standardReady = standardPresence > 0.08 ? "true" : "false";
      stage.dataset.aboutReady = aboutPresence > 0.08 ? "true" : "false";
      stage.dataset.perspectiveReady = perspectivePresence > 0.08 ? "true" : "false";
      stage.dataset.whyFtoActive = currentWhyFtoVisible ? "true" : "false";
      stage.dataset.whyFtoState = currentWhyFtoState;
      stage.dataset.whyFtoPerspective = whyFtoPerspectiveRef.current;
      stage.dataset.liveWorkflowActive = currentChapter === WORKFLOW_CHAPTER_INDEX ? "true" : "false";
      stage.dataset.motionPreference = motionPreference;
      stage.dataset.motionMode = effectiveMotionMode;
      stage.dataset.heroPhase = p < heroTiming.pullbackEnd
        ? "horizon"
        : p < heroTiming.signalStart
          ? "world"
          : p < firstChapterTiming.start
            ? "signal"
            : "chapters";
      if (motionDebugRef.current) {
        motionDebugRef.current.dataset.visible = motionDebugEnabled ? "true" : "false";
        motionDebugRef.current.textContent = visibleAiSpeedPresence > 0.02
          ? `AI Precision · ${planetSpeed.toFixed(2)}× · ${effectiveMotionMode === "full" ? "Full" : "Reduced"}${motionPreference === "auto" ? " (Auto)" : " (Forced)"}`
          : `Base motion · 1.00× · ${effectiveMotionMode === "full" ? "Full" : "Reduced"}${motionPreference === "auto" ? " (Auto)" : " (Forced)"}`;
      }

      setActiveChapter((current) => current === currentChapter ? current : currentChapter);
      setConvergenceActive((current) => current === convergenceVisible ? current : convergenceVisible);
      setWhyFtoActive((current) => current === currentWhyFtoVisible ? current : currentWhyFtoVisible);
      if (
        !offerModalOpenRef.current
        && currentChapter !== PERSPECTIVES_CHAPTER_INDEX
        && whyFtoPerspectiveRef.current !== "neutral"
      ) {
        whyFtoPerspectiveRef.current = "neutral";
        setWhyFtoPerspective("neutral");
      }
      if (whyFtoStateRef.current !== currentWhyFtoState) {
        whyFtoStateRef.current = currentWhyFtoState;
        setWhyFtoState(currentWhyFtoState);
      }
      setProgress((current) => Math.abs(current - p) > 0.002 || p === 0 || p === 1 ? p : current);
      if (aiDirectMotionStartRef.current !== null) {
        frame = window.requestAnimationFrame(render);
      }
    };

    const requestRender = () => {
      if (!frame) frame = window.requestAnimationFrame(render);
    };

    aiDirectRenderRequestRef.current = () => {
      if (frame) window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(render);
    };
    render();
    window.addEventListener("scroll", requestRender, { passive: true });
    window.addEventListener("resize", requestRender, { passive: true });
    systemMotionPreference.addEventListener("change", requestRender);
    return () => {
      window.removeEventListener("scroll", requestRender);
      window.removeEventListener("resize", requestRender);
      systemMotionPreference.removeEventListener("change", requestRender);
      aiDirectRenderRequestRef.current = null;
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);

  useEffect(() => {
    if (!whyFtoActive || whyFtoState !== "ai-human") return;
    const automaticPerspective: WhyFtoPerspective | null = activeChapter === PERSPECTIVES_CHAPTER_INDEX
      ? "neutral"
      : activeChapter === AI_CHAPTER_INDEX
        ? "ai"
        : activeChapter === HUMAN_CHAPTER_INDEX
          ? "human"
          : null;
    if (!automaticPerspective || whyFtoPerspectiveRef.current === automaticPerspective) return;
    whyFtoPerspectiveRef.current = automaticPerspective;
    planetMotionRef.current = {
      ...planetMotionRef.current,
      aiWorld: automaticPerspective === "ai" ? 1 : 0,
      humanWorld: automaticPerspective === "human" ? 1 : 0,
    };
    setWhyFtoPerspective(automaticPerspective);
    window.requestAnimationFrame(() => window.dispatchEvent(new Event("resize")));
  }, [activeChapter, whyFtoActive, whyFtoState]);

  useEffect(() => {
    const applyAutomaticSignal = (signal: BrandSignal | null) => {
      brandSignalFocusRef.current = signal;
      planetMotionRef.current = {
        ...planetMotionRef.current,
        brandSignalFocus: signal,
      };
      setBrandSignalFocus(signal);
    };

    if (!brandSignalReady) {
      brandSignalInteractionRef.current = false;
      applyAutomaticSignal(null);
      return;
    }

    brandSignalInteractionRef.current = false;
    if (planetMotionRef.current.mode === "reduced") {
      applyAutomaticSignal(null);
      return;
    }
    const sequence: Array<[BrandSignal | null, number]> = [
      ["precision", 90],
      ["judgment", 180],
      ["trust", 270],
      [null, 540],
    ];
    const timers = sequence.map(([signal, delay]) => window.setTimeout(() => {
      if (!brandSignalInteractionRef.current) applyAutomaticSignal(signal);
    }, delay));
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [brandSignalReady]);

  useEffect(() => () => {
    if (brandSignalTouchTimerRef.current !== null) {
      window.clearTimeout(brandSignalTouchTimerRef.current);
    }
  }, []);

  useEffect(() => () => {
    if (ftoAiTransitionFrameRef.current !== null) {
      window.cancelAnimationFrame(ftoAiTransitionFrameRef.current);
    }
    if (ftoAiTransitionTimerRef.current !== null) {
      window.clearTimeout(ftoAiTransitionTimerRef.current);
    }
    if (ftoPeopleTransitionFrameRef.current !== null) {
      window.cancelAnimationFrame(ftoPeopleTransitionFrameRef.current);
    }
    if (ftoPeopleTransitionTimerRef.current !== null) {
      window.clearTimeout(ftoPeopleTransitionTimerRef.current);
    }
    if (solutionTransitionFrameRef.current !== null) {
      window.cancelAnimationFrame(solutionTransitionFrameRef.current);
    }
    if (solutionTransitionTimerRef.current !== null) {
      window.clearTimeout(solutionTransitionTimerRef.current);
    }
    if (industryTransitionFrameRef.current !== null) {
      window.cancelAnimationFrame(industryTransitionFrameRef.current);
    }
    if (industryTransitionTimerRef.current !== null) {
      window.clearTimeout(industryTransitionTimerRef.current);
    }
  }, []);

  const jumpToChapter = (index: number, focusDestination = false) => {
    const track = trackRef.current;
    if (!track) return;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
      || planetMotionRef.current.mode === "reduced";
    const mobileFlow = window.matchMedia("(max-width: 760px)").matches;

    if (mobileFlow) {
      const destination = index === PERSPECTIVES_CHAPTER_INDEX
        ? whyFtoShellRef.current
        : panelRefs.current[index];
      if (!destination) return;
      const destinationTop = Math.max(
        0,
        destination.getBoundingClientRect().top + window.scrollY - 70,
      );

      setActiveChapter(index);
      setWhyFtoActive(index === PERSPECTIVES_CHAPTER_INDEX);
      window.scrollTo({
        top: destinationTop,
        behavior: reducedMotion ? "auto" : "smooth",
      });

      if (!focusDestination) return;
      const focusChapter = () => destination.focus({ preventScroll: true });
      if (reducedMotion) {
        window.requestAnimationFrame(focusChapter);
        return;
      }

      const focusStart = window.performance.now();
      const focusWhenSettled = () => {
        if (Math.abs(window.scrollY - destinationTop) <= 4) {
          focusChapter();
          return;
        }
        if (window.performance.now() - focusStart < 5000) {
          window.requestAnimationFrame(focusWhenSettled);
        }
      };
      window.requestAnimationFrame(focusWhenSettled);
      return;
    }

    const distance = Math.max(1, track.offsetHeight - window.innerHeight);
    const trackTop = track.getBoundingClientRect().top + window.scrollY;
    const destinationTiming = window.innerWidth > 1180
      ? desktopTimings[index]
      : timings[index];
    const destinationTop = trackTop + distance * (destinationTiming.end + 0.002);
    window.scrollTo({
      top: destinationTop,
      behavior: reducedMotion ? "auto" : "smooth",
    });

    if (!focusDestination) return;
    const destination = index === PERSPECTIVES_CHAPTER_INDEX
      ? document.querySelector<HTMLElement>(".why-fto-shell")
      : panelRefs.current[index];
    if (!destination) return;
    const focusChapter = () => destination.focus({ preventScroll: true });

    if (reducedMotion) {
      window.requestAnimationFrame(focusChapter);
      return;
    }

    const focusStart = window.performance.now();
    const focusWhenSettled = () => {
      if (Math.abs(window.scrollY - destinationTop) <= 4) {
        focusChapter();
        return;
      }
      if (window.performance.now() - focusStart < 4000) {
        window.requestAnimationFrame(focusWhenSettled);
      }
    };
    window.requestAnimationFrame(focusWhenSettled);
  };

  const navigateTo = (key: string, index?: number, focusDestination = false) => {
    setMenuOpen(false);
    window.history.pushState(null, "", `#${key}`);
    if (typeof index === "number") {
      jumpToChapter(index, focusDestination);
      return;
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const openOfferModal = (activeOfferId: OfferId | null, trigger: HTMLElement) => {
    offerTriggerRef.current = trigger;
    offerModalOpenRef.current = true;
    setOfferModalState({ activeOfferId });
  };

  const startOfferConversation = (destination: "#contact") => {
    if (destination !== "#contact") return;
    navigateTo(chapters[CONTACT_CHAPTER_INDEX].key, CONTACT_CHAPTER_INDEX, true);
  };

  const dismissOfferModal = (restoreFocus: boolean) => {
    setOfferModalState(null);
    if (!restoreFocus) {
      offerModalOpenRef.current = false;
      offerTriggerRef.current = null;
    }
  };

  const renderNextChapterNavigation = (
    sourceChapter: string,
    placement: NextChapterNavigationProps["placement"],
  ) => {
    const destination = NEXT_CHAPTER_DESTINATIONS[sourceChapter];
    if (!destination) return null;

    return (
      <NextChapterNavigation
        destinationLabel={destination.label}
        destinationChapter={destination.chapter}
        destinationIndex={destination.index}
        placement={placement}
        onNavigate={(chapter, index) => navigateTo(chapter, index, true)}
      />
    );
  };

  const navigateToWhyFtoState = (state: WhyFtoState) => {
    const tab = whyFtoTabs.find((item) => item.state === state);
    if (!tab) return;
    whyFtoStateRef.current = tab.state;
    setWhyFtoState(tab.state);
    selectWhyFtoPerspective("neutral");
    navigateTo(chapters[tab.chapterIndex].key, tab.chapterIndex);
  };

  useEffect(() => {
    if (!menuOpen || offerModalOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const menu = menuRef.current;
    const menuFocusable = Array.from(menu?.querySelectorAll<HTMLElement>(
      "button:not([disabled]), a[href], [tabindex]:not([tabindex='-1'])",
    ) ?? []);
    const focusable = menuButtonRef.current
      ? [...menuFocusable, menuButtonRef.current]
      : menuFocusable;
    focusable[0]?.focus();

    const handleKeydown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
        menuButtonRef.current?.focus();
        return;
      }
      if (event.key !== "Tab" || !focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    window.addEventListener("keydown", handleKeydown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeydown);
    };
  }, [menuOpen, offerModalOpen]);

  useEffect(() => {
    if (!perspectiveOverlayOpen) return;
    const previousOverlayState = document.body.getAttribute("data-perspective-overlay-open");
    document.body.setAttribute("data-perspective-overlay-open", "true");
    window.requestAnimationFrame(() => perspectiveCloseButtonRef.current?.focus());

    const handleKeydown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        perspectiveCloseButtonRef.current?.click();
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = Array.from(
        perspectiveOverlayRef.current?.querySelectorAll<HTMLElement>(
          "button:not([disabled]), a[href], [tabindex]:not([tabindex='-1'])",
        ) ?? [],
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    window.addEventListener("keydown", handleKeydown);
    return () => {
      if (previousOverlayState === null) {
        document.body.removeAttribute("data-perspective-overlay-open");
      } else {
        document.body.setAttribute("data-perspective-overlay-open", previousOverlayState);
      }
      window.removeEventListener("keydown", handleKeydown);
      if (perspectiveRestoreFocusRef.current) {
        perspectiveTriggerRef.current?.focus();
      }
    };
  }, [perspectiveOverlayOpen]);

  useEffect(() => () => {
    if (perspectiveCloseTimerRef.current) {
      window.clearTimeout(perspectiveCloseTimerRef.current);
    }
  }, []);

  useEffect(() => {
    const previousScrollRestoration = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";
    const navigateFromHistory = () => {
      const requestedTarget = window.location.hash.slice(1);
      const target = requestedTarget === "unified"
        ? "perspectives"
        : requestedTarget === "signal"
          ? "outcomes"
          : requestedTarget;
      const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
        || planetMotionRef.current.mode === "reduced";
      if (!target || target === "hero" || target === "manifesto") {
        window.scrollTo({ top: 0, behavior: reducedMotion ? "auto" : "smooth" });
        return;
      }
      const index = chapters.findIndex((chapter) => chapter.key === target);
      if (index < 0) return;
      jumpToChapter(index);
    };

    const timer = window.setTimeout(() => {
      window.requestAnimationFrame(navigateFromHistory);
    }, 360);
    window.addEventListener("popstate", navigateFromHistory);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("popstate", navigateFromHistory);
      window.history.scrollRestoration = previousScrollRestoration;
    };
  }, []);

  const renderMobileOutcomes = () => (
    <section className="mobile-core-content mobile-outcomes" aria-label="Business outcomes">
      <ol className="mobile-disclosure-list">
        {brandOutcomes.map((outcome) => {
          const selected = selectedOutcome === outcome.key;
          const panelId = `mobile-outcome-detail-${outcome.key}`;
          return (
            <li
              className="mobile-disclosure-item"
              data-selected={selected ? "true" : "false"}
              style={{
                "--mobile-accent": outcome.desktopColor,
                "--mobile-rgb": outcome.desktopRgb,
              } as CSSProperties}
              key={outcome.key}
            >
              <button
                className="mobile-disclosure-trigger mobile-outcome-trigger"
                type="button"
                aria-expanded={selected}
                aria-controls={panelId}
                onClick={() => selectOutcome(outcome.key)}
              >
                <span className="mobile-disclosure-index">{outcome.number}</span>
                <strong>{outcome.title}</strong>
                <i aria-hidden="true">{selected ? "−" : "+"}</i>
              </button>
              {selected && (
                <div className="mobile-disclosure-panel" id={panelId} role="region">
                  <p><OutcomeProofCopy copy={outcome.microcopy} /></p>
                </div>
              )}
            </li>
          );
        })}
      </ol>
    </section>
  );

  const renderMobileServices = () => (
    <section className="mobile-core-content mobile-services" aria-label="FTO services and workflows">
      <ol className="mobile-disclosure-list">
        {solutions.map((solution, index) => {
          const selected = activeSolution === index;
          const panelId = `mobile-service-detail-${index + 1}`;
          return (
            <li
              className="mobile-disclosure-item"
              data-selected={selected ? "true" : "false"}
              style={{ "--mobile-accent": "#6fcfff", "--mobile-rgb": "111,207,255" } as CSSProperties}
              key={solution.title}
            >
              <button
                className="mobile-disclosure-trigger"
                type="button"
                aria-expanded={selected}
                aria-controls={panelId}
                onClick={() => selectSolution(index, true)}
              >
                <span className="mobile-disclosure-index">0{index + 1}</span>
                <strong>{solution.title}</strong>
                <i aria-hidden="true">{selected ? "−" : "+"}</i>
              </button>
              {selected && (
                <article className="mobile-disclosure-panel mobile-service-detail" id={panelId}>
                  <span className="mobile-detail-eyebrow">{solution.eyebrow}</span>
                  <p>{solution.description}</p>
                  <div className="mobile-workflow-list">
                    <span>COMMON WORKFLOWS</span>
                    <ol>
                      {solution.workflows.map((workflow, workflowIndex) => (
                        <li key={workflow}><span>0{workflowIndex + 1}</span><strong>{workflow}</strong></li>
                      ))}
                    </ol>
                  </div>
                  {index === 0 && (
                    <button
                      className="mobile-context-offer"
                      type="button"
                      aria-haspopup="dialog"
                      aria-expanded={offerModalOpen && offerModalState?.activeOfferId === "low-volume-coverage"}
                      aria-controls="strategic-offers-dialog"
                      onClick={(event) => openOfferModal("low-volume-coverage", event.currentTarget)}
                    >
                      <span>STRATEGIC OFFER</span>
                      <strong><b>24/7/365</b> Low-volume line coverage</strong>
                      <i aria-hidden="true">→</i>
                    </button>
                  )}
                  <a
                    className="mobile-primary-cta"
                    href="#contact"
                    onClick={(event) => {
                      event.preventDefault();
                      navigateTo("contact", CONTACT_CHAPTER_INDEX, true);
                    }}
                  >
                    DESIGN YOUR DELIVERY MODEL <i aria-hidden="true">→</i>
                  </a>
                </article>
              )}
            </li>
          );
        })}
      </ol>
    </section>
  );

  const renderMobileIndustries = () => (
    <section className="mobile-core-content mobile-industries" aria-label="Industry trust standards">
      <ol className="mobile-disclosure-list">
        {industries.map((industry, index) => {
          const selected = selectedIndustry === index;
          const panelId = `mobile-industry-detail-${industry.key}`;
          return (
            <li
              className="mobile-disclosure-item"
              data-selected={selected ? "true" : "false"}
              data-industry={industry.key}
              style={{
                "--mobile-accent": industry.accent,
                "--mobile-rgb": industry.rgb,
              } as CSSProperties}
              key={industry.key}
            >
              <button
                className="mobile-disclosure-trigger"
                type="button"
                aria-expanded={selected}
                aria-controls={panelId}
                onClick={() => selectIndustryStandard(index, true)}
              >
                <span className="mobile-disclosure-index">{industry.number}</span>
                <strong>{industry.title}</strong>
                <i aria-hidden="true">{selected ? "−" : "+"}</i>
              </button>
              {selected && (
                <article className="mobile-disclosure-panel mobile-industry-detail" id={panelId}>
                  <div className="mobile-industry-heading">
                    <div>
                      <span className="mobile-detail-eyebrow">INDUSTRY</span>
                      <h3>{industry.title}</h3>
                    </div>
                    <i className="industry-icon" aria-hidden="true"><IndustryIcon industry={industry.key} /></i>
                  </div>
                  <section>
                    <span className="mobile-detail-eyebrow">TRUST STANDARD</span>
                    <p className="mobile-industry-trust">{industry.trust}</p>
                  </section>
                  <section>
                    <span className="mobile-detail-eyebrow">WHAT CUSTOMERS NEED TO FEEL</span>
                    <p>{industry.customer}</p>
                  </section>
                  <section>
                    <span className="mobile-detail-eyebrow">HOW FTO HELPS</span>
                    <p>{industry.help}</p>
                  </section>
                  <section>
                    <span className="mobile-detail-eyebrow">RELEVANT WORKFLOWS</span>
                    <ul className="mobile-industry-workflows">
                      {industry.workflows.map((workflow) => <li key={workflow}>{workflow}</li>)}
                    </ul>
                  </section>
                  <section>
                    <span className="mobile-detail-eyebrow">WHERE FTO ADDS VALUE</span>
                    <div className="mobile-industry-metrics" role="list" aria-label={`${industry.title} performance metrics`}>
                      {industry.metrics.map((metric) => (
                        <span role="listitem" key={metric.label}><strong>{metric.value}</strong><small>{metric.label}</small></span>
                      ))}
                    </div>
                  </section>
                  {industry.key === "insurance" && (
                    <button
                      className="mobile-context-offer"
                      type="button"
                      aria-haspopup="dialog"
                      aria-expanded={offerModalOpen && offerModalState?.activeOfferId === "licensed-insurance-agents"}
                      aria-controls="strategic-offers-dialog"
                      onClick={(event) => openOfferModal("licensed-insurance-agents", event.currentTarget)}
                    >
                      <span>STRATEGIC OFFER</span>
                      <strong>STATE LICENSED INSURANCE AGENTS</strong>
                      <i aria-hidden="true">→</i>
                    </button>
                  )}
                  <a
                    className="mobile-primary-cta"
                    href="#contact"
                    onClick={(event) => {
                      event.preventDefault();
                      navigateTo("contact", CONTACT_CHAPTER_INDEX, true);
                    }}
                  >
                    {industry.cta} <i aria-hidden="true">→</i>
                  </a>
                </article>
              )}
            </li>
          );
        })}
      </ol>
    </section>
  );

  const renderMobileAiHuman = () => {
    const selectedAiWorkflow = selectedFtoAiWorkflow
      ? ftoAiWorkflows.find(({ key }) => key === selectedFtoAiWorkflow) ?? null
      : null;
    const selectedPeopleWorkflow = selectedFtoPeopleWorkflow
      ? ftoPeopleWorkflows.find(({ key }) => key === selectedFtoPeopleWorkflow) ?? null
      : null;

    return (
      <section className="mobile-core-content mobile-ai-human" aria-labelledby="mobile-ai-human-title">
        <header>
          <span>AI + HUMAN</span>
          <h2 id="mobile-ai-human-title">AI provides precision. People provide judgment.</h2>
          <p>FTO helps you get there.</p>
          <small>The advantage is not choosing one. It is deciding where each belongs.</small>
        </header>
        <div className="mobile-perspective-selector" role="group" aria-label="Select AI precision or human judgment">
          <button
            type="button"
            aria-pressed={whyFtoPerspective === "ai"}
            onClick={() => selectWhyFtoPerspective("ai")}
          >
            <span>01 · PRECISION</span><strong>AI</strong><small>Precision at scale.</small>
          </button>
          <button
            type="button"
            aria-pressed={whyFtoPerspective === "human"}
            onClick={() => selectWhyFtoPerspective("human")}
          >
            <span>02 · JUDGMENT</span><strong>Hu</strong><small>Context when the moment changes.</small>
          </button>
        </div>

        {whyFtoPerspective === "ai" && (
          <article className="mobile-perspective-detail" aria-label="FTO AI workflows">
            <header>
              <span>ARTIFICIAL INTELLIGENCE</span>
              <h3>Precision at scale.</h3>
              <p>FTO AI helps operations move faster, identify patterns earlier, and apply consistent logic across high-volume work.</p>
            </header>
            <ol className="mobile-workflow-disclosures">
              {ftoAiWorkflows.map((workflow, index) => {
                const selected = selectedFtoAiWorkflow === workflow.key;
                return (
                  <li data-selected={selected ? "true" : "false"} key={workflow.key}>
                    <button
                      type="button"
                      aria-expanded={selected}
                      onClick={() => selectFtoAiWorkflow(workflow.key)}
                    >
                      <span>0{index + 1}</span><strong>{workflow.label}</strong><i aria-hidden="true">{selected ? "−" : "+"}</i>
                    </button>
                    {selected && selectedAiWorkflow && <p>{selectedAiWorkflow.description}</p>}
                  </li>
                );
              })}
            </ol>
            <button
              className="mobile-context-offer"
              type="button"
              aria-haspopup="dialog"
              aria-expanded={offerModalOpen && offerModalState?.activeOfferId === "ai-workflow-engineering"}
              aria-controls="strategic-offers-dialog"
              onClick={(event) => openOfferModal("ai-workflow-engineering", event.currentTarget)}
            >
              <span>STRATEGIC OFFER</span>
              <strong>REQUEST FREE CUSTOM AI WORKFLOW ENGINEERING</strong>
              <i aria-hidden="true">→</i>
            </button>
          </article>
        )}

        {whyFtoPerspective === "human" && (
          <article className="mobile-perspective-detail" aria-label="FTO People workflows">
            <header>
              <span>FTO PEOPLE</span>
              <h3>Context when the moment changes.</h3>
              <p>FTO people bring context, empathy, and accountable judgment to complex or high-stakes interactions — protecting trust when rules and automation are not enough.</p>
            </header>
            <ol className="mobile-workflow-disclosures">
              {ftoPeopleWorkflows.map((workflow, index) => {
                const selected = selectedFtoPeopleWorkflow === workflow.key;
                return (
                  <li data-selected={selected ? "true" : "false"} key={workflow.key}>
                    <button
                      type="button"
                      aria-expanded={selected}
                      onClick={() => selectFtoPeopleWorkflow(workflow.key)}
                    >
                      <span>0{index + 1}</span><strong>{workflow.label}</strong><i aria-hidden="true">{selected ? "−" : "+"}</i>
                    </button>
                    {selected && selectedPeopleWorkflow && <p>{selectedPeopleWorkflow.description}</p>}
                  </li>
                );
              })}
            </ol>
            <button
              className="mobile-primary-cta"
              type="button"
              onClick={() => navigateTo("contact", CONTACT_CHAPTER_INDEX, true)}
            >
              DESIGN YOUR HUMAN-LED WORKFLOW <i aria-hidden="true">→</i>
            </button>
          </article>
        )}

        {whyFtoPerspective !== "neutral" && (
          <button className="mobile-return-overview" type="button" onClick={() => selectWhyFtoPerspective("neutral")}>
            <i aria-hidden="true">←</i> Return to AI + Human overview
          </button>
        )}
      </section>
    );
  };

  const renderChapterSystem = (chapter: Chapter) => {
    switch (chapter.key) {
      case "signal":
        return (
          <div className="signal-calibration">
            <div className="signal-calibration-label"><span>The Delivery Principles</span><i /></div>
            <div className="signal-system" role="group" aria-label="Explore Precision, Judgment and Trust">
              {brandSignals.map(({ signal, title, detail, accessibleLabel }) => (
                <button
                  type="button"
                  data-signal={signal}
                  aria-pressed={brandSignalFocus === signal}
                  aria-label={accessibleLabel}
                  ref={(node) => { brandSignalCardRefs.current[signal] = node; }}
                  onPointerEnter={(event) => {
                    if (event.pointerType === "mouse") selectBrandSignal(signal, true);
                  }}
                  onPointerLeave={(event) => {
                    if (
                      event.pointerType === "mouse"
                      && document.activeElement !== event.currentTarget
                    ) {
                      selectBrandSignal(null);
                    }
                  }}
                  onPointerDown={(event) => {
                    if (event.pointerType !== "mouse") selectBrandSignal(signal, true);
                  }}
                  onPointerUp={(event) => {
                    if (event.pointerType !== "mouse") releaseTouchBrandSignal();
                  }}
                  onPointerCancel={(event) => {
                    if (event.pointerType !== "mouse") selectBrandSignal(null);
                  }}
                  onFocus={() => selectBrandSignal(signal, true)}
                  onBlur={() => selectBrandSignal(null)}
                  onClick={(event) => {
                    if (event.detail === 0) selectBrandSignal(signal, true);
                  }}
                  key={signal}
                >
                  <span><i /><strong>{title}</strong></span>
                  <small>{detail}</small>
                </button>
              ))}
            </div>
            <button
              className="why-standard-progress-cta"
              type="button"
              aria-label="Continue to the AI and Human overview."
              onClick={() => navigateToWhyFtoState("ai-human")}
            >
              <span>Continue to AI + Human</span>
              <i aria-hidden="true">→</i>
            </button>
          </div>
        );
      case "ai":
        return (
          <div className="ai-precision-system">
            <button
              className="ai-perspective-cta"
              type="button"
              aria-label="Explore the AI perspective and learn where AI should lead."
              onClick={(event) => openPerspective("ai", event.currentTarget)}
            >
              <span>Explore the AI Perspective</span>
              <i aria-hidden="true">→</i>
            </button>
            <p className="ai-perspective-support">
              <span>See where AI should lead, and where human oversight ensures the right outcome.</span>
            </p>
            <div className="ai-mobile-planet-spacer" aria-hidden="true" />
            <div className="ai-capabilities" role="group" aria-label="Explore the three AI capabilities">
              {aiCapabilities.map(({ key, number, title, detail, accessibleLabel }) => (
                <button
                  className="ai-capability-card"
                  type="button"
                  data-capability={key}
                  aria-label={accessibleLabel}
                  aria-pressed={aiCapabilityFocus === key}
                  onPointerEnter={(event) => {
                    if (event.pointerType === "mouse") selectAiCapability(key);
                  }}
                  onPointerLeave={(event) => {
                    if (
                      event.pointerType === "mouse"
                      && document.activeElement !== event.currentTarget
                    ) {
                      selectAiCapability(null);
                    }
                  }}
                  onPointerDown={(event) => {
                    if (event.pointerType !== "mouse") selectAiCapability(key);
                  }}
                  onFocus={() => selectAiCapability(key)}
                  onBlur={() => selectAiCapability(null)}
                  onClick={() => selectAiCapability(key)}
                  key={key}
                >
                  <header><span>{number}</span><i aria-hidden="true">↗</i></header>
                  <div className="ai-capability-reading">
                    <span className="ai-capability-glyph" aria-hidden="true"><i /><i /><i /><i /><i /><i /><i /><i /><i /></span>
                    <div><strong>{title}</strong><p>{detail}</p></div>
                  </div>
                  <footer><i aria-hidden="true" /><span>AI Signal Activated</span></footer>
                </button>
              ))}
            </div>
          </div>
        );
      case "human":
        return (
          <div className="human-judgment-system">
            <button
              className="human-perspective-cta"
              type="button"
              aria-label="Explore the Human Perspective and open deeper guidance about where human judgment should lead."
              onClick={(event) => openPerspective("human", event.currentTarget)}
            >
              <span>Explore the Human Perspective</span>
              <i aria-hidden="true">→</i>
            </button>
            <p className="human-perspective-support">
              See where human judgment should lead, and how AI can support the moment.
            </p>
            <div className="quality-system quality-human" role="group" aria-label="Explore the three forces of human judgment">
              {humanQualities.map(({ force, number, title, detail, accessibleLabel }) => (
                <button
                  type="button"
                  data-force={force}
                  aria-label={accessibleLabel}
                  aria-pressed={judgmentFocus === force}
                  onPointerEnter={(event) => {
                    if (event.pointerType === "mouse") selectJudgmentForce(force);
                  }}
                  onPointerLeave={(event) => {
                    if (
                      event.pointerType === "mouse"
                      && document.activeElement !== event.currentTarget
                    ) {
                      selectJudgmentForce(null);
                    }
                  }}
                  onPointerDown={(event) => {
                    if (event.pointerType !== "mouse") selectJudgmentForce(force);
                  }}
                  onFocus={() => selectJudgmentForce(force)}
                  onBlur={() => selectJudgmentForce(null)}
                  onClick={() => selectJudgmentForce(force)}
                  key={force}
                >
                  <span>{number}</span>
                  <strong>{title}</strong>
                  <small>{detail}</small>
                </button>
              ))}
            </div>
          </div>
        );
      case "unified":
        return (
          <div className={`delivery-system mode-${deliveryMode}`}>
            <div className="delivery-spectrum"><span>AI</span><i><b /></i><span>HUMAN</span></div>
            <strong>{deliveryMode === "ai" ? "Precision leads" : deliveryMode === "human" ? "Judgment leads" : "Intentional balance"}</strong>
            <small>{deliveryMode === "ai" ? "Speed · Consistency · Availability" : deliveryMode === "human" ? "Empathy · Nuance · Accountability" : "One experience · One standard"}</small>
            <div className="delivery-controls" role="group" aria-label="Explore delivery emphasis">
              <button className={deliveryMode === "ai" ? "active" : ""} onClick={() => setDeliveryMode("ai")}>AI Precision</button>
              <button className={deliveryMode === "unified" ? "active" : ""} onClick={() => setDeliveryMode("unified")}>Unified</button>
              <button className={deliveryMode === "human" ? "active" : ""} onClick={() => setDeliveryMode("human")}>Human Judgment</button>
            </div>
          </div>
        );
      case "workflow":
        return (
          <div className="workflow-system" aria-label="Live six-stage client operating network">
            <div className="workflow-live"><i /><span>FTO FLOW STUDIO</span><strong>LIVE OPERATING NETWORK</strong></div>
            <div className="workflow-network" data-focus={workflowFocus ?? "none"}>
              <svg className="workflow-connections" viewBox="0 0 700 290" role="img" aria-label="A connected workflow from Define through Measure, with AI and human judgment paths and a feedback loop">
                <defs>
                  <filter id="workflow-glow" x="-40%" y="-40%" width="180%" height="180%">
                    <feGaussianBlur stdDeviation="2.6" result="blur" />
                    <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
                  </filter>
                </defs>
                {workflowLinks.map((link) => {
                  const isActive = workflowStage >= link.activeAt;
                  const isRelated = workflowFocus === null ||
                    workflowNodeLinks[workflowFocus].includes(link.id);
                  return (
                    <g className={`workflow-link link-${link.kind} ${isActive ? "is-live" : ""} ${isRelated ? "is-related" : "is-dimmed"}`} key={link.id}>
                      <path className="workflow-path-base" d={link.d} />
                      <path className="workflow-path-flow" d={link.d} filter="url(#workflow-glow)" />
                    </g>
                  );
                })}
              </svg>
              <span className="workflow-ai-route" aria-hidden="true">AI ROUTE</span>
              {workflowNodes.map((node, index) => (
                <button
                  type="button"
                  className={`workflow-node node-${node.kind} ${index <= workflowStage ? "is-live" : ""} ${workflowFocus === index ? "is-focused" : ""}`}
                  style={{ "--node-x": `${node.x}%`, "--node-y": `${node.y}%`, "--node-delay": `${index * 90}ms` } as CSSProperties}
                  aria-label={`${node.id}. ${node.action}: ${node.result}`}
                  aria-pressed={workflowFocus === index}
                  onClick={() => setWorkflowFocus(index)}
                  onFocus={() => setWorkflowFocus(index)}
                  onBlur={() => setWorkflowFocus(null)}
                  onPointerEnter={() => setWorkflowFocus(index)}
                  onPointerLeave={(event) => {
                    if (document.activeElement !== event.currentTarget) setWorkflowFocus(null);
                  }}
                  key={node.id}
                >
                  <span className="workflow-node-index">{node.id}</span>
                  <i className="workflow-node-ring"><b /></i>
                  <span className="workflow-node-copy"><strong>{node.action}</strong><small>{node.result}</small></span>
                </button>
              ))}
            </div>
            <div className="workflow-reading">
              <span>0{workflowStage + 1} / 06</span>
              <strong>{workflowNodes[workflowStage].action} the workflow</strong>
              <small>{workflowStage === 5 ? "Feedback returns to Define" : "Signal moving through the network"}</small>
            </div>
          </div>
        );
      case "outcomes":
        return (
          <>
            {renderMobileOutcomes()}
            <div
              className="outcome-system outcome-system-mobile"
              data-ready={outcomeReady ? "true" : "false"}
              style={{
                "--outcome-accent": activeOutcome.color,
                "--outcome-rgb": activeOutcome.rgb,
                "--desktop-outcome-accent": activeOutcome.desktopColor,
                "--desktop-outcome-rgb": activeOutcome.desktopRgb,
              } as CSSProperties}
            >
              <div className="outcome-tabs" role="tablist" aria-label="Explore FTO brand outcomes">
                {brandOutcomes.map((outcome, index) => (
                  <button
                    type="button"
                    role="tab"
                    id={`outcome-tab-${outcome.key}`}
                    aria-controls="outcome-lens-panel"
                    aria-selected={activeOutcomeKey === outcome.key}
                    aria-label={outcome.accessibleLabel}
                    tabIndex={selectedOutcome === outcome.key ? 0 : -1}
                    data-outcome={outcome.key}
                    data-current={activeOutcomeKey === outcome.key ? "true" : "false"}
                    style={{
                      "--desktop-card-accent": outcome.desktopColor,
                      "--desktop-card-rgb": outcome.desktopRgb,
                    } as CSSProperties}
                    ref={(node) => { outcomeTabRefs.current[outcome.key] = node; }}
                    onClick={() => selectOutcome(outcome.key)}
                    onFocus={() => previewOutcomeLens(outcome.key)}
                    onBlur={() => previewOutcomeLens(null)}
                    onPointerEnter={() => previewOutcomeLens(outcome.key)}
                    onPointerLeave={(event) => {
                      if (document.activeElement !== event.currentTarget) previewOutcomeLens(null);
                    }}
                    onKeyDown={(event) => {
                      if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
                      event.preventDefault();
                      const nextIndex = event.key === "Home"
                        ? 0
                        : event.key === "End"
                          ? brandOutcomes.length - 1
                          : (index + (event.key === "ArrowRight" ? 1 : -1) + brandOutcomes.length) % brandOutcomes.length;
                      const nextOutcome = brandOutcomes[nextIndex].key;
                      outcomeTabRefs.current[nextOutcome]?.focus();
                    }}
                    key={outcome.key}
                  >
                    <span className="outcome-tab-top">
                      <small>{outcome.number}</small>
                      <i className="outcome-glyph" aria-hidden="true"><b /><OutcomeCaseIcon outcome={outcome.key} /></i>
                    </span>
                    <strong>{outcome.title}</strong>
                    <span className="outcome-tab-copy">{outcome.microcopy}</span>
                    <span className="outcome-selected-mark" aria-hidden="true" />
                  </button>
                ))}
              </div>
              <div className="outcome-lens-shell">
                <span className="outcome-lens-label">Client Impact & Testimonials</span>
                <article
                  id="outcome-lens-panel"
                  role="tabpanel"
                  aria-live="polite"
                  aria-labelledby={`outcome-tab-${activeOutcome.key}`}
                  tabIndex={0}
                  className="outcome-lens"
                  key={activeOutcome.key}
                >
                  <header>
                    <i className="outcome-lens-node" aria-hidden="true"><b /></i>
                    <strong>{activeOutcome.title}</strong>
                  </header>
                  <p className="outcome-lens-support">{activeOutcome.microcopy}</p>
                  <div className="outcome-lens-reading">
                    <section>
                      {activeOutcome.caseStudy1Text?.trim() ? (
                        <p className="outcome-case-study">{activeOutcome.caseStudy1Text}</p>
                      ) : null}
                      <small>{activeOutcome.quote1Role}</small>
                      <p className="outcome-expectation">
                        <span className="outcome-quote-default">{activeOutcome.quote1Text}</span>
                        <span className="outcome-quote-desktop">{activeOutcome.desktopQuote1Text ?? activeOutcome.quote1Text}</span>
                      </p>
                    </section>
                    <section>
                      {activeOutcome.caseStudy2Text?.trim() ? (
                        <p className="outcome-case-study">{activeOutcome.caseStudy2Text}</p>
                      ) : null}
                      <small>{activeOutcome.quote2Role}</small>
                      <p className="outcome-expectation">{activeOutcome.quote2Text}</p>
                    </section>
                    <section>
                      {activeOutcome.caseStudy3Text?.trim() ? (
                        <p className="outcome-case-study">{activeOutcome.caseStudy3Text}</p>
                      ) : null}
                      <small>{activeOutcome.quote3Role}</small>
                      <p className="outcome-expectation">{activeOutcome.quote3Text}</p>
                    </section>
                  </div>
                </article>
              </div>
            </div>

            <section
              className="outcome-system outcome-system-desktop"
              data-ready={outcomeReady ? "true" : "false"}
              aria-label="Business outcomes"
            >
              <div className="outcome-tabs outcome-card-grid">
                {brandOutcomes.map((outcome) => (
                  <article
                    className="outcome-card"
                    aria-labelledby={`outcome-card-title-${outcome.key}`}
                    data-outcome={outcome.key}
                    style={{
                      "--card-accent": outcome.desktopColor,
                      "--card-rgb": outcome.desktopRgb,
                    } as CSSProperties}
                    tabIndex={0}
                    onFocus={() => previewOutcomeLens(outcome.key)}
                    onBlur={() => previewOutcomeLens(null)}
                    onPointerEnter={(event) => {
                      if (event.pointerType === "mouse") previewOutcomeLens(outcome.key);
                    }}
                    onPointerLeave={(event) => {
                      if (event.pointerType === "mouse" && document.activeElement !== event.currentTarget) {
                        previewOutcomeLens(null);
                      }
                    }}
                    key={outcome.key}
                  >
                    <span className="outcome-tab-top">
                      <small>{outcome.number}</small>
                      <i className="outcome-glyph" aria-hidden="true"><OutcomeCaseIcon outcome={outcome.key} /></i>
                    </span>
                    <strong id={`outcome-card-title-${outcome.key}`}>{outcome.title}</strong>
                    <span className="outcome-tab-copy"><OutcomeProofCopy copy={outcome.microcopy} /></span>
                  </article>
                ))}
              </div>
            </section>
          </>
        );
      case "solutions":
        return (
          <>
          {renderMobileServices()}
          <div className="selector-system solution-system">
            <div className="selector-list solution-selector" role="tablist" aria-label="FTO services and workflows" aria-orientation="vertical">
              {solutions.map((solution, index) => (
                <button
                  type="button"
                  id={`service-tab-${index + 1}`}
                  role="tab"
                  aria-controls="service-detail-panel"
                  aria-selected={activeSolution === index}
                  tabIndex={activeSolution === index ? 0 : -1}
                  className={activeSolution === index ? "active" : ""}
                  onClick={() => selectSolution(index)}
                  onMouseEnter={() => {
                    if (window.innerWidth <= 1180) selectSolution(index, true);
                  }}
                  onFocus={() => selectSolution(index)}
                  onKeyDown={(event) => {
                    let nextIndex = index;
                    if (event.key === "ArrowDown" || event.key === "ArrowRight") nextIndex = (index + 1) % solutions.length;
                    else if (event.key === "ArrowUp" || event.key === "ArrowLeft") nextIndex = (index - 1 + solutions.length) % solutions.length;
                    else if (event.key === "Home") nextIndex = 0;
                    else if (event.key === "End") nextIndex = solutions.length - 1;
                    else return;
                    event.preventDefault();
                    event.currentTarget.parentElement
                      ?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[nextIndex]
                      ?.focus();
                  }}
                  key={solution.title}
                >
                  <span>0{index + 1}</span><strong>{solution.title}</strong><i aria-hidden="true" />
                </button>
              ))}
            </div>
            <article
              className="selector-focus solution-focus"
              id="service-detail-panel"
              role="tabpanel"
              aria-labelledby={`service-tab-${displayedSolution + 1}`}
              tabIndex={0}
              data-detail-phase={solutionDetailPhase}
              key={solutions[displayedSolution].title}
            >
              <span>{solutions[displayedSolution].eyebrow}</span>
              <strong>{solutions[displayedSolution].title}</strong>
              <p>{solutions[displayedSolution].description}</p>
              {displayedSolution === 0 && (
                <button
                  className="solution-offer"
                  type="button"
                  aria-haspopup="dialog"
                  aria-expanded={offerModalOpen && offerModalState?.activeOfferId === "low-volume-coverage"}
                  aria-controls="strategic-offers-dialog"
                  onClick={(event) => openOfferModal("low-volume-coverage", event.currentTarget)}
                >
                  <span className="solution-offer-label">STRATEGIC OFFER</span>
                  <strong className="solution-offer-copy">
                    <b>24/7/365</b>
                    <span>Low-volume line coverage</span>
                  </strong>
                  <span className="solution-offer-action">
                    VIEW OFFER <i aria-hidden="true">→</i>
                  </span>
                </button>
              )}
              <div className="solution-workflows">
                <span>COMMON WORKFLOWS</span>
                <ol>
                  {solutions[displayedSolution].workflows.map((workflow, index) => (
                    <li key={workflow}><span>0{index + 1}</span><strong>{workflow}</strong></li>
                  ))}
                </ol>
              </div>
              <a
                className="solution-cta"
                href="#contact"
                onClick={(event) => {
                  event.preventDefault();
                  navigateTo("contact", chapters.length - 1);
                }}
              >
                DESIGN YOUR DELIVERY MODEL <i aria-hidden="true">→</i>
              </a>
            </article>
          </div>
          </>
        );
      case "industries":
        return (
          <>
          {renderMobileIndustries()}
          <div
            className="industry-system"
            data-preview={previewIndustry !== null ? "true" : "false"}
            style={{
              "--industry-accent": renderedIndustry.accent,
              "--industry-rgb": renderedIndustry.rgb,
              "--industry-secondary": renderedIndustry.secondary,
            } as CSSProperties}
          >
            <div
              className="industry-list"
              role="tablist"
              aria-label="Industry trust standards"
            >
              {industries.map((industry, index) => (
                <button
                  type="button"
                  role="tab"
                  id={`industry-tab-${industry.key}`}
                  aria-controls="industry-trust-standard"
                  aria-selected={selectedIndustry === index}
                  tabIndex={selectedIndustry === index ? 0 : -1}
                  data-industry={industry.key}
                  data-current={activeIndustryIndex === index ? "true" : "false"}
                  ref={(node) => { industryTabRefs.current[index] = node; }}
                  onClick={() => selectIndustryStandard(index)}
                  onFocus={() => {
                    if (window.innerWidth <= 1180) previewIndustryStandard(index);
                  }}
                  onBlur={(event) => {
                    if (
                      window.innerWidth <= 1180
                      && !event.currentTarget.parentElement?.contains(event.relatedTarget as Node | null)
                    ) {
                      previewIndustryStandard(null);
                    }
                  }}
                  onPointerEnter={(event) => {
                    if (event.pointerType === "mouse" && window.innerWidth <= 1180) {
                      previewIndustryStandard(index);
                    }
                  }}
                  onPointerLeave={(event) => {
                    if (
                      event.pointerType === "mouse"
                      && window.innerWidth <= 1180
                      && document.activeElement !== event.currentTarget
                    ) {
                      previewIndustryStandard(null);
                    }
                  }}
                  onKeyDown={(event) => {
                    if (!["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
                    event.preventDefault();
                    const desktopColumns = window.innerWidth > 1180 ? 2 : 1;
                    const delta = event.key === "ArrowDown"
                      ? desktopColumns
                      : event.key === "ArrowUp"
                        ? -desktopColumns
                        : event.key === "ArrowRight"
                          ? 1
                          : -1;
                    const nextIndex = event.key === "Home"
                      ? 0
                      : event.key === "End"
                        ? industries.length - 1
                        : (index + delta + industries.length) % industries.length;
                    if (window.innerWidth <= 1180) previewIndustryStandard(nextIndex);
                    industryTabRefs.current[nextIndex]?.focus();
                  }}
                  key={industry.key}
                >
                  <i className="industry-icon industry-icon-list"><IndustryIcon industry={industry.key} /></i>
                  <span className="industry-number">{industry.number}</span>
                  <strong>{industry.title}</strong>
                  <span className="industry-state" aria-hidden="true" />
                </button>
              ))}
            </div>
            <article
              className="industry-trust-panel"
              id="industry-trust-standard"
              role="tabpanel"
              aria-live="polite"
              aria-labelledby={`industry-tab-${renderedIndustry.key}`}
              data-detail-phase={industryDetailPhase}
              key={renderedIndustry.key}
            >
              <header className="industry-trust-header">
                <div>
                  <span className="industry-mobile-trust-label">Trust Standard</span>
                  <span className="industry-title-label">Industry</span>
                  <strong>{renderedIndustry.title}</strong>
                  <p className="industry-trust-statement industry-trust-statement-mobile">{renderedIndustry.trust}</p>
                  {renderedIndustry.key === "insurance" && (
                    <button
                      className="industry-license-offer"
                      type="button"
                      aria-haspopup="dialog"
                      aria-expanded={offerModalOpen && offerModalState?.activeOfferId === "licensed-insurance-agents"}
                      aria-controls="strategic-offers-dialog"
                      onClick={(event) => openOfferModal("licensed-insurance-agents", event.currentTarget)}
                    >
                      <span className="industry-license-offer-label">STRATEGIC OFFER</span>
                      <strong className="industry-license-offer-copy">STATE LICENSED INSURANCE AGENTS</strong>
                      <span className="industry-license-offer-action">
                        VIEW OFFER <i aria-hidden="true">→</i>
                      </span>
                    </button>
                  )}
                  <section className="industry-help">
                    <small>How FTO Helps</small>
                    <p>{renderedIndustry.help}</p>
                  </section>
                  <a
                    className="industry-primary-cta"
                    href="#contact"
                    onClick={(event) => {
                      event.preventDefault();
                      navigateTo(chapters[CONTACT_CHAPTER_INDEX].key, CONTACT_CHAPTER_INDEX, true);
                    }}
                  >
                    <span>{renderedIndustry.cta}</span>
                    <i aria-hidden="true">→</i>
                  </a>
                </div>
                <i className="industry-icon industry-icon-panel"><IndustryIcon industry={renderedIndustry.key} /></i>
              </header>
              <i className="industry-trust-accent" aria-hidden="true" />
              <section className="industry-standard-desktop">
                <small>Trust Standard</small>
                <p>{renderedIndustry.trust}</p>
              </section>
              <section className="industry-customer">
                <small>What Customers Need to Feel</small>
                <p>{renderedIndustry.customer}</p>
              </section>
              <section className="industry-proof">
                <small>Where FTO Adds Value</small>
                <div className="industry-proof-legacy">
                  {renderedIndustry.proof.map((attribute) => (
                    <span key={attribute}>{attribute}</span>
                  ))}
                </div>
                <div className="industry-metrics" role="list" aria-label={`${renderedIndustry.title} performance metrics`}>
                  {renderedIndustry.metrics.map((metric) => (
                    <span className="industry-metric" role="listitem" key={metric.label}>
                      <strong>{metric.value}</strong>
                      <em>{metric.label}</em>
                    </span>
                  ))}
                </div>
              </section>
              <section className="industry-workflows">
                <small>Relevant Workflows</small>
                <div>
                  {renderedIndustry.workflows.map((workflow, workflowIndex) => (
                    <span key={workflow}>
                      <i aria-hidden="true"><b>{workflowIndex + 1}</b></i>
                      <strong>{workflow}</strong>
                    </span>
                  ))}
                </div>
              </section>
            </article>
          </div>
          </>
        );
      case "standard":
        return <GlobalDeliveryNetwork active={activeChapter === STANDARD_CHAPTER_INDEX} />;
      case "about":
        return (
          <>
            <section className="about-editorial-desktop" aria-labelledby="about-editorial-title">
              <div className="about-editorial-copy">
                <h2 id="about-editorial-title">
                  <span>Built to perform.</span>
                  <span>Designed to scale.</span>
                </h2>
                <div className="about-editorial-body">
                  <p>Founded in 2015, FTO is a global operations partner that builds and runs customer-facing and back-office teams for growing businesses.</p>
                  <p>Our operating model uses disciplined workflows to improve quality, speed, and consistency — while keeping judgment and accountability where they matter most.</p>
                  <p>With delivery teams across the Philippines, Ghana, and the U.S., FTO works as an extension of our clients: dedicated teams, clear standards, and measurable outcomes.</p>
                  <p>We create long-term value through <strong>stronger operations</strong>, <strong>trusted partnerships</strong>, and teams equipped to perform at a high standard.</p>
                </div>
              </div>
              <div className="about-visual-column">
                <figure className="about-team-visual">
                  <img
                    src={getAssetUrl("/about-team-visual.webp")}
                    alt="A diverse operations team collaborating around a laptop in a modern workplace."
                    width={1672}
                    height={941}
                    loading="lazy"
                    decoding="async"
                  />
                  <figcaption className="about-team-editorial">
                    <span>GLOBAL TALENT</span>
                    <span>REAL IMPACT</span>
                  </figcaption>
                </figure>
                <article className="about-founder-perspective" aria-labelledby="about-founder-name">
                  <figure className="about-founder-portrait">
                    <img
                      src={getAssetUrl("/mike-dershowitz-founder.png")}
                      alt="Mike Dershowitz, CEO and Founder of FTO."
                      width={1344}
                      height={768}
                      loading="lazy"
                      decoding="async"
                    />
                  </figure>
                  <div className="about-founder-copy">
                    <span className="about-founder-label">FOUNDER PERSPECTIVE</span>
                    <h3 id="about-founder-name">Mike Dershowitz</h3>
                    <p className="about-founder-role">CEO &amp; Founder, FTO</p>
                    <div className="about-founder-body">
                      <p>For more than 25 years, Mike has built and scaled businesses across outsourcing, insurance, financial services, healthcare, aviation, and technology — creating more than 3,000 jobs across seven companies.</p>
                      <p>His operating philosophy is simple: AI matters when it improves how work gets done. At FTO, disciplined workflows bring AI and human judgment together to make operations faster, more consistent, and more accountable.</p>
                    </div>
                  </div>
                </article>
              </div>
              <div className="about-principles" aria-label="FTO values">
                <article>
                  <span>01</span>
                  <h3>Culture of Excellence</h3>
                  <p>Structure, accountability, and standards that drive consistent performance.</p>
                </article>
                <article>
                  <span>02</span>
                  <h3>Solution Provider</h3>
                  <p>Proactive problem-solving focused on client needs and effective solutions.</p>
                </article>
                <article>
                  <span>03</span>
                  <h3>Integrity</h3>
                  <p>Honesty, transparency, and ethical practices in every interaction.</p>
                </article>
                <article>
                  <span>04</span>
                  <h3>Innovation</h3>
                  <p>Continuous improvement through smarter technology and better processes.</p>
                </article>
                <article>
                  <span>05</span>
                  <h3>Professionalism</h3>
                  <p>High standards of expertise, respect, and accountability.</p>
                </article>
              </div>
              <p className="about-closing">
                <strong>Built for lasting value.</strong>
                <span className="about-closing-emphasis">Stronger operations. Trusted partnerships.</span>
              </p>
            </section>
            <div className="about-location-legacy">
              <GlobalDeliveryNetwork active={activeChapter === ABOUT_CHAPTER_INDEX} />
            </div>
          </>
        );
      case "perspectives":
        return (
          <section className="perspective-system" aria-label="Frame the choice between AI precision and human judgment">
            <a
              className="perspective-feature perspective-feature-ai"
              data-perspective="ai"
              href="#perspectives"
              aria-label="Explore AI Precision."
              onClick={(event) => {
                event.preventDefault();
                selectPerspectiveElement("ai");
                selectWhyFtoPerspective("ai");
              }}
              onPointerEnter={(event) => {
                if (event.pointerType === "mouse") selectPerspectiveElement("ai");
              }}
              onPointerLeave={(event) => {
                if (
                  event.pointerType === "mouse"
                  && !event.currentTarget.contains(document.activeElement)
                  && !perspectiveOverlayOpenRef.current
                ) {
                  selectPerspectiveElement(null);
                }
              }}
              onFocus={() => selectPerspectiveElement("ai")}
              onBlur={(event) => {
                if (
                  !event.currentTarget.contains(event.relatedTarget as Node | null)
                  && !perspectiveOverlayOpenRef.current
                ) {
                  selectPerspectiveElement(null);
                }
              }}
            >
              <header><span>01 · PRECISION</span><i /></header>
              <div className="perspective-element" aria-hidden="true">
                <b>AI</b><i /><i /><i /><i />
              </div>
              <div className="perspective-feature-copy">
                <small>ARTIFICIAL INTELLIGENCE</small>
                <h3>Precision at scale.</h3>
                <p>Recognizes patterns, accelerates repeatable decisions and keeps high-volume work moving.</p>
              </div>
              <ul className="perspective-attributes" aria-label="AI attributes">
                <li>Speed</li>
                <li>Pattern recognition</li>
                <li>Repeatability</li>
              </ul>
              <span className="perspective-card-cta" aria-hidden="true">
                EXPLORE AI PRECISION <span>→</span>
              </span>
            </a>

            <div className="perspective-balance">
              <span>Designed in the balance.</span>
              <p>The advantage is not choosing one.<br />It is deciding where each belongs.</p>
            </div>

            <a
              className="perspective-feature perspective-feature-human"
              data-perspective="human"
              href="#perspectives"
              aria-label="Explore Human Judgment."
              onClick={(event) => {
                event.preventDefault();
                selectPerspectiveElement("human");
                selectWhyFtoPerspective("human");
              }}
              onPointerEnter={(event) => {
                if (event.pointerType === "mouse") selectPerspectiveElement("human");
              }}
              onPointerLeave={(event) => {
                if (
                  event.pointerType === "mouse"
                  && !event.currentTarget.contains(document.activeElement)
                  && !perspectiveOverlayOpenRef.current
                ) {
                  selectPerspectiveElement(null);
                }
              }}
              onFocus={() => selectPerspectiveElement("human")}
              onBlur={(event) => {
                if (
                  !event.currentTarget.contains(event.relatedTarget as Node | null)
                  && !perspectiveOverlayOpenRef.current
                ) {
                  selectPerspectiveElement(null);
                }
              }}
            >
              <header><span>02 · JUDGMENT</span><i /></header>
              <div className="perspective-element" aria-hidden="true">
                <b>Hu</b><i /><i /><i /><i />
              </div>
              <div className="perspective-feature-copy">
                <small>HUMAN JUDGMENT</small>
                <h3>Context when the moment changes.</h3>
                <p>People interpret nuance, protect trust and take accountability when the right response cannot be reduced to a rule.</p>
              </div>
              <ul className="perspective-attributes" aria-label="Human judgment attributes">
                <li>Empathy</li>
                <li>Nuance</li>
                <li>Accountability</li>
              </ul>
              <span className="perspective-card-cta" aria-hidden="true">
                EXPLORE HUMAN JUDGMENT <span>→</span>
              </span>
            </a>
          </section>
        );
      case "contact":
        return <DecisionPointForm />;
      default:
        return null;
    }
  };

  const active = activeChapter >= 0 ? chapters[activeChapter] : null;
  const desktopFooterSource = whyFtoActive || convergenceActive
    ? "perspectives"
    : active?.key ?? "hero";
  const desktopFooterPresentation = desktopFooterSource === "hero"
    ? { number: "01", label: "Home" }
    : PUBLIC_SECTION_PRESENTATION[desktopFooterSource] ?? {
      number: active?.number ?? "01",
      label: active?.label ?? "Home",
    };

  return (
    <main className="prototype-shell">
      <a
        className="skip-link"
        href="#outcomes"
        onClick={(event) => {
          event.preventDefault();
          navigateTo("outcomes", OUTCOMES_CHAPTER_INDEX, true);
        }}
      >
        Skip to Business Outcomes
      </a>
      <div className="scroll-track" ref={trackRef}>
        <section
          className="stage"
          data-menu-open={menuOpen ? "true" : "false"}
          data-offer-modal-open={offerModalOpen ? "true" : "false"}
          data-brand-signal-ready={brandSignalReady ? "true" : "false"}
          data-brand-signal-focus={brandSignalFocus ?? "none"}
          data-judgment-focus={judgmentFocus ?? "none"}
          data-outcome-ready={outcomeReady ? "true" : "false"}
          data-outcome-focus={activeOutcomeKey}
          data-perspective-focus={perspectiveFocus ?? "none"}
          data-perspective-overlay={perspectiveOverlay ?? "none"}
          data-why-fto-active={whyFtoActive ? "true" : "false"}
          data-why-fto-state={whyFtoState}
          data-why-fto-perspective={whyFtoPerspective}
          data-live-workflow-active={active?.key === "workflow" ? "true" : "false"}
          data-services-active={active?.key === "solutions" ? "true" : "false"}
          data-about-active={active?.key === "about" ? "true" : "false"}
          data-global-delivery-active={active?.key === "standard" ? "true" : "false"}
          data-service-index={activeSolution}
          data-contact-ready={activeChapter === chapters.length - 1 ? "true" : "false"}
          ref={stageRef}
          aria-label="FTO persistent world brand experience"
        >
          <div className="page-progress" aria-hidden="true"><span style={{ transform: `scaleX(${progress})` }} /></div>
          <div className="motion-debug" data-visible="false" ref={motionDebugRef} aria-hidden="true" />

          <header className="prototype-topbar">
            <button className="brand-mark" onClick={() => navigateTo("hero")} aria-label="FTO — Home">
              {/* eslint-disable-next-line @next/next/no-img-element -- Preserve the supplied official SVG without transformation. */}
              <img src={getAssetUrl("/fto-logo.svg")} alt="" width="500" height="500" aria-hidden="true" />
            </button>
            <div className="prototype-label">
              <span>Experience architecture</span>
              <strong>{whyFtoActive
                ? `${PUBLIC_SECTION_PRESENTATION.perspectives.number} — ${PUBLIC_SECTION_PRESENTATION.perspectives.label}`
                : convergenceActive
                  ? `${PUBLIC_SECTION_PRESENTATION.perspectives.number} — ${PUBLIC_SECTION_PRESENTATION.perspectives.label}`
                  : active
                    ? `${getPublicSectionPresentation(active).number} — ${getPublicSectionPresentation(active).label}`
                    : "Built to deliver."}</strong>
            </div>
            <div className="topbar-actions">
              <button
                className="topbar-offers"
                type="button"
                aria-haspopup="dialog"
                aria-expanded={offerModalOpen && offerModalState?.activeOfferId === null}
                aria-controls="strategic-offers-dialog"
                onClick={(event) => openOfferModal(null, event.currentTarget)}
              >
                <span>OFFERS</span>
                <span className="topbar-offers-icon">+</span>
              </button>
              <a
                className="global-contact-cta"
                href="#contact"
                onClick={(event) => {
                  event.preventDefault();
                  navigateTo("contact", chapters.length - 1);
                }}
              >
                START THE CONVERSATION <i aria-hidden="true">→</i>
              </a>
              <button
                className="menu-trigger"
                ref={menuButtonRef}
                type="button"
                aria-expanded={menuOpen}
                aria-controls="experience-menu"
                aria-label={menuOpen ? "Close experience menu" : "Open experience menu"}
                onClick={() => setMenuOpen((open) => !open)}
              >
                <span>{menuOpen ? "Close" : "Menu"}</span>
                <i aria-hidden="true"><b /><b /></i>
              </button>
            </div>
          </header>

          <div className="experience-menu" id="experience-menu" ref={menuRef} role="dialog" aria-modal="true" aria-label="Experience navigation" aria-hidden={!menuOpen} inert={!menuOpen}>
            <div className="menu-atmosphere" aria-hidden="true"><span>FTO</span><i /></div>
            <div className="menu-intro">
              <p>FTO EXPERIENCE SYSTEM</p>
              <h2>Follow the signals.</h2>
              <span>Move directly to any chapter in the experience.</span>
            </div>
            <nav className="menu-chapters" aria-label="FTO experience chapters">
              <button
                className="menu-offers-entry"
                type="button"
                aria-haspopup="dialog"
                aria-expanded={offerModalOpen && offerModalState?.activeOfferId === null}
                aria-controls="strategic-offers-dialog"
                onClick={(event) => openOfferModal(null, event.currentTarget)}
                style={{
                  color: "#00b879",
                  borderColor: "rgba(0, 184, 121, 0.35)",
                  backgroundColor: "rgba(0, 184, 121, 0.08)",
                }}
              >
                <span>PROMO</span>
                <strong>Strategic Offers</strong>
                <i>+</i>
              </button>
              {menuNavigationItems.map((item) => {
                if (item.chapterIndex === null) {
                  return (
                    <Link className="menu-navigation-item" href="/faq" key={item.key}>
                      <span className="menu-navigation-number">{item.number}</span>
                      <strong>
                        <span className="menu-navigation-label menu-navigation-label--desktop">{desktopMenuLabels[item.key]}</span>
                        <span className="menu-navigation-label menu-navigation-label--compact">{item.label}</span>
                      </strong>
                      <i>↗</i>
                    </Link>
                  );
                }
                const isWhyFtoItem = item.chapterIndex === WHY_FTO_FIRST_INDEX;
                const isActive = item.chapterIndex < 0
                  ? activeChapter < 0 && !convergenceActive && !whyFtoActive
                  : isWhyFtoItem
                    ? whyFtoActive
                    : activeChapter === item.chapterIndex;
                return (
                  <a
                    className={`menu-navigation-item${item.key === "contact" ? " menu-navigation-item--contact" : ""}${isActive ? " active" : ""}`}
                    href={`#${item.key}`}
                    key={item.key}
                    onClick={(event) => {
                      event.preventDefault();
                      navigateTo(
                        item.key,
                        item.chapterIndex < 0 ? undefined : item.chapterIndex,
                        item.chapterIndex >= 0,
                      );
                    }}
                  >
                    <span className="menu-navigation-number">{item.number}</span>
                    <strong>
                      <span className="menu-navigation-label menu-navigation-label--desktop">{desktopMenuLabels[item.key] ?? item.label}</span>
                      <span className="menu-navigation-label menu-navigation-label--compact">{item.label}</span>
                    </strong>
                    <i>↗</i>
                  </a>
                );
              })}
            </nav>
            <a
              className="menu-cta"
              href="#contact"
              onClick={(event) => {
                event.preventDefault();
                navigateTo("contact", chapters.length - 1);
              }}
            >
              START THE CONVERSATION <i>→</i>
            </a>
          </div>

          <div className="hero-copy" id="hero" ref={heroRef}>
            <p>AI + HUMAN EXPERIENCE DELIVERY</p>
            <h1>Who does your work is now a brand choice.</h1>
            <span className="hero-supporting-copy">
              FTO combines people, AI Agents, and disciplined execution to scale customer operations.
            </span>
            <button
              className="hero-enter-cta"
              type="button"
              aria-label="Scale with FTO and continue to Business Outcomes."
              onClick={() => navigateTo("outcomes", OUTCOMES_CHAPTER_INDEX, true)}
            >
              <span>Scale with FTO</span><i aria-hidden="true">↓</i>
            </button>
            <div className="mobile-chapter-next">
              {renderNextChapterNavigation("hero", "mobile")}
            </div>
          </div>

          {/* Hero Endline commented out per user request */}
          {/*
          <div className="hero-endline" aria-hidden="true">
            <span>01 / HERO</span>
            <h2>Every interaction.<br />Built to deliver value.</h2>
            <span className="hero-supporting-copy">
              Capture the value of Agentic AI + Human Agents working together in a seamless platform.
            </span>
            <button
              className="hero-enter-cta"
              type="button"
              aria-label="Scale with FTO and continue to The Delivery Standard."
              onClick={() => navigateTo("signal", 0, true)}
            >
              <span>Scale with FTO</span><i aria-hidden="true">↓</i>
            </button>
          </div>
          */}

          <section
            className="why-fto-shell"
            ref={whyFtoShellRef}
            aria-label="AI and Human delivery model"
            aria-hidden={!whyFtoActive}
            inert={!whyFtoActive}
            tabIndex={-1}
          >
            {renderMobileAiHuman()}
            {/* Standard Scene commented out per user request */}
            {/*
            <article
              className="why-fto-scene why-fto-standard"
              id="why-fto-panel-standard"
              role="tabpanel"
              aria-labelledby="why-fto-tab-standard"
              aria-hidden={whyFtoState !== "standard"}
              inert={whyFtoState !== "standard"}
            >
              <div className="why-fto-editorial">
                <p className="chapter-kicker">THE DELIVERY STANDARD</p>
                <h2>Consistency is built into every interaction.</h2>
                <p className="chapter-body brand-signal-body">
                  FTO designs customer operations around precision, judgment, and trust—so teams can deliver the right standard, even when the work gets complex.
                </p>
                <div className="brand-signal-mobile-spacer" aria-hidden="true" />
                {renderChapterSystem(chapters[WHY_FTO_FIRST_INDEX])}
              </div>
            </article>
            */}

            <article
              className="why-fto-scene why-fto-ai-human"
              id="why-fto-panel-ai-human"
              role="tabpanel"
              aria-labelledby="why-fto-tab-ai-human"
              aria-hidden={false}
              data-perspective={whyFtoPerspective}
            >
              <header className="why-ai-human-intro">
                <p>AI + HUMAN</p>
                <h1>
                  AI provides precision.<br />
                  People provide judgment.<br />
                  <span className="why-ai-human-bridge">
                    FTO helps you get there.
                  </span>
                </h1>
                <span>The advantage is not choosing one. It is deciding where each belongs.</span>
              </header>

              <div className="why-perspective-composition">
                <article
                  className="why-perspective-card why-perspective-ai"
                  data-expanded={whyFtoPerspective === "ai"}
                >
                  <button
                    className="why-perspective-trigger"
                    type="button"
                    aria-pressed={whyFtoPerspective === "ai"}
                    aria-label="Select AI Precision"
                    onClick={() => selectWhyFtoPerspective("ai")}
                  />
                  <header><span>01 · PRECISION</span><i /></header>
                  <div className="why-element-mark" aria-hidden="true"><b>AI</b><i /><i /><i /><i /></div>
                  <div className="why-perspective-copy">
                    <small>ARTIFICIAL INTELLIGENCE</small>
                    <h3>Precision at scale.</h3>
                    <p>FTO AI helps operations move faster, identify patterns earlier, and apply consistent logic across high-volume work.</p>
                  </div>
                  <ul className="why-perspective-summary" aria-label="AI attributes">
                    <li>Speed</li><li>Pattern Recognition</li><li>Repeatability</li>
                  </ul>
                  <div
                    className="why-perspective-expanded why-ai-expanded"
                    data-detail-active={displayedFtoAiView !== "overview"}
                  >
                    <section className="why-ai-workflow-section" aria-labelledby="why-ai-workflow-title">
                      <h4 id="why-ai-workflow-title">FTO AI IN ACTION</h4>
                      <ul className="why-ai-workflow-grid" aria-label="FTO AI workflows">
                        {ftoAiWorkflows.map((workflow) => (
                          <li key={workflow.key} data-active={selectedFtoAiWorkflow === workflow.key}>
                            <button
                              type="button"
                              aria-pressed={selectedFtoAiWorkflow === workflow.key}
                              aria-controls="why-ai-dynamic-region"
                              tabIndex={whyFtoPerspective === "ai" ? 0 : -1}
                              onClick={() => selectFtoAiWorkflow(workflow.key)}
                            >
                              {workflow.label}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </section>
                    <section
                      id="why-ai-dynamic-region"
                      className="why-ai-dynamic-region why-workflow-detail-panel"
                      data-transition-phase={ftoAiTransitionPhase}
                      aria-label="FTO AI operational details"
                      aria-live="polite"
                      aria-atomic="true"
                    >
                      {previousFtoAiView !== null && (
                        <div className="why-ai-dynamic-content why-ai-dynamic-content-previous" aria-hidden="true">
                          {previousFtoAiView === "overview" ? (
                            <>
                              <h4>WHERE FTO AI ADDS VALUE</h4>
                              <dl className="why-ai-value-list">
                                <div><dt>SPEED</dt><dd>Accelerates response and keeps high-volume work moving.</dd></div>
                                <div><dt>PATTERN RECOGNITION</dt><dd>Surfaces signals, intent, and exceptions earlier.</dd></div>
                                <div><dt>CONSISTENCY</dt><dd>Applies repeatable logic across workflows at scale.</dd></div>
                              </dl>
                            </>
                          ) : (
                            <>
                              <span className="why-workflow-detail-meta">
                                {String(ftoAiWorkflows.findIndex(({ key }) => key === previousFtoAiView) + 1).padStart(2, "0")} / {ftoAiWorkflows.find(({ key }) => key === previousFtoAiView)?.label}
                              </span>
                              <h4 className="why-workflow-help-label">HOW FTO AI HELPS</h4>
                              <p>{ftoAiWorkflows.find(({ key }) => key === previousFtoAiView)?.description}</p>
                              <span className="why-ai-overview-return" aria-hidden="true">← FTO AI overview</span>
                            </>
                          )}
                        </div>
                      )}
                      <div className="why-ai-dynamic-content why-ai-dynamic-content-current">
                        {displayedFtoAiView === "overview" ? (
                          <>
                            <h4>WHERE FTO AI ADDS VALUE</h4>
                            <dl className="why-ai-value-list">
                              <div><dt>SPEED</dt><dd>Accelerates response and keeps high-volume work moving.</dd></div>
                              <div><dt>PATTERN RECOGNITION</dt><dd>Surfaces signals, intent, and exceptions earlier.</dd></div>
                              <div><dt>CONSISTENCY</dt><dd>Applies repeatable logic across workflows at scale.</dd></div>
                            </dl>
                          </>
                        ) : (
                          <>
                            <span className="why-workflow-detail-meta">
                              {String(ftoAiWorkflows.findIndex(({ key }) => key === displayedFtoAiView) + 1).padStart(2, "0")} / {ftoAiWorkflows.find(({ key }) => key === displayedFtoAiView)?.label}
                            </span>
                            <h4 className="why-workflow-help-label">HOW FTO AI HELPS</h4>
                            <p>{ftoAiWorkflows.find(({ key }) => key === displayedFtoAiView)?.description}</p>
                            <button
                              className="why-ai-overview-return"
                              type="button"
                              tabIndex={whyFtoPerspective === "ai" ? 0 : -1}
                              onClick={() => selectFtoAiWorkflow(null)}
                            >
                              ← FTO AI overview
                            </button>
                          </>
                        )}
                      </div>
                    </section>
                    <button
                      className="why-perspective-action why-ai-cta"
                      type="button"
                      tabIndex={whyFtoPerspective === "ai" ? 0 : -1}
                      aria-haspopup="dialog"
                      aria-expanded={offerModalOpen && offerModalState?.activeOfferId === "ai-workflow-engineering"}
                      aria-controls="strategic-offers-dialog"
                      onClick={(event) => openOfferModal("ai-workflow-engineering", event.currentTarget)}
                    >
                      <span>REQUEST FREE CUSTOM AI WORKFLOW ENGINEERING</span> <i aria-hidden="true">→</i>
                    </button>
                  </div>
                  <span className="why-perspective-action why-ai-neutral-action" aria-hidden="true">EXPLORE AI IN ACTION<i>→</i></span>
                </article>

                <div className="why-convergence-module">
                  <div className="why-convergence-note" aria-hidden="true">
                    <span>AI PRECISION</span><b>+</b><span>HUMAN JUDGMENT</span><b>=</b><strong>ONE DELIVERY SYSTEM</strong>
                  </div>
                </div>

                <article
                  className="why-perspective-card why-perspective-human"
                  data-expanded={whyFtoPerspective === "human"}
                >
                  <button
                    className="why-perspective-trigger"
                    type="button"
                    aria-pressed={whyFtoPerspective === "human"}
                    aria-label="Select Human Judgment"
                    onClick={() => selectWhyFtoPerspective("human")}
                  />
                  <header><span>02 · JUDGMENT</span><i /></header>
                  <div className="why-element-mark" aria-hidden="true"><b>Hu</b><i /><i /><i /><i /></div>
                  <div className="why-perspective-copy">
                    <small>FTO PEOPLE</small>
                    <h3>Context when the moment changes.</h3>
                    <p>FTO people bring context, empathy, and accountable judgment to complex or high-stakes interactions — protecting trust when rules and automation are not enough.</p>
                  </div>
                  <ul className="why-perspective-summary" aria-label="FTO People attributes">
                    <li>Empathy</li><li>Nuance</li><li>Accountability</li>
                  </ul>
                  <div
                    className="why-perspective-expanded why-human-expanded"
                    data-detail-active={displayedFtoPeopleView !== "overview"}
                  >
                    <section className="why-human-workflow-section" aria-labelledby="why-human-workflow-title">
                      <h4 id="why-human-workflow-title">FTO PEOPLE IN ACTION</h4>
                      <ul className="why-human-workflow-grid" aria-label="FTO People workflows">
                        {ftoPeopleWorkflows.map((workflow) => (
                          <li key={workflow.key} data-active={selectedFtoPeopleWorkflow === workflow.key}>
                            <button
                              type="button"
                              aria-pressed={selectedFtoPeopleWorkflow === workflow.key}
                              aria-controls="why-human-dynamic-region"
                              tabIndex={whyFtoPerspective === "human" ? 0 : -1}
                              onClick={() => selectFtoPeopleWorkflow(workflow.key)}
                            >
                              {workflow.label}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </section>
                    <section
                      id="why-human-dynamic-region"
                      className="why-human-dynamic-region why-workflow-detail-panel"
                      data-transition-phase={ftoPeopleTransitionPhase}
                      aria-label="FTO People operational details"
                      aria-live="polite"
                      aria-atomic="true"
                    >
                      {previousFtoPeopleView !== null && (
                        <div className="why-human-dynamic-content why-human-dynamic-content-previous" aria-hidden="true">
                          {previousFtoPeopleView === "overview" ? (
                            <>
                              <h4>WHERE FTO PEOPLE ADD VALUE</h4>
                              <dl className="why-human-value-list">
                                <div><dt>EMPATHY</dt><dd>Recognizes emotional context and responds with care.</dd></div>
                                <div><dt>NUANCE</dt><dd>Interprets complexity, exceptions, and meaning beyond the rule.</dd></div>
                                <div><dt>ACCOUNTABILITY</dt><dd>Takes ownership when decisions carry risk, trust, or consequence.</dd></div>
                              </dl>
                            </>
                          ) : (
                            <>
                              <span className="why-workflow-detail-meta">
                                {String(ftoPeopleWorkflows.findIndex(({ key }) => key === previousFtoPeopleView) + 1).padStart(2, "0")} / {ftoPeopleWorkflows.find(({ key }) => key === previousFtoPeopleView)?.label}
                              </span>
                              <h4 className="why-workflow-help-label">HOW FTO PEOPLE HELP</h4>
                              <p>{ftoPeopleWorkflows.find(({ key }) => key === previousFtoPeopleView)?.description}</p>
                              <span className="why-human-overview-return" aria-hidden="true">← FTO People overview</span>
                            </>
                          )}
                        </div>
                      )}
                      <div className="why-human-dynamic-content why-human-dynamic-content-current">
                        {displayedFtoPeopleView === "overview" ? (
                          <>
                            <h4>WHERE FTO PEOPLE ADD VALUE</h4>
                            <dl className="why-human-value-list">
                              <div><dt>EMPATHY</dt><dd>Recognizes emotional context and responds with care.</dd></div>
                              <div><dt>NUANCE</dt><dd>Interprets complexity, exceptions, and meaning beyond the rule.</dd></div>
                              <div><dt>ACCOUNTABILITY</dt><dd>Takes ownership when decisions carry risk, trust, or consequence.</dd></div>
                            </dl>
                          </>
                        ) : (
                          <>
                            <span className="why-workflow-detail-meta">
                              {String(ftoPeopleWorkflows.findIndex(({ key }) => key === displayedFtoPeopleView) + 1).padStart(2, "0")} / {ftoPeopleWorkflows.find(({ key }) => key === displayedFtoPeopleView)?.label}
                            </span>
                            <h4 className="why-workflow-help-label">HOW FTO PEOPLE HELP</h4>
                            <p>{ftoPeopleWorkflows.find(({ key }) => key === displayedFtoPeopleView)?.description}</p>
                            <button
                              className="why-human-overview-return"
                              type="button"
                              tabIndex={whyFtoPerspective === "human" ? 0 : -1}
                              onClick={() => selectFtoPeopleWorkflow(null)}
                            >
                              ← FTO People overview
                            </button>
                          </>
                        )}
                      </div>
                    </section>
                    <button
                      className="why-perspective-action why-human-cta"
                      type="button"
                      tabIndex={whyFtoPerspective === "human" ? 0 : -1}
                      onClick={() => navigateTo(chapters[CONTACT_CHAPTER_INDEX].key, CONTACT_CHAPTER_INDEX, true)}
                    >
                      DESIGN YOUR HUMAN-LED WORKFLOW <i aria-hidden="true">→</i>
                    </button>
                  </div>
                  <span className="why-perspective-action why-human-neutral-action" aria-hidden="true">EXPLORE HUMAN EXPERTISE<i>→</i></span>
                </article>
              </div>

              {whyFtoPerspective !== "neutral" && (
                <button
                  className="why-return-neutral"
                  type="button"
                  onClick={() => selectWhyFtoPerspective("neutral")}
                >
                  <i aria-hidden="true">←</i><span>Return to AI + Human overview</span>
                </button>
              )}
            </article>

            <div className="mobile-chapter-next mobile-ai-human-next">
              {renderNextChapterNavigation("perspectives", "mobile")}
            </div>

            {/* Outcomes Scene commented out per user request */}
            {/*
            <article
              className="why-fto-scene why-fto-outcomes"
              id="why-fto-panel-outcomes"
              role="tabpanel"
              aria-labelledby="why-fto-tab-outcomes"
              aria-hidden={whyFtoState !== "outcomes"}
              inert={whyFtoState !== "outcomes"}
            >
              <div className="why-outcomes-editorial">
                <p className="why-outcomes-eyebrow">BUSINESS OUTCOMES</p>
                <h2>Better operations.<br />Stronger outcomes.</h2>
                <span className="why-outcomes-supporting">From resolution speed and compliance to customer satisfaction and global scale, FTO turns operational performance into measurable business results.</span>
                <div className="why-outcomes-grid" role="tablist" aria-label="Explore the four business outcomes">
                  {whyFtoBusinessOutcomes.map((outcome) => (
                    <button
                      type="button"
                      role="tab"
                      aria-selected={activeOutcomeKey === outcome.key}
                      data-outcome={outcome.key}
                      data-active={activeOutcomeKey === outcome.key ? "true" : "false"}
                      style={{ "--outcome-accent": outcome.color, "--outcome-rgb": outcome.rgb } as CSSProperties}
                      onClick={() => selectOutcome(outcome.key)}
                      onFocus={() => previewOutcomeLens(outcome.key)}
                      onBlur={() => previewOutcomeLens(null)}
                      onPointerEnter={(event) => {
                        if (event.pointerType === "mouse") previewOutcomeLens(outcome.key);
                      }}
                      onPointerLeave={(event) => {
                        if (event.pointerType === "mouse" && document.activeElement !== event.currentTarget) {
                          previewOutcomeLens(null);
                        }
                      }}
                      key={outcome.key}
                    >
                      <span>{outcome.number}</span>
                      <i aria-hidden="true"><b /></i>
                      <strong>{outcome.title}</strong>
                      <small>{outcome.microcopy}</small>
                    </button>
                  ))}
                </div>
                <p className="why-outcomes-closing">Designed around your operation. Measured against your goals.</p>
                <button
                  className="why-outcomes-progress-cta why-live-workflow-progress-cta"
                  type="button"
                  aria-label="Continue to Live Workflow."
                  onClick={() => navigateTo(chapters[WORKFLOW_CHAPTER_INDEX].key, WORKFLOW_CHAPTER_INDEX, true)}
                >
                  <span>Continue to Live Workflow</span>
                  <i aria-hidden="true">→</i>
                </button>
              </div>
            </article>
            */}
          </section>

          <div className="planet-wrap" ref={planetRef}>
            <div className="planet-aura" aria-hidden="true" />
            <PrototypePlanet motionState={planetMotionRef} />
            <div
              className="industry-planet-selector"
              role="tablist"
              aria-label="Explore industry standards and workflows through the FTO planet"
            >
              {industries.map((industry, index) => (
                <button
                  className="industry-planet-node"
                  type="button"
                  role="tab"
                  id={`industry-node-${industry.key}`}
                  aria-controls="industry-trust-standard"
                  aria-label={`Explore ${industry.title} industry standards and workflows.`}
                  aria-selected={selectedIndustry === index}
                  tabIndex={selectedIndustry === index ? 0 : -1}
                  data-industry={industry.key}
                  data-current={activeIndustryIndex === index ? "true" : "false"}
                  ref={(node) => { industryNodeRefs.current[index] = node; }}
                  onClick={() => selectIndustryStandard(index)}
                  onFocus={() => previewIndustryStandard(index)}
                  onBlur={(event) => {
                    if (!event.currentTarget.parentElement?.contains(event.relatedTarget as Node | null)) {
                      previewIndustryStandard(null);
                    }
                  }}
                  onPointerEnter={(event) => {
                    if (event.pointerType === "mouse") previewIndustryStandard(index);
                  }}
                  onPointerLeave={(event) => {
                    if (
                      event.pointerType === "mouse"
                      && document.activeElement !== event.currentTarget
                    ) {
                      previewIndustryStandard(null);
                    }
                  }}
                  onKeyDown={(event) => {
                    if (!["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
                    event.preventDefault();
                    const nextIndex = event.key === "Home"
                      ? 0
                      : event.key === "End"
                        ? industries.length - 1
                        : (
                            index
                            + (["ArrowDown", "ArrowRight"].includes(event.key) ? 1 : -1)
                            + industries.length
                          ) % industries.length;
                    previewIndustryStandard(nextIndex);
                    industryNodeRefs.current[nextIndex]?.focus();
                  }}
                  key={industry.key}
                >
                  <span className="industry-node-route" aria-hidden="true" />
                  <span className="industry-node-label">
                    <small>{industry.number}</small>
                    <strong>{industry.title}</strong>
                  </span>
                  <i className="industry-node-glyph" aria-hidden="true">
                    <IndustryIcon industry={industry.key} />
                  </i>
                </button>
              ))}
            </div>
            <div className="outcome-signal-layer" aria-hidden="true">
              {whyFtoBusinessOutcomes.map((outcome) => (
                <div
                  className="outcome-planet-signal"
                  data-outcome={outcome.key}
                  data-active={activeOutcomeKey === outcome.key ? "true" : "false"}
                  style={{
                    "--signal-accent": outcome.color,
                    "--signal-rgb": outcome.rgb,
                  } as CSSProperties}
                  key={outcome.key}
                >
                  <span className="outcome-signal-copy">
                    <small>{outcome.number}</small>
                    <strong>{outcome.title}</strong>
                  </span>
                  <span className="outcome-signal-connector" />
                  <i className="outcome-signal-node"><b /></i>
                </div>
              ))}
            </div>
            <div className="ai-signal-annotations" aria-hidden="true">
              <svg className="ai-annotation-lines" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet">
                <g data-capability="pattern">
                  <path pathLength="1" d="M 19 26 L 34 26 L 43 35" />
                  <circle cx="43" cy="35" r="1.3" />
                  <circle cx="43" cy="35" r="2.5" />
                </g>
                <g data-capability="speed">
                  <path pathLength="1" d="M 81 31 L 72 31 L 64 39" />
                  <circle cx="64" cy="39" r="1.3" />
                  <circle cx="64" cy="39" r="2.5" />
                </g>
                <g data-capability="repeatability">
                  <path pathLength="1" d="M 80 73 L 70 73 L 62 67" />
                  <circle cx="62" cy="67" r="1.3" />
                  <circle cx="62" cy="67" r="2.5" />
                </g>
              </svg>
              <div className="ai-annotation ai-annotation-pattern" data-capability="pattern">
                <strong>Pattern Recognition</strong>
                <span>AI identifies signals across complex volumes of work.</span>
              </div>
              <div className="ai-annotation ai-annotation-speed" data-capability="speed">
                <strong>Speed</strong>
                <span>AI accelerates response and execution.</span>
              </div>
              <div className="ai-annotation ai-annotation-repeatability" data-capability="repeatability">
                <strong>Repeatability</strong>
                <span>AI applies consistent logic at scale.</span>
              </div>
            </div>
            <svg className="hero-orbit-signal" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
              <defs>
                <linearGradient id="heroSignalGradient" x1="50" y1="50" x2="66" y2="34" gradientUnits="userSpaceOnUse">
                  <stop offset="0" stopColor="#d2e3f3" stopOpacity="0.15" />
                  <stop offset="0.48" stopColor="#2278ac" />
                  <stop offset="1" stopColor="#009f66" />
                </linearGradient>
              </defs>
              <path pathLength="1" d="M 50 50 C 39 43, 39 28, 52 24 C 61 21, 71 27, 66 34" />
              <circle className="signal-origin" cx="50" cy="50" r="0.7" />
              <circle className="signal-destination" cx="66" cy="34" r="0.58" />
            </svg>
            {chapters.map((chapter, index) => (
              <div
                className="beacon"
                aria-hidden="true"
                data-active="false"
                data-chapter={chapter.key}
                key={chapter.key}
                ref={(node) => { beaconRefs.current[index] = node; }}
                style={{
                  left: `${chapter.point[0]}%`,
                  top: `${chapter.point[1]}%`,
                  "--beacon-color": chapter.color,
                  "--beacon-rgb": chapter.rgb,
                } as CSSProperties}
              >
                <span className="beacon-label">{getPublicSectionPresentation(chapter).number}</span>
                <span className="beacon-ring ring-a" />
                <span className="beacon-ring ring-b" />
                <span className="beacon-dot" />
              </div>
            ))}
          </div>

          <svg
            className="signal-system-bridge"
            ref={signalSystemBridgeRef}
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <defs>
              <linearGradient id="signalSystemBridgeGradient" x1="1" y1="0" x2="0" y2="0">
                <stop offset="0" stopColor="#6fcfff" />
                <stop offset="0.62" stopColor="#2278ac" />
                <stop offset="1" stopColor="#009f66" />
              </linearGradient>
            </defs>
            <path className="signal-system-bridge-travel" pathLength="1" />
            <path className="signal-system-bridge-rail" pathLength="1" />
            <circle className="signal-system-bridge-node" r="3.4" />
          </svg>

          <svg
            className="brand-signal-field"
            ref={brandSignalFieldRef}
            role="group"
            aria-label="Explore the three delivery principles through the FTO planet"
            preserveAspectRatio="none"
          >
            <defs>
              <mask id="brandSignalPlanetOcclusion" maskUnits="userSpaceOnUse">
                <rect className="brand-signal-occlusion-field" fill="white" />
                <circle className="brand-signal-occlusion-core" fill="black" />
              </mask>
              <linearGradient id="brandSignalGradient-precision" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stopColor="#2278ac" stopOpacity="0.48" />
                <stop offset="0.5" stopColor="#6fcfff" stopOpacity="0.96" />
                <stop offset="1" stopColor="#2278ac" stopOpacity="0.52" />
              </linearGradient>
              <linearGradient id="brandSignalGradient-judgment" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stopColor="#009f66" stopOpacity="0.42" />
                <stop offset="0.5" stopColor="#20c889" stopOpacity="0.94" />
                <stop offset="1" stopColor="#009f66" stopOpacity="0.5" />
              </linearGradient>
              <linearGradient id="brandSignalGradient-trust" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stopColor="#f6921e" stopOpacity="0.42" />
                <stop offset="0.5" stopColor="#ffad45" stopOpacity="0.94" />
                <stop offset="1" stopColor="#f6921e" stopOpacity="0.5" />
              </linearGradient>
            </defs>
            {brandSignals.map(({ signal, title, detailLines, accessibleLabel }) => (
              <g
                className={`brand-signal-group signal-${signal}`}
                data-brand-signal={signal}
                data-orbital-ring="true"
                role="button"
                tabIndex={brandSignalReady ? 0 : -1}
                aria-label={accessibleLabel}
                aria-pressed={brandSignalFocus === signal}
                onPointerEnter={(event) => {
                  if (event.pointerType === "mouse") selectBrandSignal(signal, true);
                }}
                onPointerLeave={(event) => {
                  if (
                    event.pointerType === "mouse"
                    && document.activeElement !== event.currentTarget
                  ) {
                    selectBrandSignal(null);
                  }
                }}
                onPointerDown={(event) => {
                  if (event.pointerType !== "mouse") selectBrandSignal(signal, true);
                }}
                onPointerUp={(event) => {
                  if (event.pointerType !== "mouse") releaseTouchBrandSignal();
                }}
                onPointerCancel={(event) => {
                  if (event.pointerType !== "mouse") selectBrandSignal(null);
                }}
                onFocus={() => selectBrandSignal(signal, true)}
                onBlur={() => selectBrandSignal(null)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    selectBrandSignal(signal, true);
                  }
                }}
                key={signal}
              >
                <path className="brand-signal-orbit-hit" data-orbit-segment="full" pathLength="1" />
                <g className="brand-signal-orbit-depth brand-signal-orbit-depth-back" mask="url(#brandSignalPlanetOcclusion)">
                  <path className="brand-signal-orbit-band brand-signal-orbit-edge brand-signal-orbit-back" data-orbit-segment="back" pathLength="1" />
                  <path className="brand-signal-orbit-band brand-signal-orbit-core brand-signal-orbit-back" data-orbit-segment="back" pathLength="1" stroke={`url(#brandSignalGradient-${signal})`} />
                  <path className="brand-signal-orbit-energy brand-signal-orbit-back" data-orbit-segment="back" pathLength="1" />
                </g>
                <g className="brand-signal-orbit-depth brand-signal-orbit-depth-front">
                  <path className="brand-signal-orbit-band brand-signal-orbit-edge brand-signal-orbit-front" data-orbit-segment="front" pathLength="1" />
                  <path className="brand-signal-orbit-band brand-signal-orbit-core brand-signal-orbit-front" data-orbit-segment="front" pathLength="1" stroke={`url(#brandSignalGradient-${signal})`} />
                  <path className="brand-signal-orbit-energy brand-signal-orbit-front" data-orbit-segment="front" pathLength="1" />
                </g>
                <path className="brand-signal-connector" />
                <g className="brand-signal-beacon" data-primary-marker="true">
                  <circle className="brand-signal-node-hit" r="24" />
                  <circle className="brand-signal-beacon-halo" r="10" />
                  <circle className="brand-signal-beacon-ring" r="6.2" />
                  <circle className="brand-signal-beacon-core" r="3.2" />
                </g>
                <g className="brand-signal-annotation" data-principle-annotation="true">
                  <rect className="brand-signal-label-backdrop" rx="10" />
                  <rect className="brand-signal-label-hit" rx="10" />
                  <text className="brand-signal-target-label brand-signal-title">{title}</text>
                  {detailLines.map((line, index) => (
                    <text
                      className="brand-signal-target-label brand-signal-detail"
                      data-line={index + 1}
                      key={`${signal}-detail-${index + 1}`}
                    >
                      {line}
                    </text>
                  ))}
                </g>
              </g>
            ))}
          </svg>

          <div className="intelligence-convergence" aria-hidden="true">
            <div className="convergence-pod convergence-pod-ai" ref={convergenceAiRef}>
              <span>AI</span>
              <i />
              <div><small>AI PRECISION</small><strong>Speed · Consistency · Availability</strong></div>
            </div>
            <div className="convergence-pod convergence-pod-human" ref={convergenceHumanRef}>
              <span>HU</span>
              <i />
              <div><small>HUMAN JUDGMENT</small><strong>Empathy · Nuance · Accountability</strong></div>
            </div>
            <article className="convergence-unified" ref={convergenceUnifiedRef}>
              <div className="convergence-grid" />
              <header><span>AI + HU</span><i /><strong>ONE OPERATING SYSTEM</strong></header>
              <div className="convergence-copy">
                <p>AI + HUMAN INTELLIGENCE</p>
                <h2>Precision and judgment, working as one.</h2>
                <span>AI handles the repeatable. People protect the moments that require context, empathy, and accountability.</span>
                <div>
                  <small><i />AI PRECISION</small>
                  <b>+</b>
                  <small><i />HUMAN JUDGMENT</small>
                </div>
              </div>
              <footer><span>FTO / CONVERGENCE MOMENT</span><strong>INTENTIONAL BALANCE</strong></footer>
            </article>
          </div>

          {chapters.map((chapter, index) => (
            index >= WHY_FTO_FIRST_INDEX && index <= WHY_FTO_LAST_INDEX
          ) ? (
            <article
              className="chapter-panel chapter-panel-sentinel"
              id={chapter.key}
              data-side={chapter.side}
              data-density={chapter.density ?? "standard"}
              data-active="false"
              data-consolidated="true"
              ref={(node) => { panelRefs.current[index] = node; }}
              aria-label={`${chapter.label} — consolidated within Why FTO`}
              aria-hidden="true"
              inert
              key={chapter.key}
              style={{ "--chapter-accent": chapter.color, "--chapter-rgb": chapter.rgb } as CSSProperties}
            />
          ) : (
            <article
              className="chapter-panel"
              id={chapter.key}
              data-side={chapter.side}
              data-density={chapter.density ?? "standard"}
              data-active="false"
              ref={(node) => { panelRefs.current[index] = node; }}
              aria-label={getPublicSectionPresentation(chapter).label}
              tabIndex={-1}
              key={chapter.key}
              style={{ "--chapter-accent": chapter.color, "--chapter-rgb": chapter.rgb } as CSSProperties}
            >
              <div className="chapter-grid" aria-hidden="true" />
              <header className="chapter-header">
                <span>{getPublicSectionPresentation(chapter).number}</span>
                <i />
                <strong>{getPublicSectionPresentation(chapter).label}</strong>
              </header>
              <div className="chapter-content">
                <p className="chapter-kicker">{chapter.kicker}</p>
                {chapter.key === "outcomes" ? (
                  <h1>
                    <span className="outcome-headline-line">Better operations.</span>
                    <span className="outcome-headline-line">Stronger outcomes.</span>
                  </h1>
                ) : (
                  <h2>{chapter.key === "ai"
                    ? <>Precision when<br />every second and<br />standard matter.</>
                    : chapter.key === "signal"
                      ? <>Consistency is built into every interaction.</>
                      : chapter.key === "solutions"
                        ? <>Operations built to do the work<br />that creates value.</>
                      : chapter.key === "standard"
                        ? <>Global delivery.<br />One brand standard.</>
                      : chapter.key === "about"
                        ? <>About FTO.</>
                      : chapter.title}</h2>
                )}
                {chapter.key === "signal" ? (
                  <p className="chapter-body brand-signal-body">
                    FTO designs customer operations around precision, judgment, and trust—so teams can deliver the right standard, even when the work gets complex.
                  </p>
                ) : chapter.key === "outcomes" ? (
                  <p className="chapter-body outcome-chapter-body">
                    From resolution speed and compliance to customer satisfaction and global scale, FTO turns operational performance into measurable business results.
                  </p>
                ) : chapter.key === "standard" ? (
                  <p className="chapter-body standard-chapter-body">
                    Local market context. Global operating discipline.<br />Every FTO location is designed to deliver with the same intent.
                  </p>
                ) : chapter.key === "about" ? (
                  <p className="chapter-body about-chapter-body">
                    Local market context. Global operating discipline.<br />Every FTO location is designed to deliver with the same intent.
                  </p>
                ) : <p className="chapter-body">{chapter.body}</p>}
                {chapter.key === "outcomes" && (
                  <button
                    className="outcome-proof-cta"
                    type="button"
                    aria-haspopup="dialog"
                    aria-expanded={offerModalOpen && offerModalState?.activeOfferId === "sla-sandbox"}
                    aria-controls="strategic-offers-dialog"
                    onClick={(event) => openOfferModal("sla-sandbox", event.currentTarget)}
                  >
                    <span>Request Your 90-Day SLA Sandbox</span>
                    <i aria-hidden="true">→</i>
                  </button>
                )}
                {chapter.key === "signal" && <div className="brand-signal-mobile-spacer" aria-hidden="true" />}
                {renderChapterSystem(chapter)}
              </div>
              {chapter.key !== "contact" && (
                <div className="mobile-chapter-next">
                  {renderNextChapterNavigation(chapter.key, "mobile")}
                </div>
              )}
              <footer className="chapter-footer">
                <span>FTO / EXPERIENCE SYSTEM</span>
                <strong>{getPublicSectionPresentation(chapter).number} / {PUBLIC_SECTION_TOTAL}</strong>
              </footer>
            </article>
          ))}

          {activePerspective && perspectiveOverlay && (
            <div
              className="perspective-overlay"
              data-perspective={perspectiveOverlay}
              data-state={perspectiveOverlayClosing ? "closing" : "open"}
              role="dialog"
              aria-modal="true"
              aria-labelledby={`perspective-overlay-title-${perspectiveOverlay}`}
              ref={perspectiveOverlayRef}
            >
              <div className="perspective-overlay-grid" aria-hidden="true" />
              <header className="perspective-overlay-header">
                <span className="perspective-overlay-brand">FTO <i /></span>
                <span className="perspective-overlay-index">
                  PERSPECTIVE <strong>{activePerspective.number}</strong> OF 02
                </span>
                <button
                  type="button"
                  onClick={() => closePerspective(false)}
                  ref={perspectiveCloseButtonRef}
                  aria-label="Close perspective"
                >
                  <span>CLOSE</span><i aria-hidden="true">×</i>
                </button>
              </header>

              <div className="perspective-overlay-body">
                <aside className="perspective-overlay-visual" aria-hidden="true">
                  <article>
                    <header><span>{activePerspective.number}</span><i /><strong>{perspectiveOverlay === "ai" ? "PRECISION" : "JUDGMENT"}</strong></header>
                    <b>{activePerspective.element}</b>
                    <small>{activePerspective.label}</small>
                    <i />
                    <p>{activePerspective.headline}</p>
                  </article>
                </aside>

                <article
                  className="perspective-overlay-editorial"
                  tabIndex={-1}
                  key={perspectiveOverlay}
                >
                  <div className="perspective-overlay-label">
                    <span>PERSPECTIVE {activePerspective.number}</span>
                    <i />
                    <strong>{activePerspective.label}</strong>
                  </div>
                  <h2 id={`perspective-overlay-title-${perspectiveOverlay}`}>
                    {activePerspective.headline}
                  </h2>
                  <p className="perspective-overlay-intro">{activePerspective.intro}</p>

                  <div className="perspective-overlay-columns">
                    <section>
                      <header><span>{activePerspective.leadTitle}</span><i /></header>
                      <ul>
                        {activePerspective.leadItems.map((item) => <li key={item}>{item}</li>)}
                      </ul>
                    </section>
                    <section className="perspective-overlay-support">
                      <header><span>{activePerspective.supportTitle}</span><i /></header>
                      <ul>
                        {activePerspective.supportItems.map((item) => <li key={item}>{item}</li>)}
                      </ul>
                    </section>
                  </div>

                  <section className="perspective-principle">
                    <header><span>THE FTO DESIGN PRINCIPLE</span><i /></header>
                    <blockquote>
                      {activePerspective.principle.map((line) => <span key={line}>{line}</span>)}
                    </blockquote>
                  </section>

                  <ul className="perspective-capabilities" aria-label="Capabilities">
                    {activePerspective.capabilities.map((capability) => <li key={capability}>{capability}</li>)}
                  </ul>

                  <div className="perspective-overlay-actions">
                    <button type="button" className="perspective-primary-cta" onClick={() => closePerspective(true)}>
                      {activePerspective.primaryCta}
                    </button>
                    <button
                      type="button"
                      className="perspective-secondary-cta"
                      onClick={() => switchPerspective(perspectiveOverlay === "ai" ? "human" : "ai")}
                    >
                      {activePerspective.secondaryCta}
                    </button>
                  </div>
                </article>
              </div>

              <footer className="perspective-overlay-footer" aria-hidden="true">
                <span>{PUBLIC_SECTION_PRESENTATION.perspectives.number}</span><i /><strong>FRAME THE CHOICE</strong>
              </footer>
            </div>
          )}

          <footer
            className="desktop-chapter-footer"
            data-chapter={desktopFooterSource}
            aria-label="Chapter navigation"
          >
            <span className="desktop-chapter-current">
              <b>{desktopFooterPresentation.number}</b>
              <i aria-hidden="true">—</i>
              <strong>{desktopFooterPresentation.label}</strong>
            </span>
            {renderNextChapterNavigation(desktopFooterSource, "chapter-footer")}
          </footer>

          <div className={`scroll-cue ${progress > 0.018 ? "is-hidden" : ""}`} aria-hidden="true"><span>SCROLL TO FOLLOW THE SIGNALS</span><i /></div>

          <aside className="chapter-status" aria-live="polite">
            <span>{whyFtoActive || convergenceActive
              ? PUBLIC_SECTION_PRESENTATION.perspectives.number
              : active ? getPublicSectionPresentation(active).number : "01"}</span>
            <i />
            <strong>{whyFtoActive || convergenceActive
              ? PUBLIC_SECTION_PRESENTATION.perspectives.label
              : active ? getPublicSectionPresentation(active).label : "Hero"}</strong>
            <small>{String(Math.round(progress * 100)).padStart(2, "0")}%</small>
          </aside>

          <nav className="chapter-dots" aria-label="Experience chapters">
            {chapterNavigationItems.map((item) => (
              <button
                className={item.chapterIndex === WHY_FTO_FIRST_INDEX
                  ? whyFtoActive ? "active" : ""
                  : activeChapter === item.chapterIndex ? "active" : ""}
                onClick={() => jumpToChapter(item.chapterIndex, true)}
                aria-label={`Go to ${item.label}`}
                key={item.key}
              ><i /></button>
            ))}
          </nav>
        </section>
      </div>
      <OfferModal
        open={offerModalOpen}
        activeOfferId={offerModalState?.activeOfferId ?? null}
        offers={strategicOffers}
        onSelectOffer={(activeOfferId) => setOfferModalState({ activeOfferId })}
        onDismiss={dismissOfferModal}
        onRestoreFocus={() => {
          offerModalOpenRef.current = false;
          offerTriggerRef.current?.focus({ preventScroll: true });
        }}
        onStartConversation={startOfferConversation}
      />
    </main>
  );
}
